/**
 * The catalogue, in English.
 *
 * Typed as `Record<Clave, string>` against the Spanish source, so adding a string in one
 * language and forgetting the other fails the build.
 *
 * Written as a Nicaraguan shopkeeper's English, not as a literal translation: "corte de caja"
 * is a till count, "gaveta" is the drawer, "pulpería" stays a corner shop. Where a term is
 * legally Nicaraguan — aguinaldo, INSS, INATEC — it keeps its name, because that is what the
 * paperwork says and translating it would make the two impossible to reconcile.
 */

import type { Clave } from './index.js';

export const EN: Record<Clave, string> = {
  // ---------- Common ----------
  'comun.volver': 'Back',
  'comun.inicio': 'Home',
  'comun.cancelar': 'Cancel',
  'comun.guardar': 'Save',
  'comun.cerrar': 'Close',
  'comun.quitar': 'Remove',
  'comun.corregir': 'Correct',
  'comun.nuevo': 'New',
  'comun.agregar': 'Add',
  'comun.buscar': 'Search',
  'comun.anular': 'Void',
  'comun.abonar': 'Pay some',
  'comun.verMas': 'Show more',
  'elegir.proveedor': 'Type or choose the supplier',
  'elegir.cliente': 'Type or choose the client',
  'producto.categoriaEscriba': 'Type or choose the category',
  'producto.categoriaNueva': 'New category: «{nombre}»',
  'comun.total': 'Total',
  'comun.fecha': 'Date',
  'comun.nota': 'Note',
  'comun.motivo': 'Reason',
  'comun.nombre': 'Name',
  'comun.telefono': 'Phone',
  'comun.direccion': 'Address',
  'comun.precio': 'Price',
  'comun.cantidad': 'Quantity',
  'comun.costo': 'Cost',
  'comun.utilidad': 'Profit',
  'comun.margen': 'Margin',
  'comun.referencia': 'Receipt number',
  'comun.guardando': 'Saving…',
  'comun.esDelDueno': 'This section is the owner\'s.',
  'comun.sinAcceso': 'No access',
  'comun.sinPermiso': 'Your user cannot open this screen.',
  'comun.abriendo': 'Opening…',
  'comun.entrar': 'Enter',

  // ---------- Insecure context ----------
  'inseguro.titulo': 'This page was not opened securely',
  'inseguro.explicacion':
    'Browsers only allow PIN encryption and offline work when the page arrives over https, or from localhost on this same computer.',
  'inseguro.abiertaEn': 'This one was opened from {host}.',
  'inseguro.comoSeguir': 'What to do',
  'inseguro.opcionLocal': 'On the computer running the system: open http://localhost:5173.',
  'inseguro.opcionDominio':
    'To try it from a phone: publish the domain with a certificate, which is what Caddy does on the server.',
  'inseguro.noEsDeAqui':
    'This is not a restriction of this system. It is the browser, and it applies to any page that handles passwords.',

  // ---------- Install ----------
  'instalar.tituloAbierta': 'Create your account and get started.',
  'instalar.tituloCerrada': 'First install. Create the owner account and register this device.',
  'instalar.clave': 'Install key',
  'instalar.clavePlaceholder': "The server's SETUP_TOKEN",
  'instalar.nombreDueno': "Owner's name",
  'instalar.pin': 'PIN (6 to 10 digits)',
  'instalar.repitaPin': 'Repeat the PIN',
  'instalar.nombreDispositivo': 'Name for this device',
  'instalar.avisoDesarrollo':
    'Development server with no install key configured. A real server requires the key, and this notice does not appear.',
  'instalar.botonAbierta': 'Create my account and sign in',
  'instalar.botonCerrada': 'Install',
  'instalar.creando': 'Creating…',
  'instalar.instalando': 'Installing…',
  'instalar.tituloRegistrar': "This shop is already set up. Register this device with the owner's PIN.",
  'instalar.dueno': 'Owner',
  'instalar.pinDueno': "Owner's PIN",
  'instalar.tipoEquipo': 'What this device is',
  'instalar.nombreEquipoEjemplo': "Elvin's phone",
  'instalar.registrar': 'Register this device',
  'instalar.registrando': 'Registering…',
  'instalar.pinDuenoIncorrecto': "That is not the owner's PIN.",
  'instalar.falloRegistro': 'The device could not be registered',
  'instalar.sinDuenos': 'Could not find out who the owner is. Check the connection and open the app again.',
  'instalar.pinCorto': 'The PIN must be 6 to 10 digits',
  'instalar.pinDebil': 'That PIN is too easy to guess',
  'instalar.pinNoCoincide': 'The PINs do not match',
  'instalar.sinInternet': 'No connection. The first install does need internet.',
  'instalar.fallo': 'Could not install',

  // ---------- Sign in ----------
  'entrar.quienAtiende': "Who's on the counter?",
  'entrar.cambiarUsuario': 'Switch user',
  'entrar.sinUsuarios': 'No users on this device yet',
  'entrar.descargarUsuarios':
    'Connect to the internet once to download the users. After that you can sign in offline.',
  'entrar.sincronizar': 'Sync now',
  'entrar.inactivo': 'That user is not active',
  'entrar.pinIncorrecto': 'Wrong PIN. Try again.',
  'entrar.hola': 'Hi, {nombre}!',

  // ---------- Sync ----------
  'sync.alDia': 'Up to date',
  'sync.sincronizando': 'Syncing…',
  'sync.sinConexion': 'Offline',
  'sync.error': 'Sync failed',
  'sync.sinRegistrar': 'Not registered',
  'sync.demasiadoGrande': 'Too large to send to the server',
  'sync.dadoDeBaja': 'Revoked',
  'sync.practica': 'Practice mode — nothing is sent',
  'practica.montando': 'Setting up the practice shop…',
  'practica.usuario': 'Trainee',
  'practica.dispositivo': 'Practice terminal',
  'practica.salir': 'Leave',
  'copiaDeTienda.pestana': 'Copy',
  'copiaDeTienda.aviso': 'This is a copy of the shop: nothing you do here reaches it. Taken {cuando}',

  // --- Tutorial ---
  'tutorial.paso': 'Step {n} of {total}',
  'tutorial.entendido': 'Got it',
  'tutorial.saltar': 'Skip this step',
  'tutorial.salir': 'Leave the tutorial',
  'tutorial.hazlo': 'You do it. The tutorial moves on once it is done.',
  'tutorial.buscando': 'Waiting for it to appear on screen…',
  'tutorial.terminada': 'Done: {leccion}',
  'tutorial.terminadaDetalle': 'Repeat it whenever you like. Nothing you did here was real.',
  'tutorial.verLecciones': 'See the lessons',
  'tutorial.oferta.titulo': 'Shall we show you how this works?',
  'tutorial.oferta.detalle': 'Short lessons in a pretend shop: you make the sale, close the till and do everything else without touching anything real. Takes about twenty minutes.',
  'tutorial.oferta.si': 'Yes, show me',
  'tutorial.oferta.no': 'Not now',
  'aprender.titulo': 'Learn',
  'aprender.hechas': '{n} of {total} lessons done',
  'aprender.lecciones': 'Lessons',
  'aprender.pasos': '{n} steps',
  'aprender.invitacion': 'You learn by doing, and doing without fear needs everything to not count. Practice mode opens a pretend pulpería, stocked and running, where you can sell, get it wrong, void it and start again. Your real shop never hears about it.',
  'aprender.empezar': 'Go and practise',
  'aprender.abriendo': 'Opening the practice shop…',
  'aprender.nadaReal': 'Nothing you do there touches your shop or is sent anywhere.',
  'aprender.enPractica': 'You are in the practice shop',
  'aprender.enPracticaDetalle': 'Everything here is made up. The sales are not sales and the money is not money. Pick a lesson and follow it.',
  'aprender.salirPractica': 'Leave and go back to my shop',
  'aprender.salirTitulo': 'Leave the practice shop?',
  'aprender.salirMensaje': 'The practice shop and everything you did in it are deleted, and you go back to your real shop. You can come back whenever you like; it always starts clean.',
  'aprender.salirConfirmar': 'Leave and delete the practice shop',
  'aprender.olvidar': 'Forget my progress and start over',
  'tutorial.rumbo.titulo': 'Where everything is',
  'tutorial.rumbo.para': 'The home screen and the menu',
  'tutorial.rumbo.bienvenida': 'Welcome. This system does four things: it takes payment, keeps track of what you have, records who owes you, and tells you whether you are making money. Nothing else. One thing at a time.',
  'tutorial.rumbo.practica': 'You are in a pretend shop. The products, customers and sales you are about to see are made up. Do whatever you like: you cannot break anything.',
  'tutorial.rumbo.inicio': 'This big button is always the next thing to do. If the till is closed it takes you to start the day, or your shift; if it is open it says “Sell”. If you do not know what to do, press the big button.',
  'tutorial.rumbo.atajos': 'Below are the shortcuts: the screens used most, one tap away. They are always in the same place.',
  'tutorial.rumbo.pendientes': 'Alerts appear here: orders for today, transfers not yet confirmed, things about to expire. If there is nothing, nothing shows. A line here is something somebody has to deal with.',
  'tutorial.rumbo.mas': 'Every screen, sorted into sections, is behind this button. Press it.',
  'tutorial.rumbo.grupos': 'Everything else is here, in a few sections. Each row names the screens inside it, and each section has its own colour.',
  'tutorial.rumbo.pestanas': 'Inside each section, its tabs are along the top. One tap takes you to the next one, without going back to the menu.',
  'tutorial.rumbo.volver': '“Back” takes you back to where you were, and the little house beside the screen’s name takes you straight home. You will never get stuck on a screen. Press “Back”.',
  'tutorial.rumbo.cierre': 'That is everything you need to find your way around: the big button, the shortcuts, the alerts and “Everything else”. Now let us do some work.',
  'tutorial.caja.titulo': 'Starting the day',
  'tutorial.caja.para': 'The first thing every day',
  'tutorial.caja.yaAbierta': 'The till is already open: it is started once a shift, on arriving. To practise starting it, the shift has to be ended first: do the “Ending the day” lesson first, then come back to this one.',
  'tutorial.caja.porque': 'Before selling, the money in the drawer is counted and written down: that is how the day, or your shift, starts. Without it, the system cannot tell you at the end whether it balances, because it does not know where you started.',
  'tutorial.caja.ir': 'This is the till screen. Right now it is closed: press the button to start.',
  'tutorial.caja.contar': 'Follow the steps: whether you brought money from the safe, how many notes of each there are, the coins and the dollars. If it asks you to count again, do it calmly. At the end, check and press the last button: the till is open.',
  'tutorial.caja.listo': 'Done, the till is open. What you counted is checked against what the last shift left and what you put in for change, and any difference is recorded as cash short or over. From now on the system records every córdoba and every dollar in and out, and when you end it will tell you whether what is there matches.',
  'tutorial.caja.pasos': 'Starting goes step by step: one question per screen, and each says what to do and why. Tap “Got it” and follow the steps; I am up here with you.',
  'tutorial.vender.titulo': 'Making a sale',
  'tutorial.vender.para': 'What you will do a hundred times a day',
  'tutorial.vender.cajaCerrada': 'The till first: it is closed, and nothing can be sold without an open till. Starting goes step by step, one question per screen. Tap “Got it” and follow the steps.',
  'tutorial.vender.abrirCaja': 'Follow the steps until the till is open: count what is in the drawer, screen by screen. Then the lesson goes on.',
  'tutorial.vender.porque': 'This is the screen you will use most. Find the product, add it, take the money. We will go slowly once and after that it comes by itself.',
  'tutorial.vender.buscar': 'Type part of a product name — “rice”, say — and tap the one you want from the list below.',
  'tutorial.vender.linea': 'There it is, added. Each line says how many, at what price, and what it comes to. Tap the name to change the price.',
  'tutorial.vender.cantidad': 'Taking two? Tap +. − takes one off, and tapping the number lets you type how many, 12 say.',
  'tutorial.vender.total': 'The total adds itself up. You never have to work it out on paper.',
  'tutorial.vender.cobrar': 'When they are not taking anything else, press “Charge”.',
  'tutorial.vender.metodo': 'This is how they are paying: cash, dollars, transfer, wallet, card or on credit. It is on cash, which is the usual one.',
  'tutorial.vender.monto': 'The amount is already filled in with what is owed. If they give you the exact money, touch nothing.',
  'tutorial.vender.confirmar': 'Now: “Confirm sale”. That takes it off the stock, records the money in the till, and saves what you made on it.',
  'tutorial.vender.recibo': 'That is the receipt. With a printer it prints; without one you can show it to the customer on the screen. That was a complete sale.',
  'tutorial.vuelto.titulo': 'Change and split payments',
  'tutorial.vuelto.para': 'When they pay with a big note',
  'tutorial.vuelto.cajaCerrada': 'Taking money needs an open till, and it is closed. Tap “Got it” and follow the steps to start.',
  'tutorial.vuelto.abrirCaja': 'Follow the steps until the till is open: count what is in the drawer, screen by screen. Then the lesson goes on.',
  'tutorial.vuelto.porque': 'Almost nobody pays the exact amount. Let us ring up a sale with too much money so you can see it work out the change.',
  'tutorial.vuelto.arma': 'Build a sale: find any product and add it.',
  'tutorial.vuelto.cobrar': 'Press “Charge”.',
  'tutorial.vuelto.masDeLaCuenta': 'Tap the amount and type what they handed you, MORE than it costs — as if they had given you 500 for a 60 córdoba basket.',
  'tutorial.vuelto.vuelto': 'There is the change, worked out. That is what you hand back. Nothing to subtract in your head.',
  'tutorial.vuelto.confirmar': 'Confirm the sale.',
  'tutorial.vuelto.partido': 'If somebody pays part one way and the rest another — some cash, the rest by card — this is the button: type the first part and add it. What is left is then filled in, ready to take the other way.',
  'tutorial.fiado.titulo': 'Credit and payments',
  'tutorial.fiado.para': 'Who you sell to on credit, and when they come to pay',
  'tutorial.fiado.cajaCerrada': 'Selling, on credit or not, needs an open till, and it is closed. Tap “Got it” and follow the steps to start.',
  'tutorial.fiado.abrirCaja': 'Follow the steps until the till is open: count what is in the drawer, screen by screen. Then the lesson goes on.',
  'tutorial.fiado.porque': 'Credit is the shop’s money walking out of the door not yet paid. Here what each person owes is written down, and every time they pay some of it, with no notebook. Let us do it with Doña Marta, who already owes something: she comes to pay a little and takes more.',
  'tutorial.fiado.cliente': 'Credit is always to somebody. Tap here to choose who the sale is for.',
  'tutorial.fiado.elegir': 'Find Doña Marta and tap her. Next to each name it says what they owe.',
  'tutorial.fiado.abonar': 'The sale is hers now, and under her name it says what she owes. She has brought money to pay, so tap “Pay some of what they owe”.',
  'tutorial.fiado.abono': 'The amount starts at everything she owes; type what she hands you, 100 for example, and tap “Record payment”. The cash goes into the drawer, and the till count will expect it.',
  'tutorial.fiado.comprobante': 'That is the slip: what she paid and what she still owes. You can send it on WhatsApp or print it. Tap “Done”.',
  'tutorial.fiado.agregar': 'Now she takes something more. Add it like in any sale: it is still hers.',
  'tutorial.fiado.cobrar': 'Press “Charge”.',
  'tutorial.fiado.fiar': 'Choose “On credit”. Nobody hands you money: the sale goes on her account.',
  'tutorial.fiado.cuenta': 'This says what she owes, what she would owe with this sale and how much credit she has. Past that limit, or with no limit ever set, the sale asks for the owner’s PIN.',
  'tutorial.fiado.confirmar': 'Confirm the sale. The receipt says what she owes in all, so she knows too.',
  'tutorial.fiado.queda': 'It is all on her page, in “Clients”: what she took, what she paid and what she owes, with the statement to send on WhatsApp or print.',
  'tutorial.corregir.titulo': 'Undoing a sale',
  'tutorial.corregir.para': 'When you got it wrong or they changed their mind',
  'tutorial.corregir.cajaCerrada': 'Voiding needs an open till: the sale’s money comes out of it to go back to the customer. It is closed: tap “Got it” and follow the steps to start.',
  'tutorial.corregir.abrirCaja': 'Follow the steps until the till is open: count what is in the drawer, screen by screen. Then the lesson goes on.',
  'tutorial.corregir.porque': 'You will make mistakes. Everybody does. What matters is that fixing one is easy and that it is recorded who fixed it and why.',
  'tutorial.corregir.ir': 'Here are the latest sales, newest at the top.',
  'tutorial.corregir.abrir': 'Tap a sale to open it and see the detail.',
  'tutorial.corregir.anular': 'Press “Void this sale”.',
  'tutorial.corregir.motivo': 'Write why. The reason is not paperwork: three months from now, when somebody asks why a thousand córdobas are missing from a Tuesday, this is the only thing that will answer.',
  'tutorial.corregir.stock': 'If the goods came back fine and went on the shelf, leave it set to return to stock. If they came back broken or spoiled, untick it: the money goes back either way, but the goods are written off.',
  'tutorial.corregir.confirmar': 'Confirm the void.',
  'tutorial.corregir.pin': 'Enter the PIN: in the practice shop it is 000000. Voiding asks for the PIN of whoever is signed in, so the void goes on record under the person who did it. A sale from a shift already closed takes the owner\'s PIN.',
  'tutorial.corregir.queda': 'The sale was not deleted: it is marked as voided, with its reason and its time. Nothing is ever deleted in this system — it is corrected with a new entry, the same way a ledger works.',
  'tutorial.cerrarCaja.titulo': 'Ending the day',
  'tutorial.cerrarCaja.para': 'The last thing every day',
  'tutorial.cerrarCaja.cajaCerrada': 'To end it, the till has to be open, and this one is closed. Open it first: tap “Got it” and follow the steps.',
  'tutorial.cerrarCaja.abrirCaja': 'Follow the steps until the till is open: count what is in the drawer, screen by screen. Then the lesson goes on.',
  'tutorial.cerrarCaja.porque': 'At the end, the drawer is counted and compared with what the system expected: that is the till count. It is the only way to find out money is missing the same day rather than three months later.',
  'tutorial.cerrarCaja.ir': 'Press “End my shift”.',
  'tutorial.cerrarCaja.aCiegas': 'Notice something important: the system does NOT show you what it expected until you have entered what you counted. That is deliberate. A count done while looking at the answer measures nothing.',
  'tutorial.cerrarCaja.contar': 'Count the notes of each kind and the coins, then the dollars, screen by screen. If it is well off, it asks you to count again without saying by how much: it is nearly always a note miscounted.',
  'tutorial.cerrarCaja.queda': 'This is what stays in the drawer for tomorrow\'s change: the float. It starts at 0; if you leave something today, write how much. Below it says how much goes to the owner.',
  'tutorial.cerrarCaja.cerrar': 'Go on: say who takes the money, check everything and press the last button to do the till count.',
  'tutorial.cerrarCaja.diferencia': 'There it tells you whether there was too much or too little. A small difference is almost always change given wrong. A big one — or a small one that turns up every day on the same person\'s shift — is something else.',
  'tutorial.cerrarCaja.sacar': 'And here, how much to take out: the rest, and every dollar, goes to the owner. Tomorrow, on starting, what is counted is checked against what was left today.',
  'tutorial.cerrarCaja.quien': 'First it asks whether anyone works after you. If someone takes over, you leave them the change; if the shop is closing, the day is gone over too. To practise closing for the day, tap “No, the shop is closing”.',
  'tutorial.cerrarCaja.dia': 'As the shop is closing, the day is gone over next: the summary to send the owner, what is still waiting, tomorrow\'s orders, and whether everything reached the server. Follow it to the end.',
  'tutorial.productos.titulo': 'Adding a product',
  'tutorial.productos.para': 'When you start selling something new',
  'tutorial.productos.porque': 'A product is created once and sold a thousand times. It is worth doing properly.',
  'tutorial.productos.ir': 'Press “New”.',
  'tutorial.productos.nombre': 'The name as you say it out loud, not as the supplier\'s invoice writes it. You will search for “rice”, not “RICE GRANZA ORO 80/20 SACK”.',
  'tutorial.productos.unidad': 'The base unit is how the stock is held: by item, by pound, by litre. If you sell rice loose by the pound, it is pounds — even though you buy it by the hundredweight.',
  'tutorial.productos.categoria': 'The category is for searching and for the reports. Do not overthink it: pick the obvious one.',
  'tutorial.productos.guardar': 'Save.',
  'tutorial.precios.titulo': 'Setting prices',
  'tutorial.precios.para': 'Every price in one sitting',
  'tutorial.precios.porque': 'A product with no price cannot be sold. This screen exists so you can price everything in one go instead of opening them one at a time.',
  'tutorial.precios.todos': 'The screen opens on “Missing Prices”: only the products still missing a price, so none gets missed. Tap “All” to see every one, with the prices it already has.',
  'tutorial.precios.ir': 'One row per product, with a box for each list: the retail price and the wholesale price, side by side. Type into whichever you like.',
  'tutorial.precios.escribir': 'Type a price and tap “Save” on its row: nothing is saved until then. What you save stays on the list until you switch views, in case you made a mistake.',
  'tutorial.precios.sugerido': 'Where the system already knows what it cost you, it suggests a price with a sensible margin. It is a suggestion, not an instruction: you know your customers.',
  'tutorial.precios.costos': '“Missing Costs” lists the products the system does not yet know the cost of: their sales look like pure profit and there is no price to suggest. The owner sets it with “Cost”, on each one\'s row.',
  'tutorial.precios.listas': 'There are two price lists: “Retail price” and “Wholesale price”. A route customer is assigned the wholesale one and from then on nobody has to remember to give them their price. Charging a different list at the till needs the owner\'s say-so.',
  'tutorial.comprar.titulo': 'Receiving goods',
  'tutorial.comprar.para': 'When the supplier\'s truck arrives',
  'tutorial.comprar.porque': 'Recording the purchase is what makes the stock figures mean anything and what lets the system know what each thing cost you. Without it, the profit it shows you is fiction.',
  'tutorial.comprar.agregar': 'Add one of the products they brought: scan its code or search for it by name. If it is not in the catalogue, you create it right here.',
  'tutorial.comprar.costo': 'Enter the quantity and the line TOTAL, exactly as the invoice says. Not the price per unit: the total. The system works the rest out, and that way no centavos are lost to rounding.',
  'tutorial.comprar.proveedor': 'Tap the field and pick the supplier who brought the goods, or type their name to find them.',
  'tutorial.comprar.pago': 'How did you pay? If you still owe for it, choose “Credit” and the due date — then the system warns you before the supplier has to ring. Paid part one way and part another? Use “Pay part another way”.',
  'tutorial.comprar.flete': 'If you paid for delivery, put it here. It is spread across the products by what each is worth, because delivery is part of what the goods cost you. Paid it to someone else, like the trucker? Add it as a separate payment too.',
  'tutorial.comprar.registrar': 'Record the purchase.',
  'tutorial.comprar.costoPromedio': 'Stock has gone up and the cost has been recalculated. If you bought rice at 20 and now buy it at 24, the system keeps neither figure: it averages what you actually hold. That is what makes the profit it shows you the real one.',
  'tutorial.inventario.titulo': 'Counting what you have',
  'tutorial.inventario.para': 'Stock, counts and losses',
  'tutorial.inventario.porque': 'The system knows what SHOULD be there. Only counting tells you what IS there. The gap between those two numbers is what you are losing.',
  'tutorial.inventario.ver': 'This is what the system believes you have, and what it is worth.',
  'tutorial.inventario.contar': 'Let us do a count. Add a product to it.',
  'tutorial.inventario.aCiegas': 'Same as the till count: while you are counting it does not show you what it expected. Count for real and write down what you found.',
  'tutorial.inventario.registrar': 'Record the count.',
  'tutorial.inventario.merma': 'When you know why something was lost — it expired, it broke, somebody took it — it is better to record it as a write-off with its reason than to let it turn up as a shortfall in the next count.',
  'tutorial.clientes.titulo': 'Clients',
  'tutorial.clientes.para': 'Who you give credit to and who you deliver to',
  'tutorial.clientes.porque': 'You do not need to record everybody who comes in for a fizzy drink. This is for the route customers and the ones who buy on credit — the ones somebody has to remember.',
  'tutorial.clientes.ir': 'Press “New”.',
  'tutorial.clientes.nombre': 'The name you know them by. “Doña Marta on the corner” is fine if that is what you call her.',
  'tutorial.clientes.tipo': '“Walk-in” is somebody who comes to the shop. “Wholesale / on a route” is somebody you deliver to.',
  'tutorial.clientes.lista': 'The price list they belong on. Set it once and from then on that customer gets their prices by themselves, without the cashier having to remember.',
  'tutorial.clientes.dia': 'If you deliver on a set day, put it in. That way everybody knows which day their order is due.',
  'tutorial.clientes.guardar': 'Save.',
  'tutorial.clientes.enLaVenta': 'From now on you can pick this customer while taking payment and the sale is tied to them — with their prices and their account.',
  'tutorial.pedidos.titulo': 'Taking an order',
  'tutorial.pedidos.para': 'Pre-sales: what gets delivered later',
  'tutorial.pedidos.porque': 'An order is what a customer asked for today to receive another day. The list of orders says what is to be delivered each day, and it is charged from there.',
  'tutorial.pedidos.ir': 'Tap “New order”.',
  'tutorial.pedidos.cliente': 'Tap the field and pick the customer who placed it, or type their name. If they are new, the last option on the list adds them without leaving the order.',
  'tutorial.pedidos.entrega': 'What day are you delivering it? The list puts the orders in the order of that day and marks today\'s.',
  'tutorial.pedidos.agregar': 'Add what they asked for, as at the till: look the product up by name or scan its code. If they want more than one, tap +.',
  'tutorial.pedidos.guardar': 'Tap “Save the order”. You are back on the list, with the order on it.',
  'tutorial.pedidos.cobrar': 'On the day it is delivered, “Charge” takes it to the till with everything loaded, at the prices the customer was given. It is charged like any sale, and the order moves to “Delivered” with its ticket.',
  'tutorial.pedidos.noReserva': 'One thing to watch: an order takes nothing out of the stock. The goods leave when the order is charged. If the owner turned on setting goods aside for orders in the business settings, the order does set them aside: nobody else can be promised them, and the till asks for the owner\'s PIN to sell them.',
  'tutorial.gastos.titulo': 'Expenses and payments',
  'tutorial.gastos.para': 'The electricity, the water, what you owe suppliers',
  'tutorial.gastos.porque': 'Profit is not what you sold minus what the goods cost. You still have to take off the electricity, the water, the rent and the bags. If you do not record expenses, the system will tell you that you are making more than you are.',
  'tutorial.gastos.ir': 'Press “New expense”.',
  'tutorial.gastos.categoria': 'Pick what it was for. The categories are the ones that are useful for seeing where the money goes.',
  'tutorial.gastos.descripcion': 'Write it the way you would say it to somebody: “September electricity bill”.',
  'tutorial.gastos.monto': 'How much.',
  'tutorial.gastos.guardar': 'Save.',
  'tutorial.gastos.noEsRetiro': 'One important thing: money you take out of the till for yourself is NOT a business expense. It is an owner\'s drawing, and it goes separately, under “Owner”. Confusing the two is the easiest way to believe the business is doing badly when what is happening is that you are taking a lot out.',
  'tutorial.gastos.verPorPagar': 'Press “Debts”.',
  'tutorial.gastos.proveedores': 'Here is what you owe suppliers, with the due dates, and the button to pay them something off.',
  'tutorial.numeros.titulo': 'Reading the numbers',
  'tutorial.numeros.para': 'Knowing whether you are making money',
  'tutorial.numeros.porque': 'This screen answers one question: are you making money or losing it? Everything else you recorded exists so that this number is true.',
  'tutorial.numeros.ir': 'First pick the period: today, this month, last month.',
  'tutorial.numeros.ventas': '“Income” is everything that came in from selling, before anything is taken off — and, if a till count came out over, what was over.',
  'tutorial.numeros.utilidad': '“Gross profit” is what is left after taking off what the goods you sold cost you. It has not yet taken off the electricity or the rent.',
  'tutorial.numeros.margen': 'The percentage beside it is how much you keep out of every hundred córdobas you sell. In a pulpería, twenty to thirty is normal. Much lower than that and either you are selling too cheap or you did not record your costs properly.',
  'tutorial.numeros.gastos': 'And here are the period\'s expenses. Gross profit minus expenses is what you actually made.',
  'tutorial.numeros.cuandoMirar': 'Look at it once a week, not every day. A bad day means nothing; four weeks heading down does.',
  'tutorial.planilla.titulo': 'Payroll',
  'tutorial.planilla.para': 'Paying staff, INSS and all',
  'tutorial.planilla.porque': 'If you have people working for you, the law wants INSS, aguinaldo, holiday and severance. The system works all of that out for you, at Nicaraguan rates.',
  'tutorial.planilla.ir': 'This is your staff, with their job and how long they have been there.',
  'tutorial.planilla.acumulado': 'Here you see what is already owed to each person even though you have not paid it yet: aguinaldo and holiday build up month by month from the first day.',
  'tutorial.planilla.correr': '“Run payroll” builds the pay: for the month, the 15 days or the week. Press it.',
  'tutorial.planilla.unaVez': 'Nobody is paid TWICE for the same day: whoever is already in another run for those days is left out, because paying them twice would double every provision. You enter what varies — the days of anyone paid by the day, overtime, holiday paid, aguinaldo in December — and the system calculates the rest.',
  'tutorial.planilla.pagar': 'Running payroll creates the obligation; handing over the money is separate, because in practice they happen on different days.',
  'tutorial.respaldo.titulo': 'Backup and language',
  'tutorial.respaldo.para': 'With no signal, and in two languages',
  'tutorial.respaldo.porque': 'Two things worth knowing before this goes into the real shop.',
  'tutorial.respaldo.sync': 'In your shop, this line says whether what you did is saved on the server: green is up to date, and a number “not sent” is what is still only on this device. Here it says “Practice mode”, because nothing you do here is sent.',
  'tutorial.respaldo.sinConexion': 'With no internet everything still works: you can sell, take payment and close the till exactly the same. It is saved on the device and sent by itself when the signal comes back. The only thing you cannot do without signal is register a new device.',
  'tutorial.respaldo.codigo': 'Every device has two letters, and every receipt starts with them. That is why two tills working offline at the same time can never issue the same receipt number.',
  'tutorial.respaldo.idioma': 'And this is where the language changes. It is per device: one can be in Spanish and another in English, showing the same information.',
  'tutorial.respaldo.final': 'That is it. You can work with this now. When you forget something, the lessons stay here to be repeated.',
  'sync.sinEnviar': '{n} not sent',
  'sync.acumulando':
    '{n} records have not reached the server yet. If this device is lost, they are lost with it. Get online as soon as you can.',
  'sync.rechazados': '{n} record(s) rejected by the server. They need review.',
  'sync.deriva':
    "This device's clock is off by {s}s. Fix it: sales can end up recorded on the wrong day.",

  // ---------- Update ----------
  'actualizar.hayVersion': 'A new version is available.',
  'actualizar.ahora': 'Update',
  'actualizar.despues': 'Later',
  'actualizar.revisando': 'Checking for updates…',
  'actualizar.titulo': 'There is a new version',
  'actualizar.hayQue': 'Update to continue. Nothing already saved on this device is lost, even if it has not been sent yet.',
  'actualizar.actualizando': 'Updating…',
  'actualizar.fallo':
    'Could not update. Check the connection and try again, or continue with the old version and update as soon as you can.',
  'actualizar.reintentar': 'Try again',
  'actualizar.seguirSinActualizar': 'Continue without updating',

  // ---------- Owner PIN ----------
  'preguntar.anularGasto': 'Void this expense',
  'preguntar.cancelarPedido': 'Cancel this order',
  'preguntar.abonar': 'Pay towards this purchase',
  'preguntar.abonarGasto': 'Pay towards this expense',
  'preguntar.cuanto': 'How much are you paying?',
  'preguntar.faltaPagar': '{monto} still owed',
  'preguntar.desactivarCliente': 'Deactivate client',
  'preguntar.desactivar': 'Yes, deactivate',
  'preguntar.quitarDeLaVenta': 'Remove from the sale?',
  'preguntar.quitarDelPedido': 'Remove from the order?',
  'pin.autorizacion': "Owner's authorisation",
  'pin.incorrecto': 'Wrong PIN. Try again.',
  'pin.espere': 'Too many tries. Wait {tiempo} before trying again.',
  'pin.segundos': '{n} s',
  'pin.minutos': '{n} min',
  'pin.borrar': 'Delete the last digit',
  'pin.aceptado': 'PIN accepted',
  'pin.confirmar': 'Confirm with your PIN',

  // ---------- Home ----------
  'inicio.vender': 'Sell',
  'inicio.ajustes': 'Settings',
  'inicio.bloquear': 'Lock/Switch User',
  'inicio.sinSesion': 'Not signed in',
  'inicio.vendidoHoy': 'Sold today',
  'inicio.lotesPorVencer': '{n} batch(es) expired or expiring',
  'inicio.pedidosHoy': '{n} order(s) to deliver today',
  'inicio.pedidosCortos': '{n} order(s) without enough stock to deliver',
  'inicio.transferenciasPendientes': '{n} transfer(s) not confirmed',
  'inicio.transferenciasViejas': '{n} transfer(s) not confirmed, the oldest from {dias} days ago',

  // ---------- Sections ----------

  // ---------- Point of sale ----------
  'pos.sinCaja': 'No till is open',
  'pos.sinCajaExplicacion':
    'Before selling, start the day, or your shift: count the money the drawer starts with.',
  'pos.volverInicio': 'Back to home',
  'pos.cliente': 'Client',
  'pos.clientes': 'Clients',
  'pos.caja': 'Till',
  'pos.historial': 'History',
  'pos.entregandoPedido':
    "Delivering an order for {cliente}. Charging it marks the order delivered.",
  'pos.unCliente': 'a client',
  'pos.pedidoDespues': '{cliente}\'s order loads as soon as the till is open.',
  'pos.pedidoAlRecibir': '{cliente}\'s order loads as soon as you take over the till.',
  'pos.reemplazarTitulo': 'Swap the sale for the order?',
  'pos.reemplazarMensaje': 'The sale already has {n} line(s). Loading {cliente}\'s order takes them off, and the sale is just the order.',
  'pos.cargarElPedido': 'Load the order',
  'pos.ventaRapidaEnlace': 'Quick sale (something not in the catalogue)',
  'pos.busqueEmpezar': 'Search for a product to start',
  'pos.lista': 'list',
  'pos.cobrar': 'Charge',
  'pos.noSePudoRegistrar': 'The sale could not be recorded',
  'pos.motivoAutorizacion':
    'This price falls below the minimum margin. Whoever authorises it will be recorded.',
  'pos.motivoRapidas':
    'The quick sales on this sale come to {monto}, more than the {maximo} that goes through without the owner. With no product, nothing checks their price. Whoever authorises it will be recorded.',
  'pos.rapidasPidenDueno': "The quick sales on this sale come to more than {maximo}: charging it asks for the owner's PIN.",
  'pos.loteVencido': 'some expired',
  'pos.medicinaVencida':
    "{producto}: there are units here that expired on {fecha}. Check the one being sold is not one of them; charging it asks for the owner's PIN. Expired units are written off in «Expiry».",
  'pos.medicinaVencidaDueno':
    '{producto}: there are units here that expired on {fecha}. Check the one being sold is not one of them, and write them off in «Expiry».',
  'pos.perecederoVencido':
    '{producto}: there are units here that expired on {fecha}. Do not sell those; they are written off in «Expiry».',
  'pos.motivoVencida':
    'This sale has medicine with expired units on the shelf: {productos}. Check that what the customer takes has not expired. Whoever authorises it will be recorded.',
  'pos.lineaApartada': '{cantidad} set aside',
  'pos.apartadoPara': '{producto}: {cantidad} for {clientes}',
  'pos.apartadoPideDueno': "{producto}: {cantidad} of this sale is set aside for {clientes}; charging it asks for the owner's PIN.",
  'pos.apartadoDueno': '{producto}: {cantidad} of this sale is set aside for {clientes}. Sold, what they ordered falls short.',
  'pos.venderApartado': 'Sell what is set aside?',
  'pos.venderIgual': 'Sell anyway',
  'pos.motivoApartado':
    'This sale takes goods set aside for orders: {productos}. Sold, what they ordered falls short until more comes in. Whoever authorises it will be recorded.',
  'pos.motivoApartadoDueno':
    'This sale takes goods set aside for orders: {productos}. Sold, what they ordered falls short until more comes in.',
  'pos.lineaQueda': '{cantidad} left',
  'pos.faltaPara': '{producto}: {libre} left, takes {lleva}',
  'pos.faltaDueno': '{producto}: {libre} left and the sale takes {lleva}, more than the books say.',
  'pos.faltaPideDueno': "{producto}: {libre} left and the sale takes {lleva}; charging it asks for the owner's PIN.",
  'pos.faltaApartadoDueno': '{producto}: {libre} left unreserved and the sale takes {lleva}; the rest is set aside for {clientes}.',
  'pos.faltaApartadoPideDueno':
    "{producto}: {libre} left unreserved and the sale takes {lleva}; the rest is set aside for {clientes}, and charging it asks for the owner's PIN.",
  'pos.venderDeMas': 'Sell more than there is?',
  'pos.motivoFaltaDueno':
    'This sale takes more than the books say is free: {productos}. If it is in your hand, sell it: what the books say is put right by counting.',
  'pos.motivoFalta':
    'This sale takes more than the books say is free: {productos}. If it is in your hand, sell it: what the books say is put right by counting. Whoever authorises it will be recorded.',
  'pos.confirmando': 'Confirming with the server…',
  'pos.yaNoHay': '{motivo}. Another till sold it first: the sale was not charged.',
  'pos.sinConexionVende': 'No connection: sales are not confirmed with the server. The till keeps selling.',
  'pos.sinConfirmar': 'The server did not answer in time: the sale was recorded unconfirmed, and goes out as soon as there is a connection.',
  'pos.rapidaPideDueno': "With this one, the quick sales on this sale come to more than {maximo}: charging it will ask for the owner's PIN.",
  'pos.motivoLista':
    '«{lista}» is not this sale\'s price list. Whoever authorises it will be recorded.',
  'pos.nombreOTelefono': 'Name or phone',
  'pos.pedidosPorEntregar': 'Orders to deliver',
  'pos.entrega': 'Delivery {fecha} · {n} line(s)',
  'pos.sinCliente': 'No client (counter sale)',
  'pos.listaPredeterminada': 'Default price list',
  'pos.cobrarA': 'Charge at',
  'pos.listaDeLinea': 'Price list for {producto}',
  'pos.presentacionDeLinea': 'Pack size for {producto}',
  'pos.clienteMostrador': 'Walk-in customer',
  'pos.clienteAyuda': 'Pick a client to use their prices or their orders',
  'pos.clienteCambiar': 'Tap to change',
  'pos.quitarCliente': 'Remove the client',
  'pos.quitarLinea': 'Remove {producto} from the sale',
  'pos.cantidadDe': 'How many {producto}',
  'pos.unoMas': 'One more',
  'pos.unoMenos': 'One fewer',
  'pos.confirmarQuitar': '{producto} ({total}) comes off this sale.',
  'lista.publico': 'Retail price',
  'lista.mayorista': 'Wholesale price',
  'lista.publicoCorta': 'Retail',
  'lista.mayoristaCorta': 'Wholesale',
  'pos.venta': 'Sale {ticket}',
  'pos.siguienteVenta': 'Next sale',
  'pos.volverAPedidos': 'Back to the orders',
  'pos.ventaRapida': 'Quick sale',
  'pos.ventaRapidaExplicacion':
    'For charging something not yet in the catalogue. It moves no stock and is flagged so somebody can turn it into a product later.',
  'pos.descripcion': 'Description',
  'pos.ejemploVentaRapida': 'Bag of ice',
  'pos.producto': 'Product',
  'pos.sinPrecio': 'no price',
  'pos.faltaPrecio':
    '{producto} has no price on this list. Type it here, or set it in Products so it sticks.',
  'pos.faltaPrecioSoloAqui':
    '{producto} has no price on this list. Type it here for this sale; the owner sets its price in Products.',
  'pos.hayLineasSinPrecio':
    '{n} product(s) have no price. Tap each one and type it — nothing can be charged at zero.',
  'pos.faltaPrecioPida':
    '{producto} has no price on this list. Ask the owner to set one in Products, or take it off the sale.',
  'pos.hayLineasSinPrecioPida':
    '{n} product(s) have no price: the owner sets them in Products, or take them off the sale. Nothing can be charged at zero.',
  'pos.enLaVenta': '{n} in this sale',

  // ---------- Charging ----------
  'cobro.titulo': 'Charge',
  'cobro.vuelto': 'Change',
  'cobro.registrando': 'Recording…',
  'cobro.confirmarVenta': 'Confirm sale',
  'cobro.faltan': '{monto} short',
  'cobro.redondeo': 'Cash rounding',
  'cobro.productosYEnvio': 'Goods + delivery',
  'envio.campo': 'Delivery cost',
  'envio.mal': 'Type an amount, or leave it empty.',
  'envio.demasiado': 'With that delivery cost it is more than the system can hold, C$ 100,000,000.',
  'cobro.sinConfirmar': 'unconfirmed',
  'cobro.agregarPago': 'Add this payment',
  'cobro.dividir': 'Split between methods',
  'cobro.ayudaParte': 'Type only what they pay this way. The rest is taken next.',
  'cobro.ayudaVuelto': 'If they hand you more, type it to see the change.',
  'cobro.dolaresA': 'US$ {monto} at {tasa}',
  'cobro.dolaresRecibidos': 'Dollars received',
  'cobro.monto': 'Amount',
  'cobro.equivale': 'Comes to {monto} at {tasa}',
  'cobro.ayudaReferencia':
    'It stays pending until somebody confirms the money arrived. A screenshot is not a payment.',
  'cobro.referenciaObligatoria': 'Receipt reference',
  'cobro.faltaReferencia': 'Enter the reference',
  'cobro.referenciaRepetida': 'That reference already paid sale {ticket} ({monto}). A receipt pays only once: check it before accepting it.',
  'cobro.referenciaEnRecarga': 'That reference already paid a top-up ({monto}). A receipt pays only once: check it before accepting it.',
  'cobro.referenciaEnAbono': 'That reference already paid a payment on account ({monto}). A receipt pays only once: check it before accepting it.',
  'cobro.referenciaEnOtraParte': 'That reference is already on another part of this payment.',
  'cobro.sinVuelto': 'A transfer or a wallet payment gives no change: it pays the {monto} still owed.',
  'pago.efectivo_cordobas': 'Cash C$',
  'pago.efectivo_dolares': 'Dollars',
  'pago.transferencia': 'Bank transfer',
  'pago.billetera': 'Mobile wallet',
  'pago.tarjeta': 'Card',
  'pago.credito': 'Credit',

  // ---------- Sale line ----------
  'linea.pedirAutorizacion': 'Ask for authorisation',
  'linea.ayudaCantidad': 'Halves and quarters work: 2 1/2, 0.25',
  'linea.presentacion': 'Pack size',
  'linea.precioLista': 'List price: {monto}',
  'linea.precioFijo': 'List price: {monto}. Your user does not change prices.',
  'linea.motivoCambio': 'Reason for the change',
  'linea.ejemploMotivo': 'Regular customer',
  'linea.faltaMotivo': 'Every price change needs a reason',
  'linea.bajoMinimoRegistrado':
    'This price falls below the minimum margin. It will be recorded in the log.',
  'linea.bajoMinimoPin': "This price falls below the minimum margin. It needs the owner's PIN.",
  'linea.sinCostoPin': "Nobody knows what this product cost: bringing its price down needs the owner's PIN.",

  // ---------- Search and margin ----------
  'buscador.placeholder': 'Name or code…',
  'buscador.codigoDesconocido': 'Code {codigo} is not in the catalogue.',
  'buscador.crear': 'Create “{nombre}”',
  'buscador.preguntaTitulo': 'Unknown code',
  'buscador.desactivado': 'Deactivated',
  'abiertos.buscando': 'Looking this code up in the open catalogues…',
  'abiertos.segun': 'According to {fuente}',
  'abiertos.nadie': 'The open catalogues do not know it either.',
  'abiertos.enPractica': 'The practice shop does not look codes up in the open catalogues: the real shop does.',
  'abiertos.sinRespuesta': 'The open catalogues could not be asked: no signal, or no answer.',
  'abiertos.suyos': 'Is it one of yours? Tap it and the code is saved on it.',
  'abiertos.alCrear': '“Create a new product” fills it in from these details.',
  'abiertos.esEste': 'Is this the product?',
  'abiertos.usar': 'Use these details',
  'abiertos.noUsar': 'No, I will fill it in',
  'abiertos.ver': 'See what {fuente} says',
  'abiertos.llenado': 'Filled in from what {fuente} says. Check it before saving.',
  'margen.margen': 'margin',
  'margen.sobreCosto': '{recargo} over cost · makes {ganancia}',
  'margen.bajoCosto': 'Sold below cost',

  // ---------- Till ----------
  'caja.titulo': 'Till',
  'caja.abierta': 'Till open',
  'caja.subtitulo': '{n} sale(s)',
  'caja.fondoInicial': 'Opening float',
  'caja.ventasTurno': 'Sales this shift',
  'caja.efectivoRecibido': 'Cash C$ taken',
  'caja.dolaresRecibidos': 'Dollars taken',
  'caja.otrosMedios': 'Other payment methods',
  'caja.vueltoEntregado': 'Change given',
  'caja.retiros': 'Cash out',
  'caja.ingresos': 'Cash in',
  'caja.retirar': 'Take cash out',
  'caja.ingresar': 'Put cash in',
  'caja.registrar': 'Record it',
  'caja.ejemploRetiro': 'To the safe',
  'caja.ejemploIngreso': 'Extra change',
  'caja.explicacionMovimiento':
    'The money does not vanish: it leaves the drawer and goes into the safe, so the expected count drops but the books still balance.',
  'caja.listo': 'Done',
  'caja.esperadoCordobas': 'Expected in córdobas',
  'caja.esperadoAyuda': 'What the drawer should hold in córdobas, going by what the shift recorded: the float it opened with, plus the cash that came in, less the cash that went out. What was paid by card, bank transfer or mobile wallet, or sold on credit, never goes through the drawer, and dollars are counted on their own.',
  'caja.contado': 'Counted',
  'caja.cuadraExacto': 'The till balances exactly.',
  'caja.sobrante': 'Over',
  'caja.faltante': 'Short',
  'caja.fondoCordobas': 'Córdobas in the drawer',
  'caja.fondoDolares': 'Dollars in the drawer',
  'caja.montoInvalido': 'Type just the number, 20 or 20.50 say.',
  'caja.fondoEnDolares': 'Float in dollars',
  'caja.dolaresRetirados': 'Dollars taken out',
  'caja.dolaresIngresados': 'Dollars put in',
  'caja.moneda': 'Currency',
  'caja.montoCordobas': 'Amount in córdobas',
  'caja.montoDolares': 'Amount in dollars',
  'caja.esperadoDolares': 'Expected in dollars',
  'caja.esperadoDolaresAyuda': 'The dollars the drawer should hold: those it opened with, plus those customers paid with and those put in, less those taken out. Change is given in córdobas, so it never lowers them.',
  'caja.cordobasCuadran': 'The córdobas balance.',
  'caja.dolaresCuadran': 'The dollars balance.',
  'caja.cordobasContados': 'Córdobas counted',
  'caja.dolaresContados': 'Dollars counted',
  'caja.seValoranA': 'Valued at {tasa}',
  'caja.turnosAnteriores': 'Previous shifts',
  'caja.sinTurnos': 'No shifts closed yet.',
  'caja.vendido': 'sold {monto}',
  'caja.cuadro': 'balanced',
  'caja.dejoElCorte': 'Left by the last till count',
  'caja.puestoDeGeneral': 'Put in from general cash',
  'caja.debiaHaber': 'Should have been',
  'caja.aperturaCuadra': 'Everything the till count left is there.',
  'caja.aperturaCuadraPuesto': 'It is all there: what the till count left and what you put in.',
  'caja.aperturaCuadraLoPuesto': 'Everything you put in is there.',
  'caja.aperturaCuadraVacia': 'The drawer started empty, as it should.',
  'caja.aperturaExplicacion':
    'The difference is recorded as cash over or short, just as at a till count, and the shift starts from what you counted.',
  'caja.diferenciaApertura': 'Difference at opening',
  'caja.ajusteEntrega': 'Difference at handover',
  'caja.ajusteEntregaDolares': 'Dollar difference at handover',
  'caja.quedaMasQueContado': 'You counted {monto}: you cannot leave more than that.',
  'caja.saque': 'Take out of the drawer',
  'caja.saqueNota': 'Put it with the general cash. {monto} stays in the drawer for change.',
  'caja.nadaQueSacar': 'Nothing to take out: the {monto} stays in the drawer for change.',
  'caja.saqueTodo': 'Put it with the general cash. Nothing stays in the drawer.',
  'caja.recibeAhora': 'The owner takes it now',
  'caja.recibeAhoraAyuda': 'With their PIN: it goes straight to general cash.',
  'caja.porRecibirAyuda': "Otherwise it waits to be received: the owner counts it when they get it, and anything missing is this shift's.",
  'caja.ustedRecibe': 'You take it: it goes to general cash.',
  'caja.motivoRecibir': 'What leaves the drawer goes to general cash, received under your name.',
  'caja.saqueNotaPorRecibir':
    'Hand it to the owner: it waits to be received until they count it. {monto} stays in the drawer for change.',
  'caja.saqueTodoPorRecibir': 'Hand it to the owner: it waits to be received until they count it. Nothing stays in the drawer.',
  'caja.gavetaVacia': 'The drawer is empty: there is nothing to take out.',
  'caja.deRuta': 'This till belongs to a route that went out before delivery runs were taken out: it closes by settling the route, the cash and what is left on the truck together.',
  'caja.entregado': 'to general cash {monto}',
  'caja.alAbrir': 'at opening {monto}',
  'caja.abrioCerro': 'opened by {abrio}, closed by {cerro}',
  'caja.enEntregas': 'at handover {monto}',

  'caja.cerradaTitulo': 'The till is closed',
  'caja.cerradaExplicacion': 'To sell, start: what is in the drawer is counted, step by step.',
  'caja.primerConteoAlAbrir': 'counted twice at opening; the first, {monto}',
  'caja.primerConteoAlCerrar': 'counted twice at closing; the first, {monto}',
  'caja.primerConteoAlRecibir': 'counted twice on taking it over; the first, {monto}',

  // ---------- Starting and ending: the day and the shift ----------
  'asistente.paso': '{n} of {total}',
  'asistente.atras': 'Back',
  'asistente.siguiente': 'Next',
  'contar.billetesDe': '{billete} notes',
  'contar.monedas': 'Coins',
  'contar.monedasEtiqueta': 'Coins, in córdobas',
  'contar.monedasAyuda': 'The coins go together: add them up and write what they come to.',
  'contar.total': 'Total counted',
  'contar.revise': 'Check what you typed',
  'turno.empezarDia': 'Start the day',
  'turno.empezarTurno': 'Start my shift',
  'turno.terminarTurno': 'End my shift',
  'turno.terminarDia': 'End the day',
  'turno.subtitulo': 'One step at a time',
  'turno.cajaDe': "{nombre}'s till",
  'turno.comenzar': 'Start',
  'turno.empezarDiaExplicacion':
    'Before selling, the money in the drawer is counted. That way, at the end of the day, you know whether everything balanced. A few steps, one per screen.',
  'turno.empezarTurnoExplicacion':
    'Before serving, count the money the last shift left you. If anything is missing or over, it is known now and not put down to you.',
  'turno.pusoTitulo': 'Did you put money from general cash in the drawer?',
  'turno.pusoExplicacion': 'For example, if change was short and you brought more from the safe. If you added nothing, say no.',
  'turno.pusoCambioTitulo': 'Did you put in cash for change?',
  'turno.pusoCambioExplicacion':
    'The drawer starts with no float. If you brought money from general cash to give change, say how much; if you brought nothing, say no.',
  'turno.pusoNo': 'No, I added nothing',
  'turno.pusoSi': 'Yes, I added money',
  'turno.cuantoPuso': 'How many córdobas did you add?',
  'turno.cuantoPusoAyuda': 'What you brought from general cash. It is part of what you count next.',
  'turno.contarCordobas': 'Count the córdobas in the drawer',
  'turno.contarCordobasExplicacion':
    'Write how many notes of each there are, and what the coins come to: the system adds it up. Count what is really there, not what should be.',
  'turno.contarCordobasVisible':
    'The last shift left {monto}. Write how many notes of each there are, and what the coins come to.',
  'turno.contarCiego':
    'Write how many notes of each there are, and what the coins come to. The system does not tell you what it expected until the end: that way the count really means something.',
  'turno.contarDolares': 'Are there dollars in the drawer?',
  'turno.contarDolaresAbrir': 'Count the dollar bills separately. If there are none, tap “No dollars”.',
  'turno.contarDolaresCerrar': 'Count all the dollar bills separately. If there are none, tap “No dollars”.',
  'turno.sinDolares': 'No dollars',
  'turno.sinCordobas': 'No córdobas',
  'turno.contarLoPuesto': 'Count the {monto} you put in for change too: it is already in the drawer.',
  'turno.otraVez': 'Something is off: count again',
  'turno.otraVezExplicacion':
    'Count again, calmly: note by note, and the coins. We do not say by how much or which way: that way the count means something. What you count now is what stands.',
  'turno.revise': 'Check before going on',
  'turno.reviseAbrir': 'If something is wrong, tap “Back” and fix it. The button below opens the till.',
  'turno.reviseCerrar': 'If something is wrong, tap “Back” and fix it. The button below does the till count and closes the till.',
  'turno.reviseRecibir': "If something is wrong, tap “Back” and fix it. Once you take it over, {nombre}'s till is in your name.",
  'turno.loQueDejo': 'What the last shift left',
  'turno.loQueEncontro': 'What the count found',
  'turno.pedidosHoy': 'Orders to deliver today',
  'turno.pedidosHoyExplicacion':
    'These customers expect their order today. If one should have gone already, it says since when.',
  'turno.pedidoAtrasado': 'Late: it was for {fecha}',
  'turno.avisosTitulo': 'Before selling, look at this',
  'turno.avisosExplicacion':
    'Products running out, expiring or needing a count. Tap one to see it, or go on: they are on the home screen too.',
  'turno.abiertaTitulo': 'Done! The till is open',
  'turno.abiertaExplicacion': 'You can sell now. At the end of your shift, tap “End my shift” on the home screen and follow the steps.',
  'turno.irAlInicio': 'Go to the home screen',
  'turno.yaAbierta': 'There is a till open on this device already. To start another, end that shift first.',
  'turno.verCaja': 'See the till',
  'turno.noHayCaja': 'There is no till open on this device: there is no shift to end.',
  'turno.nadaQueRecibir': "There is nobody else's till open on this device.",
  'turno.quienSigue': 'Is anyone working after you today?',
  'turno.quienSigueExplicacion':
    'When your shift ends, the drawer is counted and the money handed over. If the shop is closing, the day is gone over too.',
  'turno.sigueOtra': 'Yes, someone else takes over',
  'turno.sigueOtraDetalle': 'Change is left for whoever is next, and the rest is handed over.',
  'turno.seCierra': 'No, the shop is closing',
  'turno.seCierraDetalle': "Change is left for tomorrow, the rest is handed over, and the day is gone over.",
  'turno.quedaTitulo': 'How much do you leave in the drawer for change?',
  'turno.quedaExplicacion':
    'It is the float for giving change, and it stays in the drawer {para}. It is filled in already: change it only if you leave a different amount. The rest, and every dollar, goes to the owner.',
  'turno.quedaExplicacionCero':
    'What you leave for change stays in the drawer {para}. It starts at 0: write how much you leave, if anything. The rest, and every dollar, goes to the owner.',
  'turno.paraManana': 'until tomorrow',
  'turno.paraElSiguiente': 'for whoever is next',
  'turno.quedaCampo': 'Stays in the drawer',
  'turno.quedaAyuda': 'In córdobas. It cannot be more than you counted.',
  'turno.saleCordobas': 'Goes to the owner',
  'turno.saleDolares': 'Dollars to the owner',
  'turno.quienRecibe': 'Who takes the money?',
  'turno.quienRecibeExplicacion':
    'What leaves the drawer goes to general cash. If the owner is here, they take it now, with their PIN; if not, you hand it over later and they count it.',
  'turno.recibeAhora': 'The owner takes it now',
  'turno.recibeDespues': 'I hand it over later',
  'turno.contoCordobas': 'Córdobas counted',
  'turno.contoDolares': 'Dollars counted',
  'turno.loRecibe': 'Taken by',
  'turno.recibeUsted': 'You',
  'turno.recibeElDueno': 'The owner, now',
  'turno.quedaPorRecibir': 'The owner, later',
  'turno.notas': 'Anything the owner should know (if any)',
  'turno.notasAyuda': 'For example: “the water bill was paid from the drawer” or “a 500 note was torn”.',
  'turno.resultadoTitulo': 'What the till count found',
  'turno.resumenTitulo': "The day's summary",
  'turno.resumenExplicacion': 'Everything today at this till, shift by shift. Send it to the owner by WhatsApp, or print it.',
  'turno.enviarWhatsapp': 'Send by WhatsApp',
  'turno.pendientesTitulo': 'What is still waiting',
  'turno.pendientesExplicacion': 'This is waiting for the owner. Tap one to see it now, or go on: it is on the home screen too.',
  'turno.mananaTitulo': "Tomorrow's orders",
  'turno.mananaExplicacion': 'These customers expect their order tomorrow. If something needs getting ready, now is the time.',
  'turno.guardadoTitulo': 'Is everything on the server?',
  'turno.guardadoExplicacion':
    "Before switching off, check that today's records went up. What has not gone up is only on this device.",
  'turno.guardadoTodo': "Everything from today is saved on the server.",
  'turno.guardando': "Sending today's records to the server… {n} to go.",
  'turno.guardadoSinConexion':
    'No internet. {n} record(s) are saved only on this device: they go up by themselves when the signal comes back. Do not delete the app or its data.',
  'turno.guardadoError': 'Could not send: {error}. It tries again by itself.',
  'turno.guardadoNoSube': 'This device is not sending anything to the server. Tell the owner.',
  'turno.guardadoPractica': 'The practice shop saves nothing on the server: there is nothing to send here.',
  'turno.reintentar': 'Try again',
  'turno.hastaManana': 'Done! See you tomorrow',
  'turno.hastaMananaExplicacion': 'The till is closed and the day gone over. Tomorrow, tap “Start the day” on the home screen.',
  'turno.turnoTerminado': 'Your shift is over',
  'turno.turnoTerminadoExplicacion':
    'Now whoever is next signs in with their PIN and taps “Start my shift”: they count what you left in the drawer.',
  'turno.pasarALaSiguiente': 'Hand over to whoever is next',
  'turno.aContar': 'Start counting',
  'turno.primerConteo': 'First count',
  'turno.segundoConteo': 'Second count',
  'turno.r.titulo': "Day's summary {fecha}",
  'turno.r.vendido': 'Sold',
  'turno.r.enEfectivo': 'In cash',
  'turno.r.otros': 'Transfer/card',
  'turno.r.fiado': 'On credit',
  'turno.r.dolares': 'Dollars taken',
  'turno.r.abonos': 'Payments in',
  'turno.r.devoluciones': 'Returns',
  'turno.r.retiros': 'Cash out',
  'turno.r.pagado': 'Paid from drawer',
  'turno.r.alAbrir': 'At opening',
  'turno.r.primerConteo': '1st count',
  'turno.r.entregas': 'At handover',
  'turno.r.corte': 'Till count',
  'turno.r.corteDolares': 'Dollar count',
  'turno.r.cuadro': 'Balanced',
  'turno.r.sobro': 'Over {monto}',
  'turno.r.falto': 'Short {monto}',
  'turno.r.queda': 'Left in drawer',
  'turno.r.saco': 'Taken out',
  'turno.r.porRecibir': '(Not yet received by the owner)',
  'turno.r.totalDia': "Day's total",
  'turno.r.diferencias': 'Differences',

  // ---------- Sales ----------
  'ventas.titulo': 'Sales',
  'ventas.registradas': '{n} recorded',
  'ventas.registradasHoy': '{n} recorded · {total}',
  'ventas.sinVentas': 'No sales yet.',
  'ventas.rapidasRecientes': '{n} quick sale(s) in the last {dias} days',
  'ventas.rapidasTitulo': 'Quick sales',
  'ventas.rapidasSubtitulo': '{n} in the last {dias} days · {total}',
  'ventas.rapidasAyuda': 'What was charged with no product: no cost, no stock moved, no margin checked. The same name or the same person over and over deserves a question; what sells often is worth making a product.',
  'ventas.sinRapidas': 'No quick sales in the last {dias} days.',
  'ventas.rapidaMeta': '{fecha} · {ticket} · {quien}',
  'ventas.autorizo': 'authorised by {nombre}',
  'ventas.alguien': 'someone',
  'ventas.crearProducto': 'Create product',
  'ventas.sinVentasHoy': 'No sales yet today.',
  'ventas.lineas': '{fecha} · {n} line(s)',
  'ventas.transferenciaSinConfirmar': 'unconfirmed transfer',
  'ventas.anulada': 'voided',
  'ventas.noSePudoAnular': 'This sale could not be voided.',
  'ventas.descuento': 'Discount',
  'ventas.envio': 'Delivery cost',
  'ventas.redondeo': 'Rounding',
  'ventas.pendiente': ' (pending)',
  'ventas.utilidadBruta': 'Gross profit',
  'ventas.motivoAnulacion': 'Voided: {motivo}',
  'ventas.anularVenta': 'Void this sale',
  'ventas.anularDeCaja':
    'This sale is from a shift already closed: the {monto} in cash comes out of the till open on this device, to hand back to the customer.',
  'ventas.anularSinCaja':
    'This sale is from a shift already closed. To hand back the {monto} in cash, first open the till on this device.',
  'ventas.pinParaAnular': "Voiding {ticket} goes on record under {nombre}. Enter {nombre}'s PIN to confirm.",
  'ventas.regresaInventario': 'The goods go back into stock',
  'ventas.regresaInventarioDesc':
    'Uncheck it if they came back damaged: the cost goes to spoilage instead of to inventory.',

  // ---------- Transfers ----------
  'transf.titulo': 'Unconfirmed transfers',
  'transf.abono': 'Payment on account',
  'transf.recarga': 'Top-up {operadora} {telefono}',
  'transf.deHoy': 'from today',
  'transf.deAyer': 'waiting since yesterday',
  'transf.haceDias': 'waiting for {n} days',
  'transf.referenciaRepetida': 'Repeated reference: it also paid sale {ticket}',
  'transf.referenciaEnRecarga': 'Repeated reference: it also paid a top-up',
  'transf.referenciaEnAbono': 'Repeated reference: it also paid a payment on account',
  'transf.pasarAFiado': "Put it on {nombre}'s account",
  'transf.referenciaAlConfirmar': 'Reference shown at the bank',
  'transf.enQueCuenta': 'Which account did it arrive in?',
  'transf.porVerificar': '{monto} to verify',
  'transf.ninguna': 'No transfers pending. Every córdoba taken has been verified.',
  'transf.quePaso': 'What happened?',
  'transf.ejemplo': 'The money never arrived',
  'transf.registrarPerdida': 'Record the loss',
  'transf.noLlego': 'Never arrived',
  'transf.noLlegoTitulo': 'The payment never arrived',
  'transf.confirmar': 'Confirm',
  'transf.referencia': 'ref. {ref}',

  // ---------- Expiry ----------
  'venc.titulo': 'Expiry',
  'venc.subtitulo': '{n} batch(es) · {monto} at risk',
  'venc.lotes': '{n} batch(es)',
  'venc.yaVencidos':
    '{n} batch(es) already expired are still counted as stock. Writing them off takes them out of inventory and records the loss.',
  'venc.ninguno': 'No batch expires in the next {dias} days.',
  'venc.lote': 'Batch {numero} · {cantidad} {unidad}',
  'venc.vence': 'Expires {fecha}',
  'venc.enDias': ' · in {n} day(s)',
  'venc.haceDias': ' · {n} day(s) ago',
  'venc.darDeBaja': 'Write this batch off',
  'venc.registrando': 'Recording…',
  'venc.notaBaja': 'Batch {numero}, expired {fecha}',
  'venc.estado.vencido': 'Expired',
  'venc.estado.critico': 'Expires this week',
  'venc.estado.proximo': 'Expiring soon',
  'venc.estado.ok': 'Good',

  // ---------- Products ----------
  'productos.titulo': 'Products',
  'productos.subtitulo': '{activos} active',
  'productos.subtituloConInactivos': '{activos} active · {inactivos} deactivated',
  'productos.cargaRapida': 'Bulk entry',
  'productos.buscar': 'Search by name or code…',
  'productos.ninguno': 'No product matches',
  'productos.sinProductos': 'No products yet',
  'productos.empezarCatalogo': 'Start with a basic catalogue',
  'productos.cargarAMano': 'Or enter several by hand',
  'productos.medicamento': 'Medicine',
  'productos.perecedero': 'Perishable',
  'productos.escanearTitulo': 'Find a product',
  'productos.verProducto': 'Open the product',
  'productos.crearConCodigo': 'Create a new product',
  'productos.vistaActivos': 'Active',
  'productos.vistaDesactivados': 'Deactivated',
  'productos.sinDesactivados': 'No deactivated products',
  'productos.seleccionar': 'Select',
  'productos.todos': 'All',
  'productos.elegidos': '{n} selected',
  'productos.desactivarVarios': 'Deactivate {n}',
  'productos.reactivarVarios': 'Reactivate {n}',
  'productos.desactivando': 'Deactivating {n} of {total}…',
  'productos.reactivando': 'Reactivating {n} of {total}…',
  'productos.desactivarVariosTitulo': 'Deactivate {n} product(s)?',
  'productos.desactivarVariosMensaje':
    'They stop showing at the till, in prices and in this list, and their codes no longer find them. They are not deleted: whatever was already sold or bought with them keeps their name, and they can be reactivated from “Deactivated”.',
  'productos.catalogo': 'Price catalog',

  // ---------- Price catalog ----------
  'catalogo.titulo': 'Price catalog',
  'catalogo.tituloDe': '{negocio} price catalog',
  'catalogo.explicacion':
    'A PDF of what each product sells for on the lists ticked, by category and with its photo, to send or print. No costs, no stock.',
  'catalogo.listas': 'Price lists',
  'catalogo.cuantos': '{n} product(s) · {paginas} page(s)',
  'catalogo.conFotos': 'With the photo of {n} of them.',
  'catalogo.ofertas': 'Offers',
  'catalogo.ofertaAntes': 'Was {antes} · until {hasta}',
  'catalogo.ofertaHasta': 'Until {hasta}',
  'catalogo.ofertaLleva': '{nombre}: take {lleva} for {monto} · until {hasta}',
  'catalogo.conOfertas': 'On offer, at the start: {n}, with the price before and until when.',
  'catalogo.preparandoFotos': 'Getting the photos ready: {n} of {de}…',
  'catalogo.eligeUna': 'Tick at least one list.',
  'catalogo.ninguno': 'No product has a price on those lists.',
  'catalogo.descargar': 'Download PDF',
  'catalogo.mandar': 'Send PDF',
  'catalogo.error': 'The PDF could not be made. Try again.',
  'catalogo.continua': '{categoria} (continued)',
  'catalogo.pagina': 'Page {n} of {de}',
  'catalogo.archivo': 'price-catalog_{fecha}.pdf',
  'catalogo.precios': 'Prices',
  'catalogo.conPrecios': 'With prices',
  'catalogo.sinPrecios': 'Without prices',
  'catalogo.tituloSinPrecios': 'Product catalog',
  'catalogo.tituloSinPreciosDe': '{negocio} product catalog',
  'catalogo.explicacionSinPrecios':
    'A PDF of every product on sale and its pack sizes, by category and with its photo, to send or print. No prices, no costs, no stock.',
  'catalogo.ningunoSinPrecios': 'There are no products on sale.',
  'catalogo.archivoSinPrecios': 'product-catalog_{fecha}.pdf',

  // ---------- Suppliers ----------
  'prov.titulo': 'Suppliers',
  'prov.activos': '{n} active',
  'prov.contacto': 'Contact',
  'prov.guardar': 'Save supplier',
  'prov.ninguno': 'No suppliers yet. At least one is needed to record a purchase.',
  'prov.compras': '{n} purchase(s)',
  'prov.nuevo': 'New supplier',
  'prov.yaExiste': 'You already have a supplier called “{nombre}”.',
  'prov.elegirEse': 'Pick “{nombre}”',
  // A supplier, opened from the list (2026-10-16).
  'prov.editar': 'Edit',
  'prov.editarTitulo': 'Edit supplier',
  'prov.guardarCambios': 'Save changes',
  'prov.sinNombre': 'A supplier needs a name.',
  'prov.noEsta': 'That supplier is not on this device.',
  'prov.desactivado': 'Deactivated',
  'prov.sinAnotar': 'Not recorded',
  'prov.llamar': 'Call',
  'prov.seLeDebe': 'Owed to them',
  'prov.noSeLeDebe': 'Nothing owed to them.',
  'prov.verPorPagar': 'Paid from “Accounts payable”',
  'prov.comprasTitulo': 'Purchases',
  'prov.comprado': '{n} purchase(s) for {monto}',
  'prov.ultima': 'latest: {fecha}',
  'prov.ningunaCompra': 'Nothing bought from them yet.',
  'prov.debeDeEsta': '{monto} owed',
  'prov.pagos': 'Payments',
  'prov.ningunPago': 'Nothing paid to them yet.',

  // ---------- Price lists ----------
  'listas.titulo': 'Price lists',
  'listas.subtitulo': 'A client is assigned a list; the cashier can change a price with a reason.',
  'listas.preciosDefinidos': '{n} price(s) set',
  'listas.predeterminada': 'Default',
  'listas.hacerPredeterminada': 'Make it the default',
  'listas.nueva': 'New list',
  'listas.ejemplo': 'Special',
  'listas.explicacion':
    'Lists are never deleted, only deactivated: an old sale has to be able to say which list it was charged on.',

  // ---------- Periods ----------
  'periodo.hoy': 'Today',
  'periodo.ayer': 'Yesterday',
  'periodo.ultimos30': 'Last 30 days',
  'periodo.esteMes': 'This month',
  'periodo.mesPasado': 'Last month',
  'periodo.esteAnio': 'This year',
  'periodo.todo': 'All time',

  // ---------- Errors ----------
  'error.pantallaTitulo': 'This screen could not be shown',
  'error.pantallaTexto': 'Whatever was saved is still saved. Go back home and open it again.',
  'error.volverAlInicio': 'Back to home',
  'error.sinRegistrar': 'This device is not registered yet',
  'error.sinSesion': 'Nobody is signed in',
  'error.textoLargo': '«{campo}» is too long: it takes {maximo} characters at most. Shorten it and try again.',
  'error.dispositivoNoRegistrado': 'This device is not registered',
    'error.sinCodigo': 'This device has no code assigned',

  // ---------- Clients ----------
  'clientes.titulo': 'Clients',
  'clientes.activos': '{n} active',
  'clientes.tipo': 'Type',
  'clientes.mostrador': 'Walk-in',
  'clientes.mayorista': 'Wholesale / on a route',
  'clientes.etiquetaMostrador': 'Walk-in',
  'clientes.desactivado': 'Deactivated',
  'clientes.etiquetaMayorista': 'Wholesale',
  'clientes.listaPrecios': 'Price list',
  'clientes.ayudaLista': 'This is the price this client always sees, with nobody having to remember.',
  'clientes.listaDelDueno': 'A client\'s price list is set by the owner.',
  'clientes.laPredeterminada': 'The default one',
  'clientes.yaExiste': 'You already have a client called “{nombre}”.',
  'clientes.elegirEse': 'Pick “{nombre}”',
  'clientes.ayudaDireccion': 'As people say it here: two blocks south of the church.',
  'clientes.diaVisita': 'Visit day',
  'clientes.sinDiaFijo': 'No fixed day',
  'clientes.notas': 'Notes',
  'clientes.desactivar': 'Deactivate',
  'clientes.editarTitulo': 'Edit client',
  'clientes.confirmarDesactivar': 'Deactivate this client? Their history is kept.',
  'clientes.ninguno': 'No clients yet. They are needed to sell on credit and to take route orders.',
  'clientes.pedido': 'Order',
  'dia.0': 'Sunday',
  'dia.1': 'Monday',
  'dia.2': 'Tuesday',
  'dia.3': 'Wednesday',
  'dia.4': 'Thursday',
  'dia.5': 'Friday',
  'dia.6': 'Saturday',

  // ---------- Pre-sales ----------
  'pedidos.titulo': 'Orders',
  'pedidos.pendientes': '{n} order(s) pending',
  'pedidos.preciosDeLista': 'At this client\'s prices: {lista}.',
  'pedidos.seEntregaEl': 'Delivery date',
  'pedidos.agregarProducto': 'Add a product',
  'pedidos.explicacion':
    'An order sets nothing aside and moves nothing yet: it is a promise, kept when it is delivered and charged.',
  'pedidos.explicacionApartado':
    'This order sets aside what it takes: nobody else can be promised it, and the till asks for the owner\'s PIN to sell it. It is saved only with a connection, so that no other phone promises the same goods.',
  'pedidos.lineaNoAlcanza': '{libre} left that is not set aside',
  'pedidos.noAlcanza': '{producto}: {libre} left that is not set aside, and the order asks for {pide}.',
  'pedidos.noAlcanzaQue': 'Lower the quantity or take the product off: what is set aside belongs to other orders.',
  'pedidos.sinConexion':
    'No connection: an order that sets stock aside is saved only once the server confirms it. Connect and try again.',
  'pedidos.confirmando': 'Confirming with the server…',
  'pedidos.noSeConfirmo': 'The server did not answer in time and the order was not saved. Check the connection and try again.',
  'pedidos.motivoSinConfirmar': 'The server did not confirm it in time',
  'pedidos.noQuedo': 'The server did not take it and the order was not saved. Check what is left unreserved and try again.',
  'pedidos.guardar': 'Save the order',
  'pedidos.guardarCambios': 'Save changes',
  'pedidos.editar': 'Edit',
  'pedidos.editarTitulo': 'Edit order',
  'pedidos.cambiadoPara': 'Order for {cliente} changed',
  'pedidos.cambioNoSeConfirmo': 'The server did not answer in time and the change was not saved: the order stays as it was. Check the connection and try again.',
  'pedidos.cambioNoQuedo': 'The server did not take it and the change was not saved. Check what is left unreserved and try again.',
  'pedidos.cambiarEntregado': 'This order has been delivered: what was charged is put right on the sale.',
  'pedidos.cambiarCancelado': 'This order was cancelled.',
  'pedidos.cambiarNoEsta': 'That order is not on this device.',
  'pedidos.cambiarSinProducto': 'A line of this order is of a product or a presentation that is gone. Cancel it and take another.',
  'pedidos.ninguno': 'No orders pending.',
  'pedidos.clienteDesconocido': 'Unknown client',
  'pedidos.productoNoEncontrado': 'Product not found',
  'pedidos.cancelar': 'Cancel this order',
  'pedidos.porQueCancela': 'Why is the order being cancelled?',
  'pedidos.noSeCancelo': 'The order was not cancelled: {motivo}',
  'pedidos.nuevo': 'New order',
  'pedidos.nuevoCliente': 'Add new client',
  'pedidos.clienteNuevoAyuda': 'It is saved with your clients and picked for this order.',
  'pedidos.busqueEmpezar': 'Search for or scan what the client ordered',
  'pedidos.enElPedido': '{n} in this order',
  'pedidos.faltaPrecio':
    '{producto} has no price on this order\'s list. Type it here, or set it in Products so it sticks.',
  'pedidos.faltaPrecioSoloAqui':
    '{producto} has no price on this order\'s list. Type it here for this order; the owner sets its price in Products.',
  'pedidos.hayLineasSinPrecio': '{n} product(s) have no price. Tap each one and type it: the order is not saved at zero.',
  'pedidos.faltaPrecioPida':
    '{producto} has no price on this order\'s list. Ask the owner to set one in Products, or take it off the order.',
  'pedidos.hayLineasSinPrecioPida':
    '{n} product(s) have no price: the owner sets them in Products, or take them off the order. The order is not saved at zero.',
  'pedidos.faltaCliente': 'Choose whose order this is.',
  'pedidos.quitarLinea': 'Remove {producto} from the order',
  'pedidos.confirmarQuitar': '{producto} ({total}) comes off this order.',
  'pedidos.vistaPendientes': 'Pending',
  'pedidos.vistaEntregados': 'Delivered',
  'pedidos.vistaCancelados': 'Cancelled',
  'pedidos.atrasado': 'Late',
  'pedidos.hoy': 'Today',
  'pedidos.cobrar': 'Charge',
  'pedidos.recibo': 'Receipt',
  'pedidos.reciboTitulo': 'Order receipt',
  'pedidos.reciboSinCobrar': 'Not charged yet. Once it is, the till gives the receipt for the sale.',
  'pedidos.ningunoEntregado': 'No order has been delivered yet.',
  'pedidos.ningunoCancelado': 'No cancelled orders.',
  'pedidos.entregadoEn': 'Ticket {ticket} · {fecha}',
  'pedidos.entregadoSinVenta': 'Delivered. The sale has not reached this phone yet.',
  'pedidos.cobroQuien': 'Charged by {quien} · {como}',
  'pedidos.canceladoPor': 'Cancelled: {motivo}',
  'pedidos.noSeCarga': 'That order cannot be charged',
  'pedidos.noEsta': 'That order is not on this phone.',
  'pedidos.yaEntregado': '{cliente}\'s order was already delivered: it is ticket {ticket}.',
  'pedidos.fueCancelado': '{cliente}\'s order was cancelled: {motivo}.',
  'pedidos.verLista': 'See the orders',
  'pedidos.tomadoPara': 'Order for {cliente} saved',
  'pedidos.apartado': 'Set aside',
  'pedidos.corto': 'Short of stock',
  'pedidos.faltaDe': '{cantidad} of {producto} missing',
  'pedidos.detalleTitulo': 'Delivered order',
  'pedidos.elPedido': 'The order',
  'pedidos.tomado': 'Taken',
  'pedidos.cambiado': 'Changed',
  'pedidos.paraEl': 'Due',
  'pedidos.lista': 'Price list',
  'pedidos.autorizo': 'allowed by {nombre}',
  'pedidos.totalDelPedido': 'Order total',
  'pedidos.laEntrega': 'The delivery',
  'pedidos.ticket': 'Ticket',
  'pedidos.cobrado': 'Charged',
  'pedidos.donde': 'Where',
  'pedidos.atraso': '{n} day(s) after the day it was due',
  'pedidos.comoSePidio': 'Charged as it was ordered.',
  'pedidos.distintoDeLoPedido': 'Charged differently from the order:',
  'pedidos.totalCobrado': 'Total charged',
  'pedidos.noLlego': 'never arrived',
  'pedidos.devoluciones': 'Returns',
  'pedidos.reembolsoEn': 'refunded in {como}',
  'pedidos.reembolsoACuenta': 'taken off what they owe',
  'pedidos.verVenta': 'Open the sale',

  // ---------- Settling a route from before ----------
  // «Reparto» was taken out (the owner, 2026-10-04); what is left is settling a route already out.
  'reparto.camion': 'Truck',
  'reparto.desdeEl': 'since {fecha}',
  'reparto.deAntes':
    'This till belongs to a route that went out before delivery runs were taken out. It closes by settling the route: the cash is counted, and what is left on the truck, which goes back to the shop.',
  'reparto.loLiquidaElDueno':
    'The owner settles it, here on this phone, counting the cash and what is left on the truck with you. Hand it over so they can sign in with their PIN.',
  'reparto.pasarAlDueno': 'Hand it to the owner',
  'reparto.vaEnElCamion': 'Left on the truck',
  'reparto.camionVacio': 'Nothing was left on the truck: only the cash to count.',
  'reparto.explicacionLiquidacion': 'Count each one. Whatever is missing is booked as a delivery shortfall.',
  'reparto.contadoEn': 'What you count on the truck',
  'reparto.sistemaTiene': 'The system has {cantidad}',
  'reparto.fiadoEnRuta': 'Sold on credit on the route',
  'reparto.fiadoEnRutaAyuda': "Each client owes this now. Ask about anything you don't recognise.",
  'reparto.abonosEnRuta': 'Client payments in cash on the route',
  'reparto.pagadoEnRutaTotal': 'Paid on the route: {monto}',
  'reparto.efectivoContado': 'Cash counted (C$)',
  'reparto.incluyaFondo': 'Include the float it left with.',
  'reparto.deberiaHaber': 'There should be {monto}',
  'reparto.dolaresContados': 'Dollars counted (US$)',
  'reparto.conteoIlegible': 'The count of {producto} does not read: type just the number, or leave it empty if none came back.',
  'reparto.dolaresIlegibles': 'The dollars do not read: type just the number, or leave it empty if there are none.',
  'reparto.cuenteEfectivo': 'Count the cash before settling up.',
  'reparto.noSePudoLiquidar': 'The settlement could not be recorded',
  'reparto.liquidando': 'Settling…',
  'reparto.explicacionCierre':
    'They close together on purpose: a settlement that balances the money and leaves the goods uncounted is not a settlement.',
  'reparto.liquidada': 'Route settled',
  'reparto.liquidadaExplicacion':
    "The route's till is closed and its cash has gone to general cash. No more routes go out: to sell, start a till as on any day.",
  'reparto.devuelto': 'Came back off the truck',
  'reparto.faltanteMercaderia': 'Missing from the truck',

  // ---------- Expenses ----------
  'gastos.titulo': 'Expenses and payments',
  'gastos.registrados': '{n} expense(s) recorded',
  'gastos.nuevo': 'New expense',
  'fijos.titulo': 'Fixed expenses',
  'fijos.ayuda': 'What is paid every month or every few, like rent or internet. When it falls due, it shows under “Due”.',
  'fijos.ninguno': 'No fixed expenses yet.',
  'fijos.nuevo': 'New fixed expense',
  'fijos.editarTitulo': 'Fixed expense',
  'fijos.tocaPagar': 'Due',
  'fijos.tocaPagarAyuda': 'The fixed expenses that fell due or fall due this week.',
  'fijos.vencio': 'Was due {fecha}',
  'fijos.venceHoy': 'Due today',
  'fijos.vence': 'Due {fecha}',
  'fijos.registrar': 'Record',
  'fijos.esteMesNo': 'Not this month',
  'fijos.omitirTitulo': 'Skip “{descripcion}” for {mes}?',
  'fijos.omitirMensaje': 'It is not asked for again for that month. The other months stay as they are.',
  'fijos.varia': 'Varies',
  'fijos.cadaMes': 'Every month, on day {dia}',
  'fijos.cadaMeses': 'Every {n} months, on day {dia}',
  'fijos.cadaAnio': 'Every year, on {mes} {dia}',
  'fijos.enPausa': 'Paused',
  'fijos.seRepite': 'Repeats',
  'fijos.seRepiteAyuda': 'It becomes a fixed expense: each time it falls due, on day {dia}, it shows under “Debts” ready to record.',
  'fijos.cada': 'How often',
  'fijos.cada1': 'Every month',
  'fijos.cada2': 'Every 2 months',
  'fijos.cada3': 'Every 3 months',
  'fijos.cada6': 'Every 6 months',
  'fijos.cada12': 'Every year',
  'fijos.montoVaria': 'The amount changes each time',
  'fijos.montoVariaAyuda': 'Like electricity or water: typed when it is recorded.',
  'fijos.monto': 'Amount',
  'fijos.montoAyuda': 'Empty if it changes each time, like electricity or water.',
  'fijos.montoInvalido': 'An amount above zero, or empty if it changes each time.',
  'fijos.dia': 'Day of the month',
  'fijos.diaAyuda': 'In shorter months, the last day.',
  'fijos.formaPago': 'How it is usually paid',
  'fijos.activo': 'Active',
  'fijos.activoAyuda': 'Off, it is not asked for until you turn it back on.',
  'fijos.guardar': 'Save fixed expense',
  'fijos.quitar': 'Remove this fixed expense',
  'fijos.quitarTitulo': 'Remove “{descripcion}”?',
  'fijos.quitarMensaje': 'It is no longer asked for. What was already recorded stays among the expenses.',
  'fijos.quitarConfirmar': 'Remove',
  'fijos.descripcionMes': '{descripcion} · {mes}',
  'fijos.cambiaDesde': 'Changing the day moves the day of every month still due; changing how often starts counting from the next time it falls due.',
  'fijos.antesLoVencido': 'Before changing how often it is paid, record or skip what is already due: {meses}.',
  'fijos.hayVencidos': 'A fixed expense is overdue',
  'fijos.hayHoy': 'A fixed expense is due today',
  'fijos.yaRegistrado': 'This month is already recorded, perhaps from another phone. It was not recorded twice.',
  'inicio.tocaPagar': 'Due: {lista}',
  'inicio.tocaPagarMas': '{lista} and {n} more',
  'inicio.tocaPagarMeses': '{descripcion} ({n} months)',
  'gastos.vistas': 'Parts of expenses and payments',
  'gastos.vistaGastos': 'Expenses',
  'gastos.vistaPorPagar': 'Debts',
  'gastos.vistaPagos': 'Payments',
  'gastos.vistaDueno': 'Owner',
  'gastos.hayVencidas': 'A bill is past due',
  'gastos.categoria': 'Category',
  'gastos.deQueFue': 'What was it for?',
  'gastos.ayudaDescripcion':
    'Write it the way you would say it to someone: "September electricity bill".',
  'gastos.montoCordobas': 'Amount (C$)',
  'gastos.montoDolares': 'Amount (US$)',
  'gastos.moneda': 'Currency',
  'gastos.aTasa': 'At C${tasa}',
  'gastos.cordobas': 'Córdobas',
  'gastos.dolares': 'Dollars',
  'gastos.comoSePago': 'How was it paid?',
  'gastos.ayudaPago': 'Cash comes out of the safe, not out of the shift drawer.',
  // From the drawer, and in parts (2026-10-16).
  'gastos.ayudaPagoSinCaja': 'No till is open: cash comes out of the safe. Money taken from a drawer is recorded while its till is open.',
  'gastos.ayudaPagoOtroDia': 'On another day, cash comes out of the safe: an open drawer pays only what is paid today.',
  'gastos.nadaQueda': 'The parts pay for the whole expense.',
  'gastos.partesDeMas': 'The parts add up to {monto} more than the expense.',
  'gastos.enPartes': ' · in parts',
  'gastos.formaEnPartes': 'In parts',
  'gastos.conElGasto': '{fecha} · with the expense',
  'gastos.proveedorOpcional': 'Supplier (optional)',
  'gastos.nadieEnParticular': 'Nobody in particular',
  'gastos.fotoRecibo': 'Photo of the receipt',
  'gastos.ayudaFotoRecibo': 'Optional. It stays with the expense, so later you can see what the money went on.',
  'gastos.tomarFotoRecibo': 'Take a photo of the receipt',
  'gastos.cambiarFotoRecibo': 'Change the photo',
  'gastos.recibo': 'Receipt',
  'gastos.parecido': 'There is already an expense just like it that day: {monto} for «{descripcion}». Is it the same one?',
  'gastos.guardar': 'Save the expense',
  'gastos.movimiento': 'Movement',
  'gastos.duenoSaca': 'The owner takes money out',
  'gastos.duenoMete': 'The owner puts money in',
  'gastos.deDondeSale': 'Where does it come from?',
  'gastos.aDondeEntra': 'Where does it go?',
  'gastos.explicacionDueno':
    "This is not a business expense and does not reduce profit: it reduces equity, which is exactly what happens in reality.",
  'gastos.dineroDelDueno': "Owner's money",
  'gastos.cuentasPorPagar': 'Accounts payable',
  'gastos.proveedor': 'Supplier',
  'gastos.vence': 'Due {fecha}',
  'gastos.sinFecha': 'No due date',
  'gastos.abonado': ' · {monto} paid',
  'gastos.deAntes': ' · from before',
  'gastos.nadaPorPagar': 'Nothing is owed to any supplier.',
  'gastos.deudaAnterior': 'Owed to a supplier from before',
  'deudaAnterior.titulo': 'Owed from before',
  'deudaAnterior.corregir': 'Correct the debt',
  'deudaAnterior.explicacion':
    'An invoice the shop already owed before the app. It stays in accounts payable and is paid off like a purchase on credit, with no goods coming in: those are already in the opening stock.',
  'deudaAnterior.sinProveedores': 'First add the supplier in',
  'deudaAnterior.cuanto': 'How much is still owed (C$)',
  'deudaAnterior.ayudaCuanto': 'What is left to pay on that invoice, in córdobas.',
  'deudaAnterior.bajoLoPagado': '{monto} has already been paid on it: it cannot be less.',
  'deudaAnterior.factura': 'Invoice number (optional)',
  'deudaAnterior.fechaFactura': 'Invoice date',
  'deudaAnterior.vence': 'Due (optional)',
  'gastos.ninguno': 'No expenses recorded yet.',
  'gastos.quedaDebiendo': ' · still owed',
  'gastos.yaPagado': ' · paid',
  'gastos.porQueAnula': 'Why is this expense being voided?',
  'gastos.retiro': 'Drawing',
  'gastos.aporte': 'Contribution',
  'gastos.efectivoUsd': 'Cash US$',
  'gastos.billeteraMovil': 'Mobile wallet',
  'gastos.credito': 'Credit',
  'gastos.listaTitulo': 'What was spent',
  'gastos.buscar': 'Search: what for, supplier, number…',
  'gastos.todasCategorias': 'All',
  'gastos.orden': 'Order',
  'gastos.ordenFecha': 'Newest',
  'gastos.ordenMonto': 'Largest',
  'gastos.verAnulados': 'Show voided ones too',
  'gastos.suma': '{total} in {n} expense(s)',
  'gastos.ningunoAsi': 'No expenses match these filters.',
  'gastos.anulado': 'Voided',
  'gastos.corregido': 'Corrected',
  'gastos.formaPago': 'How it was paid',
  'gastos.deLaGaveta': ' · from the drawer',
  'gastos.enDolares': 'US$ {monto} at C$ {tasa}',
  'gastos.registradoPor': 'Recorded by',
  'gastos.pagos': 'Payments',
  'gastos.pagado': 'Paid',
  'gastos.falta': 'Still owed',
  'gastos.anuladoPor': 'Voided by {nombre}, {cuando}.',
  'gastos.corregidoPor': 'Corrected by {nombre}, {cuando}.',
  'gastos.motivo': 'Reason: {motivo}',
  'gastos.verCorreccion': 'See what it became',
  'gastos.corrigeA': 'Corrects an expense recorded earlier.',
  'gastos.verAnterior': 'See the earlier one',
  'gastos.conPagos': 'It has payments: it can no longer be corrected or voided.',
  'gastos.corregirTitulo': 'Correct expense',
  'gastos.corregirExplicacion':
    'The expense is not erased: it stays voided, with what was wrong, and this one takes its place. It needs the owner\'s PIN.',
  'gastos.queEstabaMal': 'What was wrong?',
  'gastos.guardarCorreccion': 'Save correction',
  'gastos.correccionDeGaveta': 'It came out of the open drawer: so does the correction, and the drawer expects only the difference.',
  'gastos.correccionDeCajaContada': 'It came out of a till that has since been counted: the correction goes through general cash.',
  'gastos.pinCorregir': 'Correct the expense “{descripcion}”.',
  'gastos.pinAnular': 'Void the expense “{descripcion}”.',
  'gastos.noSeCorrigio': 'It could not be corrected: {motivo}',
  'gastos.noSeAnulo': 'It could not be voided: {motivo}',
  'gastos.correccionNoAlcanza': 'The drawer should hold {monto}, counting what comes back from the earlier expense: not enough.',
  'gastos.correccionContada': '{motivo}. What the expense was for can be corrected; not the amount, how it was paid or the date.',
  'gastos.correccionMueveDinero': 'That would move money the count already found: leave the amount, how it was paid and the date as they were.',
  'gastos.anuladoSinCorreccion': 'The expense was voided, but the corrected one was not recorded: {motivo}. Record it with “New expense”.',

  // ---------- Accounting ----------
  'conta.titulo': 'Accounting',
  'conta.resultados': 'Income',
  'conta.balance': 'Balance sheet',
  'conta.caja': 'Cash book',
  'conta.utilidad': 'Profit',
  'conta.utilidadBruta': 'Gross profit',
  'conta.gastos': 'Expenses',
  'conta.utilidadNeta': 'Net profit',
  'conta.ingresos': 'Income',
  'conta.costoVentas': 'Cost of sales',
  'conta.explicacionRetiros':
    "Money the owner takes out does not appear here: it is not an expense, it is a reduction of equity. Counting it as an expense is the commonest way a profit figure goes wrong.",
  'conta.activo': 'Assets',
  'conta.pasivo': 'Liabilities',
  'conta.patrimonio': 'Equity',
  // All of it to the end of the period, not the period's alone: «Profit for the period» said the opposite.
  'conta.utilidadEjercicio': 'Profit to date',
  'conta.balanceCuadra': 'The balance sheet balances: assets = liabilities + equity.',
  'conta.descuadre': 'Out by {monto}. This should never happen: report it.',
  'conta.fiadoSubio': 'What clients owe went up by {monto} in the period: that much of the profit is not money in hand yet.',
  'conta.fiadoBajo': 'What clients owe went down by {monto} in the period.',
  'conta.fiadoTotal': 'At the end of the period they owe you {monto} on fiado.',
  'conta.cuadres': 'Each account against its detail',
  'conta.cuadresAyuda': 'What the books say against what the clients, the purchases, the shelves… add up to. As of today, and it all ties out.',
  'conta.cuadresNo': "{n} account(s) don't tie out with their detail. This should never happen: tell someone.",
  'conta.cuadra': 'Ties out',
  'conta.redondeo': '{monto} of rounding',
  'conta.noCuadra': "Doesn't tie out: the detail adds up to {monto}",
  'conta.detalle.gaveta': 'What the open tills expect, and what each till count left',
  'conta.detalle.clientes': 'What clients owe',
  'conta.detalle.proveedores': 'What is owed on purchases and expenses',
  'conta.detalle.inventario': 'The goods, at average cost',
  'conta.detalle.camion': 'What is on the trucks',
  'conta.detalle.transformacion': 'What is being repacked',
  'conta.detalle.porRecibir': 'Cash handed over by tills, not yet received',
  'conta.detalle.dolares': 'The dollars in the open tills',
  'conta.detalle.transferencias': 'Payments waiting in the queue',
  'conta.detalle.saldoTelefonico': 'Airtime on hand, at cost',
  'conta.detalle.adelantos': 'Advances not yet taken off a payroll',
  'conta.detalle.aguinaldo': "Each employee's accrued aguinaldo",
  'conta.detalle.vacaciones': "Each employee's accrued vacation",
  'conta.detalle.sueldos': 'Payrolls not yet paid',
  'conta.detalle.inss': 'INSS on payrolls, not yet paid',
  'conta.detalle.inatec': 'INATEC on payrolls, not yet paid',
  'conta.detalle.ir': 'IR withheld on payrolls, not yet paid',
  'conta.resumenCaja': 'Opening {inicial} · in {entradas} · out {salidas}',
  'conta.sinMovimientos': 'No movements in this period.',
  'conta.concepto': 'Description',
  'conta.entra': 'In',
  'conta.sale': 'Out',
  'conta.saldo': 'Balance',
  'conta.porProducto': 'By product',
  'conta.porCliente': 'By client',
  'utilidad.sinCatalogo': 'Quick sales (not in the catalogue)',
  'utilidad.mostrador': 'Counter (no client)',
  'utilidad.clienteEliminado': 'Deleted client',
  'conta.sinVentasPeriodo': 'No sales in this period.',
  'conta.cantidadCorta': 'Qty',
  'conta.venta': 'Sales',
  'conta.totalDe': 'Total {que}',
  'conta.sinMovimiento': 'No movement',
  'conta.moneda': 'Show the figures in',
  'conta.enDolares': 'In dollars at the official rate, C$ {tasa} per dollar. Rounding can leave a total a cent off the sum of its lines.',
  // What each line means and how it is worked out, in a bubble on it (2026-10-17). Each
  // account's own, under `cuentaAyuda.*`.
  'conta.ayuda.cuenta': '{nombre} · account {codigo}',
  'conta.ayuda.ingresos':
    'What the shop sold in the period, and the rest that comes in (delivery charges, top-up commissions, overages), before taking off what the goods cost and the expenses. A sale on credit counts the day it is sold, not the day it is paid. The sum of the lines under «Income».',
  'conta.ayuda.costos':
    "What the goods sold cost, each product at the average cost it had when it sold, and what else comes straight off what was sold: what customers returned and corrections to the goods' cost. The sum of these lines.",
  'conta.ayuda.utilidadBruta':
    "Income less cost of sales: what the sales leave before the shop's expenses.",
  'conta.ayuda.margen':
    'The {margen} is the gross margin: gross profit divided by income. Of every 100 that comes in, what is left after paying for the goods.',
  'conta.ayuda.gastos':
    'What keeping the shop open cost in the period: the payroll and what is paid on it, rent, electricity, water, transport, packaging, and what was lost to damage, expiry and shortfalls. Each expense counts the day it was made, paid or still owed. The sum of the lines under «Expenses».',
  'conta.ayuda.utilidadNeta':
    "Gross profit less expenses: what the shop made in the period, or lost if it is negative. It is not the money that came in: a sale on credit counts the day it is sold though it is paid later, and what the owner takes out is not an expense.",
  'conta.ayuda.total': 'The sum of the lines under «{grupo}».',
  'conta.ayuda.activo':
    "What the shop has at the end of the period: the money (drawers, safe, banks and wallet), the goods at what they cost, and what clients and employees owe it. Each line is its account's balance: everything that came into it since the first day less everything that went out.",
  'conta.ayuda.pasivo':
    'What the shop owes at the end of the period: to suppliers, for what was bought or spent on credit, and from the payroll what is owed to the employees and what is due to INSS, INATEC and the DGI. Each line is what came to be owed less what has been paid.',
  'conta.ayuda.patrimonio':
    "The part of the shop that is the owner's: what they put in and what the shop already had when it started on the app (capital), less what they took out for themselves (drawings), plus what the shop has made since the first day (profit to date). It is what is left of the assets once the liabilities are taken off.",
  'conta.ayuda.utilidadEjercicio':
    "The net profit of all time to the end of the period, not just this period's: all income less all costs and expenses since the first day. It is not moved into «Retained earnings»: the books work it out from the entries every time.",
  'conta.ayuda.totalPatrimonio':
    'The sum of the lines under «Equity», profit to date included. It has to equal assets less liabilities.',
  'conta.ayuda.caja':
    "All the shop's money in one book: the drawers (córdobas and dollars), general cash, what a till handed over and is still to be received, the banks and the wallet, the dollars at the official rate of the day they came in. Transfers waiting in the queue do not count yet. Opening: what all of it held when the period began. In and out: what came in and what went out in the period; moving money from one account to another, the drawer to the safe or to the bank, is out of one and into the other. The «Balance» column keeps it movement by movement.",
  // The cash book's sum, a line a step, under its bubble's words.
  'conta.ayuda.saldoInicial': 'Opening',
  'conta.ayuda.entradas': 'In',
  'conta.ayuda.salidas': 'Out',
  'conta.ayuda.saldoFinal': 'Closing',
  'conta.ayuda.porProducto':
    'Each product sold in the period, from the one that made the most to the least. Sale: what was charged for it less what came back, without cash rounding or delivery. Cost: what what was sold cost, at the average cost the product had when it sold. Profit: sale less cost. Margin: profit divided by sale. The quantity is in the product\'s own unit. What was sold outside the catalogue goes together under «Quick sales», with no cost. Above, the profit and margin of it all.',
  'conta.ayuda.porCliente':
    'Each client who bought in the period, from the one that made the most to the least, with sale, cost, profit and margin counted as by product. What was sold with no client goes together under «Counter (no client)». The two tables add up to the same. Above, the profit and margin of it all.',
  'conta.ayuda.cuadre':
    "What the books say against what this account's detail adds up to. They are written together, in the same step, so they have to agree: if not, the app has a fault and it should be reported.",
  'conta.ayuda.cuadreInventario':
    'What the books say against the goods, counted at their average cost. They are written together, in the same step, so they have to agree but for a few cents: each movement is rounded to the cent and the average is kept finer. More than that is a fault of the app, and it should be reported.',
  'conta.ayuda.losLibros': 'The books',
  // What each account of the chart means and what moves it, in the bubble on its line: one per
  // account, or `explicacionCuenta` (i18n/dominio.ts) does not compile.
  'cuentaAyuda.CAJA_CORDOBAS':
    "The drawers' córdobas: what the open tills expect and what each count left for change. Up with what is charged in cash and what is put into the drawer; down with change given, what is paid out of the drawer, takings removed and what the count takes out.",
  'cuentaAyuda.CAJA_DOLARES':
    "The open drawers' dollars, in córdobas at the official rate. Up with what is charged in dollars and what is put in at opening; the count takes them all out, so between shifts it is at zero. Change is always given in córdobas.",
  'cuentaAyuda.BANCOS':
    'The bank account in córdobas. Up with card payments, confirmed transfers, deposits and what comes in from another account; down with what is paid by transfer, cash taken out and what goes to another account. A transfer taken at the till waits in «Unconfirmed transfers» until it is confirmed.',
  'cuentaAyuda.BANCOS_2': 'Another bank account in córdobas, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCOS_3': 'Another bank account in córdobas, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCOS_4': 'Another bank account in córdobas, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCOS_5': 'Another bank account in córdobas, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCO_DOLARES':
    'The bank account in dollars, carried in córdobas at the official rate of each movement and never revalued; its statement is compared in dollars. Up with dollars deposited and what comes in from another account; down with what is taken out and what is paid from it. Customers do not pay into it.',
  'cuentaAyuda.BANCO_DOLARES_2': 'Another bank account in dollars, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCO_DOLARES_3': 'Another bank account in dollars, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCO_DOLARES_4': 'Another bank account in dollars, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.BANCO_DOLARES_5': 'Another bank account in dollars, opened and named by the owner: it moves like the first one, with its own statement.',
  'cuentaAyuda.CAJA_FUERTE':
    "The cash outside the drawers, in the safe. Up when the owner receives a count's or a removal's money, with cash taken from the bank and what the owner puts in; down with what is paid in cash outside the drawer, the float to open a till, deposits and the owner's drawings. Its dollars at the official rate of the day they came in.",
  'cuentaAyuda.EFECTIVO_POR_RECIBIR':
    'What a till handed over that nobody has counted yet: the money of a count or a removal with the owner not there, like an envelope in the safe. Down when the owner receives and counts it; whatever is short or over then is that till\'s.',
  'cuentaAyuda.BILLETERA_MOVIL':
    'The mobile wallet. Up with confirmed customer payments by wallet and what the owner puts in; down with what is paid by wallet: purchases, expenses, payroll and top-up credit.',
  'cuentaAyuda.INVENTARIO':
    'The goods on the shop floor and in the storeroom, at what they cost. Up with purchases, with their share of freight and of the invoice\'s VAT, with what comes back on a return and what turns up at a count; down with what is sold, at its average cost, and with what is written off or missing at a count.',
  'cuentaAyuda.INVENTARIO_CAMION':
    'The goods on the delivery trucks, at what they cost: up when loaded, down with what is sold on the route and when the route is settled.',
  'cuentaAyuda.INVENTARIO_TRANSFORMACION':
    'The goods moved to the «Being repacked» location, at what they cost: up and down with transfers to and from it. Repacking itself works on the shop\'s goods and does not pass through here.',
  'cuentaAyuda.CUENTAS_POR_COBRAR':
    "Fiado: what clients owe, the sum of their balances. Up with each sale's part on credit and with earlier balances; down with payments, what is returned to the client's account and what is written off. A client who paid too much counts as negative.",
  'cuentaAyuda.ADELANTOS_EMPLEADOS':
    'Salary advanced to employees and not yet deducted. Up with each advance; down when a payroll run or a settlement deducts it, never more than the net pay: what does not fit waits for the next one.',
  'cuentaAyuda.TRANSFERENCIAS_PENDIENTES':
    'Payments by transfer or wallet that were taken and wait in the queue for the owner to see them at the bank. Confirmed, they move to the bank or the wallet; if they never arrived, to «Transfers never received» or to the client\'s account. They do not count as money in the «Cash book» yet.',
  'cuentaAyuda.SALDO_TELEFONICO':
    'Credit bought to sell as top-ups, at what it cost. Up with each purchase of credit; each top-up sold takes it down at its average cost, and what was charged above that is the commission.',
  'cuentaAyuda.CUENTAS_POR_PAGAR':
    'What is owed to suppliers: purchases and expenses on credit, and debts from before the shop used the app. Up with each one; down with what is paid to them and with returns taken off the debt.',
  'cuentaAyuda.AGUINALDO_POR_PAGAR':
    'The aguinaldo building up: each payroll run sets aside a twelfth of the pay of whoever has benefits. Down when aguinaldo is paid or a settlement pays what built up.',
  'cuentaAyuda.VACACIONES_POR_PAGAR':
    'Holiday pay building up: each payroll run sets aside a twelfth of the pay of whoever has benefits. Down when holidays are paid or a settlement pays what built up.',
  'cuentaAyuda.INSS_POR_PAGAR':
    "The payroll's INSS still to be paid: what was withheld from employees and what the shop puts in, on the pay that counts for INSS, of whoever is on INSS. Down when the INSS bill is paid.",
  'cuentaAyuda.INATEC_POR_PAGAR':
    "The payroll's INATEC still to be paid: the rate in the settings, 2% unless changed, of the pay that counts for INSS of whoever is on INSS. Down when it is paid.",
  'cuentaAyuda.IR_RETENIDO_POR_PAGAR':
    'Income tax withheld from employees and still to be handed to the DGI: the progressive table on the pay that counts less their INSS, worked out over the year and spread over each run. Down when it is paid.',
  'cuentaAyuda.SUELDOS_POR_PAGAR':
    'The net pay of payroll runs not yet handed over: up when a run or a settlement is made and down when it is paid, advances already deducted.',
  'cuentaAyuda.IVA_POR_PAGAR':
    "VAT that would be charged to customers. The shop is not registered with the DGI, so it is not used: the VAT on purchase invoices goes into the goods' cost.",
  'cuentaAyuda.CAPITAL':
    "What the owner put into the shop and what the shop already had when it started on the app: the goods and the clients' earlier balances and the first count of general cash, less the debts to suppliers from before.",
  'cuentaAyuda.RETIROS_DUENO':
    "The money the owner took out for themselves. It comes off equity, hence negative, and is not an expense of the shop. Only «Owner's money» moves it: taking money out of a drawer is something else.",
  'cuentaAyuda.RESULTADOS_ACUMULADOS':
    'Earlier profits, if they were set apart. The books do not use it: what was made since the first day is worked out each time and shows as «Profit to date».',
  'cuentaAyuda.VENTAS':
    'What was sold: on each line, the quantity times the price actually charged, without delivery or cash rounding. It counts the day of the sale, in cash, by bank or on credit. It only goes down if the sale is voided: returns go to «Customer returns».',
  'cuentaAyuda.COMISIONES': 'What top-ups leave: what was charged for each one less what that credit cost.',
  'cuentaAyuda.ENVIOS':
    "What clients pay to have their order brought to them, kept apart from sales so the goods' margin stays their own. It only goes down if the sale is voided.",
  'cuentaAyuda.GANANCIA_CAMBIARIA':
    "What is made on dollars: a dollar payment worth more at the official rate than the client was credited at the shop's rate, a count's dollars worth more at the day's rate, or a transfer between accounts in different currencies. With the two rates the same it does not appear.",
  'cuentaAyuda.SOBRANTE_CAJA': 'What was over when a till was counted: at opening, at the count, when it passed to another shift or when the owner received what it handed over.',
  'cuentaAyuda.SOBRANTE_INVENTARIO': 'Goods that turned up over at a count, at their average cost, and top-up credit that turned up over.',
  'cuentaAyuda.SOBRANTE_GENERAL': 'What was over when general cash was counted against the books. The first count does not go here: it goes to capital.',
  'cuentaAyuda.COSTO_VENTAS':
    'What the goods sold cost: on each sale, the quantity times the average cost the product had at that moment. Down with returns and voids, at the cost they left with, and adjusted if the cost or quantity of a purchase already sold is corrected.',
  'cuentaAyuda.DESCUENTOS':
    'Discounts on the total of a ticket. The till does not give them: a price cut is made by lowering the line\'s price, and then «Sales» goes down.',
  'cuentaAyuda.DEVOLUCIONES_VENTAS':
    'What customers returned, at what they had been charged: apart from sales, to see whether it grows. Delivery and rounding are not given back.',
  'cuentaAyuda.AJUSTE_COSTO_INVENTARIO':
    "What the goods' value changed by when the owner corrected a product's cost: the quantity on hand times the difference. Lowering the cost raises it; raising it lowers it, possibly below zero.",
  'cuentaAyuda.SUELDOS': 'The pay of payroll runs and settlements, with other income that counts for INSS, and expenses recorded as wages.',
  'cuentaAyuda.INSS_PATRONAL':
    "The shop's share of INSS: the rate in the settings, 21.5% unless changed, of the pay that counts for INSS of whoever is on INSS.",
  'cuentaAyuda.INATEC_GASTO': "Each run's INATEC: the rate in the settings, 2% unless changed, of the pay that counts for INSS of whoever is on INSS.",
  'cuentaAyuda.AGUINALDO_GASTO': 'What the aguinaldo is costing: the twelfth each run sets aside, and what was paid above what had built up.',
  'cuentaAyuda.VACACIONES_GASTO': 'What holiday pay is costing: the twelfth each run sets aside, and what was paid above what had built up.',
  'cuentaAyuda.INDEMNIZACION_GASTO':
    "Severance for seniority when a contract ends (Art. 45): 30 days' pay a year for the first three years and 20 after, up to 150 days. Only on resignation, dismissal without cause or mutual agreement, and for whoever has benefits.",
  'cuentaAyuda.MERMA':
    'Damaged or lost goods, at their cost: what is written off as damaged, what is lost repacking and what comes back unsellable.',
  'cuentaAyuda.VENCIDOS': 'Goods that expired and were written off, at their average cost.',
  'cuentaAyuda.CONSUMO_INTERNO': 'Goods taken for the house, at their average cost.',
  'cuentaAyuda.ALQUILER': 'Rent: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.ENERGIA': 'Electricity: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.AGUA': 'Water: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.TRANSPORTE': 'Transport and fuel: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.TELEFONO_INTERNET': 'Phone and internet: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.MANTENIMIENTO':
    'Maintenance and repairs: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.IMPUESTOS_MUNICIPALES':
    "The town hall's licence and rubbish fees: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it.",
  'cuentaAyuda.EMPAQUES':
    'Packaging and bags: the expenses recorded under it, on the day of the expense, paid or on credit, and shortages explained as it. Bags for repacking do not go here: they go into the cost of what is packed.',
  'cuentaAyuda.OTROS_GASTOS':
    'Expenses of no other category: those recorded as «Other expenses», on the day of the expense, paid or on credit, and shortages explained as it.',
  'cuentaAyuda.FALTANTE_CAJA':
    'What was short when a till was counted: at opening, at the count, when it passed to another shift or when the owner received what it handed over. Down when what it went on is explained.',
  'cuentaAyuda.FALTANTE_INVENTARIO':
    'Goods that are not there, at their cost: what was short at a count, what was written off as stolen and top-up credit that was short. Also what moved before a count and reached the books after it.',
  'cuentaAyuda.FALTANTE_REPARTO': 'Goods that did not come back from the truck when the route was settled, at their average cost.',
  'cuentaAyuda.REDONDEO':
    'The centavos that do not circulate: when everything is paid in cash, the total is rounded to the nearest coin. Up when the shop loses the centavos and down when the customer pays more, so over a period it can be negative.',
  'cuentaAyuda.PERDIDA_CAMBIARIA': 'What is lost on dollars: the opposite of «FX gain».',
  'cuentaAyuda.TRANSFERENCIAS_PERDIDAS':
    "Payments by transfer or wallet for a sale or a top-up that never reached the bank and were not moved to the client's account.",
  'cuentaAyuda.FALTANTE_GENERAL': 'What was short when general cash was counted against the books. Down when what it went on is explained.',
  'cuentaAyuda.DIFERENCIAS_CONCILIACION':
    'What the bank or wallet statement shows short against the books, such as fees; if it shows more, it goes down.',
  'cuentaAyuda.FIADO_INCOBRABLE': 'Fiado a client owed that the owner gave up on. Down if that is undone or the client ends up paying it.',

  // ---------- Payroll ----------
  'planilla.montoInvalido': 'Type just the number, for example 2500 or 2500.50.',
  'planilla.noSeGuardo': 'Could not save. Try again.',
  'planilla.titulo': 'Payroll',
  'planilla.personas': '{n} person(s) on payroll',
  'planilla.correr': 'Run payroll',
  'planilla.costoMensual': 'Monthly cost',
  'planilla.pieCosto': 'wages + employer share + provisions',
  'planilla.aguinaldoAcumulado': 'Aguinaldo accrued',
  'planilla.vacacionesAcumuladas': 'Holiday pay accrued',
  'planilla.loQueSeDebe': 'already owed',
  'planilla.sinPagar': 'Payroll runs unpaid',
  'planilla.personal': 'People',
  'planilla.corridas': 'Runs',
  'planilla.contratar': 'Hire somebody',
  'planilla.cargo': 'Job title',
  'planilla.salarioMensual': 'Monthly salary (C$)',
  'planilla.leCuesta': 'Costs the business {costo}',
  'planilla.ayudaSalario':
    'The real cost is this figure plus the {tasa} employer share and the provisions.',
  'planilla.fechaIngreso': 'Start date',
  'planilla.ayudaIngreso': 'Length of service comes from here, and severance comes from that.',
  'planilla.ayudaIngresoEditar': 'The day they started work, even if before they were entered here: length of service and the days each run pays come from it.',
  'planilla.cedula': 'ID number',
  'planilla.inssNumero': 'INSS no.',
  'planilla.nadie': 'Nobody on payroll yet.',
  'planilla.desdeAnios': '{cargo} · since {fecha} · {anios} year(s)',
  'planilla.acumulado': 'Accrued: aguinaldo {aguinaldo} · holiday {vacaciones} ({dias} days)',
  'planilla.adelanto': 'Advance',
  'planilla.debeAdelantos': 'Owes {monto} in advances',
  'planilla.adelantoA': 'Advance to {nombre}',
  'planilla.adelantoAyuda':
    "{nombre} owes it: the next payroll takes it off what they are handed, and whatever doesn't fit goes on to the one after.",
  'planilla.montoAdelanto': 'How much is advanced (C$)',
  'planilla.entregarAdelanto': 'Hand over the advance',
  'planilla.adelantosDescontados': 'Advances taken off: {monto} · to hand over {entregar}',
  'planilla.menosAdelanto': '− advance {monto}',
  'planilla.entregar': 'Hand over {monto}',
  'planilla.menosAdelantos': 'Less advances',
  'planilla.liquidar': 'Settle up',
  'planilla.yaNoEstan': 'No longer here',
  'planilla.sinCorridas': 'No payroll has been run yet.',
  'planilla.liquidacionDe': 'Final settlement — {nombre}',
  'planilla.delPeriodo': 'Payroll for {periodo}',
  'planilla.resumenCorrida': '{fecha} · {n} person(s) · cost {costo}',
  'planilla.pagadaEl': 'Paid {fecha}',
  'planilla.marcarPagada': 'Mark as paid',
  'planilla.cotizaciones': 'INSS {inss} · INATEC {inatec} · income tax {ir}',
  'planilla.netoYCosto': 'Net to hand over · total cost {costo}',
  'planilla.corriendo': 'Running…',
  'planilla.cerrarCorrida': 'Close the run',
  // The aguinaldo, paid on its own (2026-10-31).
  'planilla.aguinaldo.pagar': 'Pay aguinaldo',
  'planilla.aguinaldo.pagando': 'Paying…',
  'planilla.aguinaldo.cuando': 'The aguinaldo is due in the first ten days of December, with no INSS or income tax.',
  'planilla.aguinaldo.explica':
    "A month's wage for each year worked, from 1 December to 30 November, or the share for somebody with less. It comes out of what the payroll runs set aside for each person; anything over that is an expense of this payment.",
  'planilla.aguinaldo.nadie': 'Nobody has aguinaldo yet: each payroll run builds it up for whoever has benefits.',
  'planilla.aguinaldo.apartado': 'Set aside: {monto} · no INSS or income tax',
  'planilla.aguinaldo.enElPago': 'On the payment: {monto} · set aside then: {apartado}',
  'planilla.aguinaldo.deMas': '{monto} over what was set aside: an expense of this payment.',
  'planilla.aguinaldo.noEstaba': 'Not on the payment: will be added.',
  'planilla.aguinaldo.noSeEntiende': "The amount for {nombres} is missing or can't be read.",
  'planilla.aguinaldo.total': 'Handed over, with no INSS or income tax',
  'planilla.aguinaldo.totalDeMas': 'Of that, {monto} over what was set aside: aguinaldo expense.',
  'planilla.aguinaldo.corregirTitulo': 'Edit aguinaldo payment',
  'planilla.aguinaldo.pagado': 'Paid {fecha} from {cuenta}. Any change to what was handed over comes out of it, or goes back into it, that same day.',
  'planilla.aguinaldo.liquidados': 'Already settled up with {nombres}: this payment can no longer be edited.',
  'planilla.aguinaldo.antes': 'Before: {monto}',
  'planilla.aguinaldo.sale': '{monto} more comes out of {cuenta}, dated {fecha}.',
  'planilla.aguinaldo.vuelve': '{monto} goes back into {cuenta}, dated {fecha}.',
  'planilla.aguinaldo.anular': 'Void the payment',
  'planilla.aguinaldo.anulando': 'Nobody is left on the payment: it is voided, and what was paid goes back into {cuenta}.',
  'planilla.aguinaldo.titulo': 'Aguinaldo',
  'planilla.aguinaldo.resumen': '{fecha} · {n} person(s) · from {cuenta}',
  'planilla.aguinaldo.anulado': 'Voided',
  'planilla.aguinaldo.sinInss': 'No INSS or income tax.',
  'planilla.aguinaldo.deAntes': 'This run paid {monto} of aguinaldo, from when it was paid with the run: it stays as it is.',
  // Each person's pay stub of a run, a settlement or an aguinaldo payment (2026-11-01).
  'planilla.colillas.boton': 'Pay stubs',
  'planilla.colillas.botonUna': 'Pay stub',
  'planilla.colillas.titulo': 'Pay stubs',
  'planilla.colillas.explica':
    'One for each person: what they earned, what came off it and what they receive. Printed or as a PDF, it has a line for them to sign that they received it; by WhatsApp it goes without. Like the receipts, the stubs are in Spanish.',
  'planilla.colillas.recibe': 'Receives {monto}',
  'planilla.colillas.imprimirTodas': 'Print all ({n})',
  // The same stubs as a PDF to download, a page of US Letter each (2026-11-01).
  'planilla.colillas.pdf': 'Download PDF',
  'planilla.colillas.pdfTodas': 'Download all as PDF',
  'planilla.colillas.archivo': 'pay-stub_{nombre}_{fecha}.pdf',
  'planilla.colillas.archivoTodas': 'pay-stubs_{fecha}.pdf',
  'planilla.colillas.error': 'The PDF could not be made. Try again.',
  // A run set right, or voided (2026-10-31).
  'planilla.corregir.boton': 'Edit',
  'planilla.corregir.titulo': 'Edit payroll run',
  'planilla.corregir.liquidados': 'Already settled up with {nombres}: this run can no longer be edited.',
  'planilla.corregir.antes': 'Before: net {neto} · cost {costo}',
  'planilla.corregir.pagoSale': 'Already paid: {monto} more comes out of {cuenta}, dated {fecha}.',
  'planilla.corregir.pagoVuelve': 'Already paid: {monto} goes back into {cuenta}, dated {fecha}.',
  'planilla.corregir.guardar': 'Save the changes',
  'planilla.corregir.guardando': 'Saving…',
  'planilla.corregir.sinCambios': 'No changes',
  'planilla.corregir.anular': 'Void the run',
  'planilla.corregir.anulando': 'Nobody is left on the run: it is voided, and what it booked is reversed.',
  'planilla.corregir.pagada': 'Paid {fecha} from {cuenta}. Any change to what was handed over comes out of it, or goes back into it, that same day.',
  'planilla.corregir.loQueLaPago': 'whatever paid it',
  'planilla.corregir.sinPagar': 'Not paid yet.',
  'planilla.corregir.porQue': 'Reason for the change (optional)',
  'planilla.corregir.porQueEjemplo': 'E.g. underpaid',
  'planilla.corregir.tenia': 'On the run: {bruto} pay, {neto} net',
  'planilla.corregir.noEstaba': 'Not on the run: will be added.',
  'planilla.anulada': 'Voided',
  'planilla.corregidaEl': 'Edited {fecha}',
  'planilla.periodo': 'Period',
  'planilla.ayudaPeriodo':
    'Nobody is paid twice for the same day: whoever is already in a run for these days is left out.',
  'planilla.salarioDelMes': "This month's salary",
  'planilla.desdeIngreso': 'Started on {fecha}: {dias} days of this run, a month counted as thirty',
  'planilla.desdeIngresoSemana': 'Started on {fecha}: {dias} days of this week',
  'planilla.ayudaSalarioMes': 'Blank = the full salary',
  'planilla.extrasGravables': 'Taxable extras',
  'planilla.vacacionesPagadas': 'Holiday pay paid out',
  'planilla.tieneVacaciones': 'Has {monto} · pays INSS and income tax',
  'planilla.diasVacaciones': 'Holiday days',
  'planilla.diasAcumulados': '{dias} accrued',
  'planilla.aguinaldo': 'Aguinaldo',
  'planilla.liquidarA': 'Final settlement for {nombre}',
  'planilla.ultimoDia': 'Last day',
  'planilla.salarioPendiente': 'Salary outstanding (C$)',
  'planilla.ayudaPendiente': 'Days already worked this month that no run has covered.',
  'planilla.seLeVaAPagar': 'They will be paid',
  'planilla.indemnizacion': 'Severance ({anios} years)',
  'planilla.salarioPendienteCorto': 'Salary outstanding',
  'planilla.menosInss': 'Less their INSS',
  'planilla.menosIr': 'Less income tax',
  'planilla.aEntregar': 'To hand over',
  'planilla.explicacionLiquidacion':
    'The system works out Art. 45 severance from the start date; nobody types it. Dismissal with just cause pays none, but aguinaldo and holiday pay already earned are paid all the same.',
  'planilla.liquidando': 'Settling…',
  'planilla.liquidarYCerrar': 'Settle up and close the contract',
  'planilla.editar': 'Edit',
  'planilla.editarA': 'Edit {nombre}',
  'planilla.comoSePaga': 'How they are paid',
  'planilla.sueldoFijo': 'Fixed pay',
  'planilla.porDia': 'By the day worked',
  'planilla.cadaCuanto': 'How often they are paid',
  'planilla.frecuencia.semanal': 'Weekly',
  'planilla.frecuencia.quincenal': 'Every 15 days',
  'planilla.frecuencia.mensual': 'Monthly',
  'planilla.cada.dia': 'a day',
  'planilla.cada.semanal': 'a week',
  'planilla.cada.quincenal': 'every 15 days',
  'planilla.cada.mensual': 'a month',
  'planilla.pagoPorDia': 'Pay per day (C$)',
  'planilla.sueldoPorSemana': 'Pay per week (C$)',
  'planilla.sueldoPorQuincena': 'Pay per 15 days (C$)',
  'planilla.ayudaPorDia': "A day's pay. At each run you enter the days they worked, half days too.",
  'planilla.ayudaSalarioSinInss': 'The real cost is this figure plus the aguinaldo and holiday provisions.',
  'planilla.ayudaDosCampos': 'Type the monthly salary or the pay {cada}: the other is worked out.',
  'planilla.pagarCorrida': 'Pay the payroll',
  'planilla.pagarLiquidacion': 'Pay the settlement',
  'planilla.pagoDeEfectivoGeneral': 'In cash, it comes out of general cash, not the drawer.',
  'planilla.pagoAnterior': 'Log a past payment',
  'planilla.yaSePago': 'Already paid',
  'planilla.yaSePagoAyuda': 'For days already paid: the run is recorded as paid, on the day and in the way it was paid.',
  'planilla.sePagoEl': 'Paid on',
  'planilla.fechaPagoFuera': 'A day between {desde} and today',
  'planilla.antesDelAdelanto': 'This run took off an advance {nombre} was given on {fecha}: it was paid that day or later',
  'planilla.cotizacionesPorPagar': 'Owed to INSS, INATEC and the DGI',
  'planilla.ultimoPagoCotizaciones': 'Last payment: {periodo}, on {fecha}',
  'planilla.pagarCotizacionesCorto': 'Pay',
  'planilla.pagarCotizaciones': 'Pay INSS, INATEC and the DGI',
  'planilla.mesQueSePaga': 'Month being paid',
  'planilla.cotizacionesAyuda': 'Each amount is what the runs up to that month owe. If the bill says otherwise, type what it says.',
  'planilla.cotizacion.inss': 'INSS (employee and employer)',
  'planilla.cotizacion.inatec': 'INATEC',
  'planilla.cotizacion.ir': 'Income tax withheld',
  'planilla.seDebe': 'Owed: {monto}',
  'planilla.registrarCotizaciones': 'Record the payment',
  'planilla.vacaciones': 'Holidays',
  'planilla.vacacionesApuntadas': 'Already recorded: {dias} holiday days ({lista}). They are off the days earned: do not type them again under «Holiday days».',
  'vacaciones.titulo': 'Holidays for {nombre}',
  'vacaciones.tiene': 'Has {dias} holiday days earned, {monto} set aside to pay them.',
  'vacaciones.desde': 'From',
  'vacaciones.hasta': 'To',
  'vacaciones.hastaAntes': 'It cannot end before it starts',
  'vacaciones.antesDeEntrar': 'Started on {fecha}: could not rest before',
  'vacaciones.dias': 'Days rested',
  'vacaciones.diasAyuda': 'That is {n} days: type fewer if they worked any. Half a day: 0.5.',
  'vacaciones.diasFuera': 'Between 0.5 and {n} days',
  'vacaciones.efecto': '{monto} comes out of what was set aside, and {dias} days are left.',
  'vacaciones.sinGanar': 'They have earned {ganados} days: the other {sinGanar} are paid leave and do not come out of their holidays.',
  'vacaciones.sinDinero': 'Their pay for those days is paid as always, in the payroll run: no money is handed over here.',
  'vacaciones.registrar': 'Record the holidays',
  'planilla.ayuda.costoMensual': "What the people on a fixed pay cost the business in a month: their monthly wage; the employer's INSS ({inss}) and INATEC ({inatec}) for those who pay INSS; and what is set aside for their aguinaldo and their holidays, a twelfth of the wage each. Those two are not handed over each month: they are owed, and paid in December, when they rest or when they leave. Whoever is paid by the day is left out: they cost what they work.",
  'planilla.ayuda.aguinaldo': "The aguinaldo people have earned and not been paid yet: each run sets aside a twelfth of what they earn. It is paid with «Pay aguinaldo» in the first ten days of December, a month's wage for each year worked, or when settling up with somebody who leaves.",
  'planilla.ayuda.vacaciones': "What is set aside for people's holidays, not used yet: each run sets aside 2.5 days a month, a twelfth of the wage. It comes out of here when they rest («Holidays» on their card, or in the run) or are paid for them, and what is left is paid when they leave.",
  'planilla.ayuda.sinPagar': 'The net of the runs and settlements already done that has not been handed over yet, less the advances they took off. Paid with «Mark paid» in «Runs».',
  'planilla.calc.salarios': 'Monthly wages',
  'planilla.calc.inssPatronal': 'Employer INSS ({tasa})',
  'planilla.calc.inatec': 'INATEC ({tasa})',
  'planilla.calc.aguinaldo': 'Aguinaldo, a twelfth',
  'planilla.calc.vacaciones': 'Holidays, a twelfth',
  'planilla.calc.diasDe': '{nombre} · {dias} days',
  'planilla.acumuladoAguinaldo': 'Accrued: aguinaldo {aguinaldo}',
  'planilla.ayudaSalarioSinInssSinVacaciones': 'The real cost is this figure plus the aguinaldo provision.',
  'planilla.prestacionesSinVacaciones': 'Aguinaldo and severance',
  'planilla.ayuda.costoMensualSinVacaciones':
    "What the people on a fixed pay cost the business in a month: their monthly wage; the employer's INSS ({inss}) and INATEC ({inatec}) for those who pay INSS; and what is set aside for their aguinaldo, a twelfth of the wage. That is not handed over each month: it is owed, and paid in December or when they leave. Holidays don't accrue in this shop («Shop settings»). Whoever is paid by the day is left out: they cost what they work.",
  'planilla.desglose.aguinaldoDespues': "{monto} set aside for the aguinaldo isn't handed to them now: it's paid in December or when they leave.",
  'planilla.desglose.titulo': 'Payroll breakdown',
  'planilla.desglose.sueldoDias': 'Pay for {dias} of {de} days (started {fecha})',
  'planilla.desglose.porDia': '{dias} days at {tarifa}',
  'planilla.desglose.inss': 'Their INSS ({tasa})',
  'planilla.desglose.ir': 'Income tax (IR)',
  'planilla.desglose.neto': 'Net',
  'planilla.desglose.adelantos': 'Advances taken off',
  'planilla.desglose.entregado': 'Handed over',
  'planilla.desglose.aEntregarTodos': 'To hand over, everyone',
  'planilla.desglose.entregadoTodos': 'Handed over, everyone',
  'planilla.desglose.costo': 'What it costs the business',
  'planilla.desglose.suPago': 'Their pay',
  'planilla.desglose.yaApartado': 'Set aside in earlier runs',
  'planilla.desglose.costoTotal': 'Total cost',
  'planilla.desglose.cotizacionesDespues':
    "{monto} of INSS, INATEC and income tax isn't handed to them: it's paid to INSS, INATEC and the DGI with «Pay» in «Runs».",
  'planilla.desglose.apartadoDespues':
    "{monto} set aside for the aguinaldo and holidays isn't handed to them now: it's paid in December, when they rest or when they leave.",
  'planilla.registrarPagada': 'Record as paid',
  'planilla.netoPagadoYCosto': 'Net handed over · total cost {costo}',
  'planilla.sueldosEnGastos': '«Expenses» has salaries recorded since {desde}: {lista}. If they are for these days, void them there, or they are counted twice.',
  'planilla.yNMas': 'and {n} more',
  'planilla.pagaInss': 'Pays INSS',
  'planilla.pagaInssAyuda': 'Their INSS is taken off their pay, and the business pays its own and INATEC. Off, none of that.',
  'planilla.prestaciones': 'Aguinaldo, holiday and severance',
  'planilla.prestacionesAyuda': 'They build up with every run, and severance is paid when they leave. Off, nothing builds up.',
  'planilla.sigueDebiendose': 'What they already built up is still owed and paid when they leave: aguinaldo {aguinaldo}, holiday {vacaciones}.',
  'planilla.desdeLaProxima': 'Changes here apply from the next run; runs already made stay as they were paid.',
  'planilla.sinInss': 'No INSS',
  'planilla.sinPrestaciones': 'No benefits',
  'planilla.pieCostoSinDia': 'wages + employer share + provisions; leaves out {n} paid by the day',
  'planilla.deLaSemana': 'Payroll for the week {rango}',
  'planilla.deLaQuincena': 'Payroll for {rango}',
  'planilla.aQuienes': 'Who is paid',
  'planilla.semanaTermina': 'The week ends on',
  'planilla.queMitad': 'Which half of the month',
  'planilla.primeraQuincena': 'Days 1 to 15',
  'planilla.segundaQuincena': 'Days 16 to {fin}',
  'planilla.yaEsta': 'Already paid in: {planilla}',
  'planilla.salarioDeLaSemana': "This week's pay",
  'planilla.salarioDeLaQuincena': "These 15 days' pay",
  'planilla.diasTrabajados': 'Days worked',
  'planilla.diasPorTarifa': '× {tarifa} = {monto}',
  'planilla.ayudaDias': '{tarifa}; half a day is 0.5',
  'planilla.noSeEntiende': 'A number typed on this line does not read: {nombres}',
  'planilla.faltanDias': 'Days worked missing: {nombres}',
  'planilla.tieneVacacionesSinInss': 'Has {monto} · pays income tax',
  'planilla.diasN': '{n} day(s)',
  'planilla.explicacionSinPrestaciones': 'Hired without aguinaldo, holiday or severance: they are paid what is outstanding and anything built up before.',
  'salida.despido_sin_causa': 'Dismissal without just cause',
  'salida.renuncia': 'Resignation',
  'salida.mutuo_acuerdo': 'Mutual agreement',
  'salida.despido_con_causa': 'Dismissal with just cause',

  // ---------- Purchases ----------
  'compra.nueva': 'Goods bought',
  'compra.proveedor': 'Supplier',
  'compra.factura': 'Invoice',
  'compra.facturaRepetida': 'There is already a purchase from this supplier with that invoice ({fecha}, {monto}). Are you entering it twice?',
  'compra.costoPorUnidad': '{monto} per {unidad}',
  'compra.costoMasAlto': 'That is {pct}% more than it has been costing ({antes} per {unidad}). Check the quantity and the unit before saving.',
  'compra.costoMasBajo': 'That is {pct}% less than it has been costing ({antes} per {unidad}). Check the quantity and the unit before saving.',
  'compra.costoMasAltoSinMonto': 'That is a lot more than it has been costing. Check the quantity and the unit before saving.',
  'compra.costoMasBajoSinMonto': 'That is a lot less than it has been costing. Check the quantity and the unit before saving.',
  'compra.opcional': 'Optional',
  'compra.tasaDelDia': "The day's rate",
  'compra.ayudaTasa': "The rate on the purchase date, not today's.",
  'compra.ayudaTasaDolares': 'Paid with dollars from general cash: they leave at the official rate, which is what they are kept at.',
  'compra.ayudaTasaCuentaDolares': 'Paid from a dollar account: they leave at the official rate, which is what the account keeps them at.',
  'compra.formaPago': 'Payment method',
  'compra.alCredito': 'Credit',
  'compra.vence': 'Due',
  'compra.flete': 'Delivery cost {moneda}',
  'compra.ayudaFlete': 'Spread across the lines by value. Paid it to someone else, like the trucker? Add it as a separate payment.',
  'compra.iva': 'IVA {moneda}',
  'compra.ayudaIva': 'The one on the invoice, if it has any. It is part of what the goods cost: spread across the lines by value, like the delivery cost.',
  'compra.ivaCorto': 'IVA',
  'compra.ivaInvalido': 'Check the IVA: type it as an amount, like 150.00, or leave it empty.',
  'compra.nuevoProveedor': 'Add new supplier',
  'compra.agregarComo': 'Add «{nombre}» as a supplier',
  'compra.proveedorNuevoAyuda': 'It is saved with your suppliers and picked for this purchase.',
  'compra.pagarParte': 'Pay part another way',
  'compra.otroPago': 'Add another payment',
  'compra.partes': 'Paid in parts',
  'compra.parteForma': 'Method',
  'compra.parteMonto': 'Amount {moneda}',
  'compra.elResto': 'The rest',
  'compra.restoAyuda': 'What the parts leave: {monto}',
  'compra.nadaQueda': 'The parts pay for the whole purchase.',
  'compra.partesDeMas': 'The parts add up to {monto} more than the purchase.',
  'compra.faltaMontoParte': 'Enter how much each part paid.',
  'compra.unidad': 'Unit',
  'compra.costoTotal': 'Line cost {moneda}',
  'compra.ayudaCosto': 'What the invoice says for this line.',
  'compra.lote': 'Batch',
  'compra.subtotal': 'Subtotal',
  'compra.fleteCorto': 'Delivery cost',
  'compra.totalMoneda': 'Total {moneda}',
  'compra.equivaleA': 'Comes to',
  'compra.registrando': 'Recording…',
  'compra.registrar': 'Record the purchase',
  'compra.registradaDe': 'Purchase recorded: {proveedor}, {total}',
  'compra.elijaProveedor': 'Choose a supplier',
  'compra.agregueProducto': 'Add at least one product',
  'compra.tasaInvalida': 'Check the rate: type it like 36.62 or 36,62.',
  'compra.fleteInvalido': 'Check the freight: type it as an amount, like 150.00, or leave it empty.',
  'compra.faltaTasa': "A purchase in dollars needs the day's rate",
  'compra.faltaVencimiento': 'A purchase on credit needs a due date',
  'compra.faltaCantidad': 'The quantity for {producto} is missing',
  'compra.faltaCosto': 'The cost for {producto} is missing',
  'gastos.demasiado': 'That expense is more than the system can hold, C$ 100,000,000: check the amount',
  'compra.demasiado': 'That purchase is more than the system can hold, C$ 100,000,000: check the costs',
  'compra.faltaLote': '{producto} needs a batch number and an expiry date',
  'compra.noSePudo': 'The purchase could not be recorded',
  'compra.productoNuevoAyuda': 'Once saved it is in the catalogue and on this purchase, which stays as you left it.',

  // ---------- Phone credit ----------
  'recargas.titulo': 'Top-ups',
  'recargas.volver': 'Back to top-ups',
  'recargas.ayudaReferencia': "The top-up goes out now, but the payment waits to be confirmed until somebody sees the money, like a sale's.",
  'recargas.porConfirmar': 'to confirm',
  'recargas.noLlego': 'never arrived',
  'recargas.necesitaCaja':
    'Top-ups take cash, so they need an open till for the count to balance.',
  'recargas.comisionHoy': '{monto} in commission today',
  'recargas.comprarSaldo': 'Buy credit',
  'recargas.operadora': 'Network',
  'recargas.monto': 'Top-up amount',
  'recargas.comoPaga': 'How they pay',
  'recargas.efectivo': 'Cash',
  'recargas.registrar': 'Record the top-up',
  'recargas.noSePudo': 'It could not be recorded',
  'recargas.saldoDisponible': 'Credit on hand',
  'recargas.sinSaldo':
    'No credit recorded. Top-ups still work, but with no way to know what each one earns — record the credit purchase so the commission shows.',
  'recargas.saldoNegativo':
    '{operadora} credit is negative: top-ups were sold without the purchase being recorded.',
  'recargas.ultimas': 'Recent top-ups',
  'recargas.explicacionCompra':
    'Credit is bought below face value. That difference is the profit, and it is earned as each top-up is sold.',
  'recargas.valorFacial': 'Face value',
  'recargas.loQuePago': 'What was paid',
  'recargas.descuentoDel': '{pct}% discount',
  'recargas.comoPago': 'How it was paid',
  'recargas.registrarCompra': 'Record the purchase',
  'recargas.costoMayor': 'What was paid cannot be more than the face value: nobody sells credit for more than it is worth.',
  'recargas.contarSaldo': 'Count balance',
  'recargas.contarExplicacion':
    "Write the balance the operator shows now, on its portal or in its text message. The difference from the books is recorded: if some is missing, top-ups went out that nobody recorded, or were given away.",
  'recargas.saldoReal': 'Actual balance at {operadora}',
  'recargas.registrarConteo': 'Record the count',
  'recargas.resultadoConteo': 'Books {libros} · actual {real}',
  'recargas.faltaSaldo': '{monto} of balance missing',
  'recargas.sobraSaldo': '{monto} of balance over',
  'recargas.cuadraSaldo': 'It matches',

  // ---------- Bulk entry ----------
  'carga.yaVende':
    'The shop is already selling: here a product is made with its prices, and its stock comes in with a purchase, which says what it cost and how it was paid.',
  'carga.yaVendeSinPrecios':
    'The shop is already selling: here a product is made, and its stock comes in with a purchase, which says what it cost and how it was paid.',
  'carga.titulo': 'Bulk entry',
  'carga.sinListas':
    'Create at least one price list first. Open Products once and the system creates the retail and wholesale price lists.',
  'carga.cargadosSesion': '{n} product(s) entered in this session',
  'carga.margenPublico': '% retail',
  'carga.margenMayorista': '% wholesale',
  'carga.ejemploNombre': 'Rice 80/20',
  'carga.costoPor': 'Cost / {unidad}',
  'carga.existencia': 'On hand',
  'carga.atajos': 'Enter saves · Esc clears',
  'carga.enEstaSesion': 'Entered in this session',
  'carga.sinPrecio': 'no price',
  'carga.costoResumen': 'cost {monto}',

  // ---------- New product ----------
  'producto.creado': 'Product created: {nombre}',
  'producto.nuevo': 'New product',
  'producto.categoria': 'Category',
  'producto.unidadBase': 'Stock unit',
  'producto.unidad': 'Unit',
  'producto.ayudaUnidad':
    'How stock is held. Sale presentations (bag, quintal, case) are added afterwards.',
  'producto.codigoBarras': 'Barcode',
  'producto.ayudaCodigo':
    'Optional. Scan it with the camera or type it in. More can be added later: the same goods in another pack size scan differently.',
  'producto.precios': 'Prices',
  'producto.preciosDelDueno': 'Prices are set by the owner.',
  'producto.esMedicamento': 'Is a medicine',
  'producto.descMedicamento':
    'Only the MINSA over-the-counter list. Forces a batch number and expiry date on receipt.',
  'producto.esPerecedero': 'Is perishable',
  'producto.descPerecedero': 'Also forces an expiry date on receipt.',
  'producto.registroSanitario': 'Health registration',
  'producto.guardar': 'Save the product',
  'producto.noSePudoGuardar': 'It could not be saved',
  'producto.sugerirMargen': 'Suggest a price with a {margen}% margin',
  'producto.nombre': 'Name (on receipts)',
  'producto.nombreEn': 'Name in English',
  'producto.ayudaNombreEn':
    'Optional. Shown when the app is in English; receipts always carry the name above.',

  // ---------- Product photo ----------
  'foto.tomar': 'Take photo',
  'foto.cambiar': 'Change photo',
  'foto.quitar': 'Remove photo',
  'foto.preparando': 'Preparing the photo…',
  'foto.galeria': 'Gallery',
  'foto.internet': 'Search the internet',
  'foto.camara.titulo': 'Photo of {producto}',
  'foto.camara.ayuda': 'The photo is what shows in the square.',
  'foto.camara.asiQueda': 'This is the photo.',
  'foto.camara.usar': 'Use photo',
  'foto.camara.otraVez': 'Retake',
  'foto.camara.sinCamaraAyuda':
    'This device has no camera, or the browser will not let it be used. Close this and use “Gallery” to pick a file.',
  'foto.ayuda': 'With a photo it is easy to spot at the till.',
  'foto.noSePudo': 'That image could not be used. Try another photo.',

  // ---------- Scanner ----------
  'escaner.abrir': 'Scan with the camera',
  'escaner.titulo': 'Scan a product',
  'escaner.tituloCodigo': 'Scan the code',
  'escaner.abriendo': 'Opening the camera…',
  'escaner.preparando': 'Getting the reader ready…',
  'escaner.ayuda': 'Fit the barcode inside the box',
  'escaner.linterna': 'Flashlight',
  'escaner.listo': 'Done',
  'escaner.seguir': 'Keep scanning',
  'escaner.continuoAyuda': 'Scan one product after another: each one beeps when it is read.',
  'escaner.codigoGuardado': 'Code saved on this product',
  'escaner.noEsta': 'This code is not in the catalogue',
  'escaner.noEstaAyuda':
    'If it belongs to a product you already have, say which one: the code is saved, and next time it is recognised by itself.',
  'escaner.esDeUnProducto': 'Pick the product',
  'escaner.pidaAlDueno': 'Saving it on a product needs somebody who can edit products.',
  'escaner.cualProducto': 'Which product is it?',
  'escaner.cualProductoAyuda': 'Code {codigo} will be saved on the product you pick.',
  'escaner.buscarProducto': 'Search by name',
  'escaner.cualPresentacion': 'Which one is code {codigo} printed on? That is the one scanning it will charge for.',
  'escaner.otroProducto': 'It was another product',
  'escaner.varios': 'This code is on {n} products',
  'escaner.variosAyuda':
    'Tap the one in your hand, or press its number. If it is the same product entered twice, remove the code from one of them in Products.',
  'cualEs.titulo': 'Which one is it?',
  'escaner.reintentar': 'Try again',
  'escaner.falla.permiso': 'The app is not allowed to use the camera',
  'escaner.falla.permisoAyuda':
    'Allow the camera for this page in the browser settings — the padlock next to the address — and tap “Try again”.',
  'escaner.falla.sinCamara': 'No camera found',
  'escaner.falla.sinCamaraAyuda':
    'This device has no camera, or the browser will not let the app use it. The code can also be typed in by hand.',
  'escaner.falla.ocupada': 'The camera is busy',
  'escaner.falla.ocupadaAyuda': 'Another app is using it. Close that app and tap “Try again”.',
  'escaner.falla.lector': 'The reader could not start',
  'escaner.falla.lectorAyuda':
    'Open the app once with a signal so it is saved on the phone, then try again.',
  'escaner.falla.otra': 'The camera could not be opened',
  'escaner.falla.otraAyuda': 'Tap “Try again”. If it keeps happening, close the app and open it again.',

  'codigo.yaEsDe': 'That code already belongs to “{producto}”.',
  'codigo.yaEsDeDesactivado': 'That code belongs to “{producto}”, which is deactivated.',
  'codigo.verlo': 'Open that product',
  'codigo.yaEstaTitulo': 'Already in the shop',
  'codigo.nuevoTitulo': 'New code',
  'codigo.mismoUOtro':
    'Some factories print the same code on two different products. If this is another one, carry on: scanning it will ask which one.',
  'codigo.esElMismo': 'It is the same product',
  'codigo.esOtro': 'It is another product with the same code',
  'codigo.yaLoLlevan': 'That code is already on {n} products.',
  'codigo.esUnoDeEstos': 'If it is one of these, tap it to open it.',
  'codigo.compartido': 'Also on “{producto}”: scanning it will ask which one.',
  'codigo.compartidoDesactivado': 'Also on “{producto}”, which is deactivated.',
  'codigo.tambienLoLleva': 'Also on “{producto}”',
  'codigo.tambienLoLlevaDesactivado': 'Also on “{producto}”, which is deactivated',
  'codigo.sePreguntaCual': 'When two products share a code, scanning it asks which one.',
  'codigo.loLlenoYo': 'I will fill it in',
  'codigo.muyLargo': 'A barcode cannot be longer than {max} characters.',

  // ---------- Purchase list ----------
  'compras.titulo': 'Purchases',
  'compras.registradas': '{n} recorded',
  'compras.ninguna': 'No purchase recorded yet.',
  'compras.registrarPrimera': 'Record the first one',
  'compras.resumen': '{fecha} · {n} line(s) · {pago}',
  'compras.tasaUsd': ' · US$ at {tasa}',
  'compras.anulada': 'Voided: {motivo}',
  'compras.vence': 'Due {fecha}',
  'compras.flete': 'delivery {monto}',
  'compras.iva': 'IVA {monto}',
  'compras.enPartes': 'in parts',
  'compras.parteEfectivo': 'Cash',
  'compras.conLaCompra': '{fecha} · with the purchase',
  'compras.motivoAnulacion': 'Reason for voiding',
  'compras.anularEsta': 'Void this purchase',
  'compras.noSePudoAnular':
    'It could not be voided: there are later movements on these products. If the cost is what is wrong, correct it; if it is the quantity, record a stock adjustment.',
  'compras.anteriores': 'Past purchases',
  'compras.pagado': 'Paid',
  'compras.porPagar': 'Still owed',
  'corregir.boton': 'Correct a cost',
  'corregir.titulo': 'Correct a cost · {proveedor}',
  'corregir.explicacion':
    "For when a line's cost was typed wrong and the goods have already moved. What is left changes value, what already sold goes to cost of sales, and what was paid or is owed changes with it. The purchase of {fecha} keeps the right cost.",
  'corregir.conDevolucion': 'Some of it already went back to the supplier: this line cannot be corrected.',
  'corregir.costoCorrecto': "The line's correct cost ({moneda}), delivery cost apart",
  'corregir.antes': 'Now: {costo}',
  'corregir.bajoLoAbonado':
    'This supplier has already been paid more than the purchase would cost: they owe you the difference.',
  'corregir.debeMas': 'The supplier is owed {monto} more.',
  'corregir.debeMenos': 'The supplier is owed {monto} less.',
  'corregir.gavetaMas': 'It was paid from the drawer, which is still open: its till count expects {monto} less.',
  'corregir.gavetaMenos': 'It was paid from the drawer, which is still open: its till count expects {monto} more.',
  'corregir.corte': 'It was paid from a drawer already counted.',
  'corregir.conteo': 'It was paid {cuenta}, and counted since.',
  'encontrado.enEse': 'At that count: {antes}; corrected, {despues}.',
  'encontrado.capitalSube': 'It was its first count, which set the capital: capital goes up by {monto}.',
  'encontrado.capitalBaja': 'It was its first count, which set the capital: capital goes down by {monto}.',
  'encontrado.cuadre': 'What squaring it found changes by {monto}.',
  'corregir.cuentaMas': 'It was paid {cuenta}: the books show {monto} less there.',
  'corregir.cuentaMenos': 'It was paid {cuenta}: the books show {monto} more there.',
  'corregir.deEfectivo': 'from general cash',
  'corregir.deBanco': 'from the bank',
  'corregir.deBilletera': 'from the wallet',
  'corregir.guardar': 'Correct the cost',
  'corregir.noSePudo': 'The cost could not be corrected. Check the line and try again.',

  // ---------- Correct a quantity (2026-10-13) ----------
  'cantidad.boton': 'Correct a quantity',
  'cantidad.titulo': 'Correct a quantity · {proveedor}',
  'cantidad.explicacion':
    "For when a line's quantity was typed wrong: a sack entered as one unit. What the line cost stays; what changes is what each unit cost, in the stock and in the sales that took it, which then show what they really made. The purchase of {fecha} keeps the right quantity.",
  'cantidad.correcta': 'What really came',
  'cantidad.ahora': 'The line says {cantidad}: {costo} per {unidad}',
  'cantidad.misma': "That is already the line's quantity.",
  'cantidad.otraAlMismoCosto':
    'Another purchase of this product came in after this one at the same cost, on {fecha}: correct that one first, then this one.',
  'cantidad.cadaUnidad': 'Each {unidad}: {antes} → {despues}',
  'cantidad.ventasBajan': '{n} sale(s) took it at that cost: they cost {monto} less, and make that much more.',
  'cantidad.ventasSuben': '{n} sale(s) took it at that cost: they cost {monto} more, and make that much less.',
  'cantidad.estante': 'In stock: {antes} → {despues} {unidad}, worth {valorAntes} → {valorDespues}.',
  'cantidad.contado':
    'Counted after the purchase: the {cantidad} {unidad} the count found stay, worth {valorAntes} → {valorDespues}.',
  'cantidad.sobranteBaja': 'What the count booked as extra goes down {monto}.',
  'cantidad.sobranteSube': 'What the count booked as extra goes up {monto}.',
  'cantidad.faltanteBaja': 'What the count booked as missing goes down {monto}.',
  'cantidad.faltanteSube': 'What the count booked as missing goes up {monto}.',
  'cantidad.mezclado':
    'More of this product came in at another cost after that purchase: the stock comes to {costo} per {unidad}. If that is not right, correct it on the product, with «Correct the cost».',
  'cantidad.sinDinero': 'No money moves: what was paid and what is owed stay the same.',
  'cantidad.guardar': 'Correct the quantity',
  'cantidad.noSePudo': 'The quantity could not be corrected. Check the line and try again.',
  'pagoCorregido.boton': 'Correct the payment method',
  'pagoCorregido.titulo': 'Correct the payment method · {proveedor}',
  'pagoCorregido.explicacion':
    'Say how the purchase of {fecha}, {monto}, was really paid: the money goes back where the purchase says it came from, and comes out of where it really came from. What the goods cost stays the same.',
  'pagoCorregido.dice': 'The purchase says: {forma}',
  'pagoCorregido.deVerdad': 'How it was really paid',
  'pagoCorregido.deGaveta': 'from the drawer',
  'pagoCorregido.deCuenta': 'from {nombre}',
  'pagoCorregido.vuelveDeuda': 'The supplier is no longer owed {monto}.',
  'pagoCorregido.vuelveGaveta': 'It said it came out of the drawer, which is still open: its till count expects {monto} more.',
  'pagoCorregido.vuelveCorte': 'It said it came out of a drawer already counted.',
  'pagoCorregido.vuelveConteo': 'It said it was paid {cuenta}, and that was counted since.',
  'pagoCorregido.vuelveCuenta': 'It said it was paid {cuenta}: on the books {monto} goes back there.',
  'pagoCorregido.saleDeuda': 'The supplier is owed {monto}, due {fecha}.',
  'pagoCorregido.saleGaveta': 'It comes out of the drawer, which is still open: its till count expects {monto} less.',
  'pagoCorregido.saleCorte': 'It came out of a drawer already counted.',
  'pagoCorregido.saleConteo': 'It was paid {cuenta}, and that was counted since.',
  'pagoCorregido.saleCuenta': 'It was paid {cuenta}: on the books there is {monto} less there.',
  'pagoCorregido.mismaPlata': 'Both are the same money: what the purchase says changes, and nothing moves on the books.',
  'pagoCorregido.enDolares': 'A purchase in dollars came in at the rate its way of paying took: it is not corrected here.',
  'pagoCorregido.enPartes': 'That purchase was paid in parts: each part is a payment of its own, and it is not corrected here.',
  'pagoCorregido.conDevoluciones': 'That purchase already has returns to the supplier, settled the way it was paid: it is not corrected here.',
  'pagoCorregido.conAbonos': 'That purchase already has payments to the supplier: void them first, then correct how it was paid.',
  'pagoCorregido.guardar': 'Correct the payment method',
  'pagoCorregido.noSePudo': 'The payment method could not be corrected. Check and try again.',

  // ---------- Correcting a till's count (2026-10-14) ----------
  'conteoCaja.boton': 'Correct the count',
  'conteoCaja.tituloApertura': 'Correct the opening',
  'conteoCaja.tituloCorte': 'Correct the till count',
  'conteoCaja.ayudaApertura': 'Type what was really in the drawer when it opened. The opening stays in the log as it was typed, with the correction beside it.',
  'conteoCaja.ayudaCorte': 'Type what was really counted at closing and what was really left in the drawer. The till count stays in the log as it was typed, with the correction beside it.',
  'conteoCaja.seAnoto': 'What was recorded',
  'conteoCaja.seConto': 'Counted',
  'conteoCaja.seEsperaba': 'Expected',
  'conteoCaja.delGeneral': 'Out of general cash',
  'conteoCaja.puesto': 'Brought from general cash',
  'conteoCaja.alAbrirFue': 'At opening',
  'conteoCaja.resultado': 'Result',
  'conteoCaja.seDejo': 'Left in the drawer',
  'conteoCaja.seEntrego': 'Handed over',
  'conteoCaja.alDueno': '{monto}, to the owner',
  'conteoCaja.porRecibir': '{monto}, to be received',
  'conteoCaja.faltaron': '{monto} short',
  'conteoCaja.sobraron': '{monto} over',
  'conteoCaja.cuadro': 'It matched',
  'conteoCaja.cordobasHabia': 'Córdobas really in the drawer',
  'conteoCaja.dolaresHabia': 'Dollars really in the drawer',
  'conteoCaja.cordobasContados': 'Córdobas really counted',
  'conteoCaja.dolaresContados': 'Dollars really counted',
  'conteoCaja.yaSeAbrio': 'The drawer has been opened again: what was left also changes what the next opening expected.',
  'conteoCaja.laSiguiente': 'At the next opening',
  'conteoCaja.ultimoCorteAyuda': 'What the last till count left is corrected on that till count, and this opening is corrected with it.',
  'conteoCaja.corregirUltimoCorte': 'Correct the last till count',
  'conteoCaja.libros': 'With this correction',
  'conteoCaja.alAbrir': 'At opening: {antes}; corrected, {despues}.',
  'conteoCaja.enElCorte': 'At the till count: {antes}; corrected, {despues}.',
  'conteoCaja.enLaSiguiente': 'At the next opening: {antes}; corrected, {despues}.',
  'conteoCaja.faltaban': '{monto} was short',
  'conteoCaja.sobraban': '{monto} was over',
  'conteoCaja.cuadraba': 'it matched',
  'conteoCaja.faltan': '{monto} is short',
  'conteoCaja.sobran': '{monto} is over',
  'conteoCaja.cuadra': 'it matches',
  'conteoCaja.vuelveAlGeneral': '{monto} goes back to general cash.',
  'conteoCaja.saleDelGeneral': '{monto} more comes out of general cash.',
  'conteoCaja.generalContado': 'General cash was counted after it, and its balance stays as counted.',
  'conteoCaja.masPorRecibir': 'To be received from this till: {monto} more.',
  'conteoCaja.menosPorRecibir': 'To be received from this till: {monto} less.',
  'conteoCaja.quedaraEnGaveta': 'Left in the drawer: {monto}.',
  'conteoCaja.esperara': 'At its till count, the till will expect {monto}.',
  'conteoCaja.guardar': 'Correct',
  'conteoCaja.noSePudo': 'The count could not be corrected. Check and try again.',
  'conteoCaja.no.deRuta': "A route's till is squared when the route is settled.",
  'conteoCaja.no.antigua': 'This till opened before the float was kept on the books: its opening cannot be corrected.',
  'conteoCaja.no.entregada': 'This till changed hands after it opened: whoever took it over counted it, and that count already found what was there.',
  'conteoCaja.no.sinCorte': 'This till has not been counted yet.',
  'conteoCaja.no.quedaDeMas': 'More cannot be left in the drawer than was counted.',
  'conteoCaja.no.relevada': 'The drawer has been opened again, and the next opening is not on this device: what was left cannot be corrected from here.',
  'conteoCaja.no.recibida': 'The owner already received and counted what this till handed over: that can no longer be corrected.',
  'conteoCaja.no.explicada': '{explicado} of the shortage has already been explained; corrected, {faltaria} would be short. Void that explanation in “Expenses and payments” first.',

  // ---------- Correct the stock's cost (2026-10-13) ----------
  'costoInv.boton': 'Correct the cost',
  'costoInv.titulo': 'Correct the cost · {producto}',
  'costoInv.explicacion':
    'What each {unidad} costs in stock from now on: what is left is worth that, and what sells goes at that cost. Sales already made stay as they are, and the difference goes to «Inventory cost adjustment». If the cost went wrong because a purchase had the wrong quantity, correct that quantity in «Goods bought» instead: that corrects the sales too.',
  'costoInv.lugar': 'Where',
  'costoInv.ahora': 'Now: {costo} per {unidad} · {cantidad} {unidad} · {valor}',
  'costoInv.costo': 'Cost (C$)',
  'costoInv.cuantas': 'For how many ({unidad})',
  'costoInv.ayuda': 'For example, a 100 lb sack that cost C$ 1,250: C$ 1,250 for 100.',
  'costoInv.valor': 'What is here is worth {antes} → {despues}: {diferencia} to «Inventory cost adjustment».',
  'costoInv.sinExistencia': 'Nothing in stock here: the new cost is for what sells before the next purchase.',
  'costoInv.mismo': 'That is already its cost.',
  'costoInv.guardar': 'Correct the cost',
  'costoInv.noSePudo': 'The cost could not be corrected. Try again.',
  'costoInv.motivoSinCosto': 'It had no cost',
  'costoInv.motivoRecienPuesto': 'Corrects the cost just set',

  // ---------- Starter catalogue ----------
  'semilla.titulo': 'Starter catalogue',
  'semilla.sinPermiso': 'Editing products needs permission.',
  'semilla.agregados': '{n} product(s) added.',
  'semilla.sinPreciosAun':
    'None has a price or a cost yet: the cost comes from the first purchase and the price is yours to set. A price that comes out of the box is a wrong price somebody will believe.',
  'semilla.sinPreciosDelDueno':
    'None has a price or a cost yet: the cost comes from the first purchase and the price is set by the owner.',
  'semilla.verCatalogo': 'See the catalogue',
  'semilla.porAgregar': '{n} product(s) to add',
  'semilla.explicacion':
    'A list of what almost any pulpería sells, so you do not start on a blank screen. Pick what is useful; the rest can be deleted or edited later.',
  'semilla.yaEstan': 'All {total} are in',
  'semilla.pendientes': '{n} of {total} to add',
  'semilla.agregando': 'Adding {n} of {total}…',
  'semilla.todoCargado': 'Everything is already in',
  'semilla.agregar': 'Add {n} product(s)',
  'semilla.notaFinal':
    'No prices and no costs, on purpose. Prices are local and they move; cost comes from the first purchase, which is where it has to come from.',

  // ---------- Inventory ----------
  'inv.titulo': 'Inventory',
  'inv.enExistencia': '{monto} in stock',
  'inv.contar': 'Count',
  'inv.reempaque': 'Repacking',
  'inv.traslado': 'Transfer',
  'inv.buscar': 'Search for a product…',
  'inv.negativos':
    '{n} product(s) show negative stock: more was sold than was recorded. A count fixes them.',
  'inv.verNegativos': 'See which',
  'inv.verTodo': 'See all',
  'inv.sinNegativos': 'No product shows negative stock now.',
  'inv.sinExistencias': 'No stock to show.',
  'inv.aCero': 'Recount to 0',
  'inv.contarProducto': 'Recount {producto}',
  'inv.aCeroAyuda':
    'Stock: {cantidad}. More was sold than was recorded. If none is left, record it at 0; if some is, type how much there is.',
  'inv.sacar': 'Write off',
  'inv.sacarProducto': 'Write off {producto}',
  'inv.registrarSalida': 'Record the write-off',
  'inv.cantidadEn': 'Quantity in {unidad}',
  'inv.ayudaMotivo': 'Each reason goes to its own account: damaged and stolen are not the same.',
  'inv.escaneado': 'Stock',
  'inv.hay': '{cantidad} in stock',
  'inv.noHay': 'None in stock',
  'inv.hayNegativo': 'Negative: {cantidad}. A count fixes it.',
  'inv.productoNuevoAyuda': 'Once saved it is in the catalogue, with no stock yet: stock comes in with a purchase or a count.',
  'salidaInv.merma': 'Damaged or spilled',
  'salidaInv.vencido': 'Expired',
  'salidaInv.consumo_interno': 'Taken for the house',
  'salidaInv.robo': 'Stolen or missing',

  // ---------- Transfer ----------
  'traslado.titulo': 'Transfer',
  'traslado.saleDe': 'Out of',
  'traslado.entraA': 'Into',
  'traslado.distintos': 'The source and the destination have to be different.',
  'traslado.hay': 'There is {cantidad} {unidad}',
  'traslado.excede': ' — you are moving more than is recorded',
  'traslado.sinExistencia': 'No stock recorded at the source',
  'traslado.seMueven': '{monto} moves, at the cost it is recorded at.',
  'traslado.registrar': 'Record the transfer',
  'traslado.registrado': 'Transfer recorded',

  // ---------- Product detail ----------
  'detalle.producto': 'Product',
  'detalle.noEncontrado': 'This product was not found.',
  'detalle.subtitulo': '{categoria} · held in {unidad}',
  'detalle.existencia': 'Stock',
  'detalle.sinExistencia': 'No stock recorded.',
  'detalle.valorTotal': 'Total value',
  'detalle.negativa':
    'Stock is negative. More was sold than the system had recorded — check it with a count.',
  'detalle.presentaciones': 'Pack sizes and prices',
  'detalle.presentacion': 'Pack size',
  'detalle.aCosto': 'at {monto}',
  'detalle.costoUnidad': ' · cost {monto}',
  'detalle.nuevaPresentacion': 'New pack size',
  'detalle.ejemploPresentacion': '5 lb bag',
  'detalle.precioDeLaNueva': 'Added with its price: {precios}, {n} × the «{unidad}» price. Change it in the table.',
  'detalle.enUnidad': 'In {unidad}',
  'detalle.lotes': 'Batches received',
  'detalle.vence': 'expires {fecha} · {cantidad} {unidad}',
  'detalle.codigos': 'Barcodes',
  'detalle.sinCodigos': 'None yet. With one, this product is found at the till just by scanning it.',
  'detalle.nuevoCodigo': 'Add a code',
  'detalle.yaLoTiene': 'This product already has that code.',
  'detalle.suenaComo': 'Scanning it sells',
  'detalle.elijaPresentacion': 'Choose the pack size',
  'detalle.deCualEs': 'Which pack size is this code on? Choose it and the code is saved: a case\'s code is printed on the case.',
  'detalle.desactivado':
    "Deactivated. It doesn't show at the till or in prices, and its code doesn't find it. What was sold and bought keeps its name.",
  'detalle.reactivar': 'Reactivate',
  'detalle.desactivar': 'Deactivate product',
  'detalle.ayudaDesactivar':
    "For something the shop no longer sells. It isn't deleted: what was sold and bought keeps its name, and it can be reactivated.",
  'detalle.desactivarTitulo': 'Deactivate “{nombre}”?',
  'detalle.desactivarMensaje':
    "It stops showing at the till, in prices and in the product list, and its code no longer finds it. It isn't deleted: whatever was already sold or bought keeps its name, and it can be reactivated from “Deactivated”, in Products.",

  // ---------- Repacking ----------
  'reempaque.titulo': 'Repacking',
  'reempaque.cambiar': 'Change',
  'reempaque.queSeAbre': 'What is being opened?',
  'reempaque.disponible': '{cantidad} {unidad} · {costo}/{unidad}',
  'reempaque.sinExistencia': 'no stock recorded',
  'reempaque.hay': '{cantidad} {unidad}',
  'reempaque.seConsume': 'Used up ({unidad})',
  'reempaque.sePierde': 'Lost',
  'reempaque.loDerramado': 'What was spilled',
  'reempaque.queSale': 'What comes out?',
  'reempaque.lleva': 'How much {origen} it holds, in {unidad}',
  'reempaque.llevaNoSuma': 'What each holds adds up to {suma} {unidad}; {bueno} {unidad} were used, less what was lost.',
  'reempaque.enUnidad': 'in {unidad}',
  'reempaque.costoEmpaque': 'Packaging and labour cost',
  'reempaque.ayudaEmpaque': 'Bags, labels, time. Added to the cost of the packed product.',
  'reempaque.comoQueda': 'How the cost works out',
  'reempaque.valorConsumido': 'Value used up',
  'reempaque.valorMerma': 'Lost (to spoilage)',
  'reempaque.empaque': 'Packaging',
  'reempaque.vaAlEmpacado': 'Goes to the packed product',
  'reempaque.costoDe': 'Cost of {producto}',
  'reempaque.registrar': 'Record the repacking',
  'reempaque.registrado': 'Repacking recorded',
  'reempaque.noSePudo': 'The repacking could not be recorded',

  // ---------- Stock count ----------
  'conteo.resultado': 'Count result',
  'conteo.cuadraron': '{n} of {total} products matched.',
  'conteo.detalle': 'system {sistema} · counted {contado}',
  'conteo.cuadro': 'matched',
  'conteo.titulo': 'Physical count',
  'conteo.conCantidad': '{n} product(s) with a quantity',
  'conteo.agregarProducto': 'Add a product to the count',
  'conteo.explicacion':
    'What the system holds is hidden while counting. A count taken while looking at the answer measures nothing. Selling can go on: each product is compared with what the system said when its number was written. Deactivated products can be counted too.',
  'conteo.registrar': 'Record the count',
  'conteo.malas': 'Check what was counted for {lista}: a quantity cannot be negative or have letters.',
  'conteo.productoNuevoAyuda': 'Once saved it is in the catalogue and on this count, which stays as you left it.',

  // ---------- Log ----------
  'bitacora.titulo': 'Log',
  'bitacora.registros': '{n} record(s) on this device',
  'bitacora.movimientos': 'Movements',
  'bitacora.rechazos': 'Rejections',
  'bitacora.grupo.todo': 'Everything',
  'bitacora.grupo.ventas': 'Sales and till',
  'bitacora.grupo.inventario': 'Inventory',
  'bitacora.grupo.clientes': 'Clients and routes',
  'bitacora.grupo.dinero': 'Money and payroll',
  'bitacora.grupo.sistema': 'System',
  'bitacora.sinMovimientos': 'No movements of that kind on this device.',
  'bitacora.desconocido': 'Unknown',
  'bitacora.sinSincronizar': ' · not synced',
  'bitacora.explicacion':
    'The log is not a separate feature: it is the record everything else is derived from. It cannot be edited or deleted, because nothing is stored twice.',
  'bitacora.errorServidor':
    'The server could not be reached ({error}). Below are only the ones from this device.',
  'bitacora.sinRechazos': 'No record was rejected. That is how it should always be.',
  'bitacora.revisado': 'Reviewed',
  'bitacora.explicacionRechazos':
    'A rejected record is never deleted. Marking it reviewed only says somebody has seen it; what happened stays recorded.',

  // ---------- Quick pricing ----------
  'precios.titulo': 'Prices/Costs',
  'precios.subtitulo': '{hechos} of {total} with every price',
  'precios.todosListos': 'Everything has a price on every list.',
  'precios.todosConCosto': 'Everything has a cost.',
  'precios.nadaQueMostrar': 'No products yet.',
  'precios.soloFaltantes': 'Missing Prices',
  'precios.soloSinCosto': 'Missing Costs',
  'precios.todos': 'All',
  'precios.campo': '{lista} for {producto}',
  'precios.buscar': 'Search for a product…',
  'precios.porUnidad': 'per {unidad}',
  'precios.costo': 'cost {monto}',
  'precios.sinCosto': 'no cost yet',
  'precios.guardado': 'Saved',
  'precios.ponerCosto': 'Cost',
  'precios.ponerCostoDe': 'Correct the cost of {producto}',
  'precios.sugerido': 'Suggested {monto}',
  'precios.usarSugerido': 'Use {monto}',
  'precios.margen': 'Margin',
  'precios.explicacion':
    'Type the price and tap “Save” on its row. What you save stays on the list until you switch views, in case something needs fixing. If you have already bought the product, the system suggests a price that leaves you {margen}% profit. Under each price is the margin it leaves: amber below the {minimo} minimum, red when it makes nothing.',
  'precios.explicacionSinCosto':
    'Type the price and tap “Save” on its row. What you save stays on the list until you switch views, in case something needs fixing.',
  'precios.seguir': 'Carry on',

  // ---------- Product photos ----------
  'fotos.titulo': 'Photos',
  'fotos.subtitulo': '{hechos} of {total} with a photo',
  'fotos.sinFoto': 'Missing Photos',
  'fotos.todos': 'All',
  'fotos.buscar': 'Search by name or code…',
  'fotos.explicacion':
    'Tap “Take photo” on a product: the camera opens, and the photo goes on as soon as you accept it. “Gallery” is for a photo you already have, such as one the supplier sent, and “Search the internet” finds photos of that product in open catalogues and on Wikipedia. The product stays on the list until you switch views, in case it needs taking again.',
  'fotos.tomarDe': 'Take photo of {producto}',
  'fotos.cambiarDe': 'Change photo of {producto}',
  'fotos.galeriaDe': 'Gallery: a photo of {producto} already on the phone',
  'fotos.internetDe': 'Find a photo of {producto} on the internet',
  'fotos.guardada': 'Photo saved',
  'fotos.todosConFoto': 'Every product has a photo.',
  'fotos.ningunoSinFoto': 'No product missing a photo matches.',
  'fotos.ninguno': 'No product matches.',
  'fotos.nadaQueMostrar': 'No products yet.',
  'fotos.seguir': 'Carry on',
  'sugeridas.titulo': 'Photos from the internet',
  'sugeridas.explicacion': 'Photos of “{producto}” found in open product catalogues and on Wikipedia. Pick the one that is your product, in your pack size.',
  'sugeridas.buscando': 'Looking for photos…',
  'sugeridas.sinConexion': 'Looking for photos on the internet needs a connection to the server.',
  'sugeridas.error': 'The search did not work. Try again.',
  'sugeridas.ninguna': 'No photo of this product was found. Take one with “Take photo”.',
  'sugeridas.ocupado': 'Too many searches in a row. Try again in a minute.',
  'sugeridas.ocupadoAlgo': 'Not every place could be searched: in a minute, “Search again” may find more.',
  'sugeridas.noLlegaron': 'Photos were found but could not be fetched. Try again.',
  'sugeridas.otraVez': 'Search again',
  'sugeridas.porSuCodigo': 'By barcode',
  'sugeridas.elegir': 'Pick the photo from {fuente}: {titulo}',
  'sugeridas.de': 'From {fuente}',
  'sugeridas.verPagina': 'See its page',
  'sugeridas.usar': 'Use this photo',

  // ---------- Packages the sales ask for ----------
  'paquetes.titulo': 'Packages',
  'paquetes.subtitulo': 'From the last {dias} days of sales',
  'paquetes.sugeridos': 'Missing Packages',
  'paquetes.todos': 'All',
  'paquetes.buscar': 'Search by name or code…',
  'paquetes.explicacion':
    'What keeps being sold in one same larger amount, like 12 packets at once, is quicker to ring up as a package: at the till you tap “Pack (12)” instead of counting 12. Here is each product sold that way in the last {dias} days: the same amount in {veces} sales or more, on different days, and far more often than the amounts either side of it. Tap “Add”: the package comes with its name, what it holds and its price, which starts at what it holds × the unit’s, and you change whatever needs it before adding it. “No” takes it off the list on this device.',
  'paquetes.porque': '{cantidad} at a time in {veces} of its {de} sales, on {dias} different days',
  'paquetes.porqueTodas': '{cantidad} at a time in all {de} of its sales, on {dias} different days',
  'paquetes.agregar': 'Add {paquete}',
  'paquetes.agregarDe': 'Add {paquete} to {producto}',
  'paquetes.agregado': '{paquete} added',
  'paquetes.yaNo': '{paquete} is no longer suggested on this device.',
  'paquetes.deshacer': 'Undo',
  'paquetes.noDe': 'Don’t suggest {paquete} for {producto}',
  'paquetes.noExplicado': 'Not a package: don’t suggest it again on this device',
  'paquetes.nuevo': 'New package',
  'paquetes.nuevoDe': 'New package of {producto}',
  'paquetes.ningunoSugerido': 'No product missing a package matches.',
  'paquetes.ninguno': 'No product matches.',
  'paquetes.nadaQueSugerir': 'Nothing to suggest: no product has kept selling in a larger amount it has no package of.',
  'paquetes.nadaQueMostrar': 'No products yet.',
  'paquetes.seguir': 'Carry on',
  'paquetes.hoja.cantidadMal': 'Type how much it holds, as a number.',
  'paquetes.hoja.yaTiene': 'It already has {paquete}.',
  'pos.enOferta': 'Offer',
  'pos.llevaGratis': '{nombre} · {n} free',
  'pos.llevaFalta': '{nombre} · take {n}',
  'pos.ofertaTermino': 'The offer on {producto} has ended: it is back at its list price. Check the total and charge again.',
  'ofertas.titulo': 'Offers',
  'ofertas.subtitulo': 'What to put on sale, how much off and until when',
  'ofertas.explicacion':
    'Suggested: what expires before it sells at its pace, and what moves slowly: more than eight weeks of stock, or nothing sold in {dias} days. The nearer the date, or the longer it has sat, the more comes off, never below what it cost except what is expiring. An offer goes on the shop’s list: the till charges it by itself between its dates, and what sells on offer does not count in «Reorder».',
  'ofertas.enOferta': 'On offer ({n})',
  'ofertas.empieza': 'From {desde} to {hasta}: not started yet',
  'ofertas.terminaHoy': 'Ends today',
  'ofertas.hasta': 'Until {hasta} · {n} days left',
  'ofertas.motivo.vence': 'Expiring',
  'ofertas.motivo.lento': 'Slow-moving',
  'ofertas.motivo.otro': 'Set by hand',
  'ofertas.terminar': 'End',
  'ofertas.terminarDe': 'End the offer on {producto}',
  'ofertas.terminarTitulo': 'End the offer?',
  'ofertas.terminarMensaje': '{producto} goes back to its list price today, at every till.',
  'ofertas.terminarProgramada': 'The offer on {producto} will not start.',
  'ofertas.noSePudo': 'The offer could not be ended.',
  'ofertas.buscar': 'Search by name or code…',
  'ofertas.sugeridas': 'Suggested',
  'ofertas.todos': 'All',
  'ofertas.ningunoCoincide': 'No product matches.',
  'ofertas.nadaQueSugerir': 'Nothing to suggest: nothing expires before it sells and nothing has sat on the shelf for more than eight weeks.',
  'ofertas.nadaQueMostrar': 'No products yet.',
  'ofertas.porqueVence': '{cantidad} expire on {fecha} unsold at its pace, in {dias} days',
  'ofertas.porqueVenceManana': '{cantidad} expire tomorrow unsold at its pace',
  'ofertas.porqueVenceHoy': '{cantidad} expire today unsold',
  'ofertas.porqueLento': '{cantidad} on hand: {semanas} weeks at its pace',
  'ofertas.porqueParado': '{cantidad} on hand and none sold in {dias} days',
  'ofertas.seVencen': '{monto} that would be lost',
  'ofertas.parados': '{monto} sitting still',
  'ofertas.sugerido': 'Suggested',
  'ofertas.duracion': '{n} days, until {hasta}',
  'ofertas.duracionHoy': 'Today only',
  'ofertas.tabla.antes': 'Before',
  'ofertas.tabla.enOferta': 'On offer',
  'ofertas.tabla.precio': 'Price',
  'ofertas.tabla.precioDe': 'Price for {n}',
  'ofertas.tabla.margen': 'Margin',
  'ofertas.tabla.ganancia': 'Profit',
  'ofertas.cambio.sinEfecto': 'Not below the list price, {monto}: the till charges that.',
  'ofertas.bajoCostoAviso': 'Under cost: still better than throwing it out expired.',
  'ofertas.sinPrecio': 'No price on the shop’s list',
  'ofertas.yaEnOferta': 'On offer until {hasta}',
  'ofertas.puesta': 'Put on offer',
  'ofertas.yaNo': 'Not suggested on this device for {dias} days.',
  'ofertas.deshacer': 'Undo',
  'ofertas.poner': 'Put on offer',
  'ofertas.ponerDe': 'Put {producto} on offer',
  'ofertas.noDe': 'Don’t suggest {producto}',
  'ofertas.noExplicado': 'Not putting it on offer: don’t suggest it on this device for {dias} days',
  'ofertas.hoja.titulo': 'Offer on {producto}',
  'ofertas.hoja.tipo': 'Kind of offer',
  'ofertas.hoja.tipoPrecio': 'Lower the price',
  'ofertas.hoja.tipoLleva': 'Multi-buy',
  'ofertas.hoja.llevaCual': 'How many they take, and pay for',
  'ofertas.hoja.lleva': 'Take',
  'ofertas.hoja.paga': 'Pay for',
  'ofertas.hoja.llevaMal': 'They take 2 to 50, and pay for at least one fewer than they take.',
  'ofertas.hoja.llevaPierde': '{presentacion}: every {n} taken lose {pierde}.',
  'ofertas.hoja.descuento': 'How much off them all',
  'ofertas.hoja.sinPrecio': 'It has no price on the shop’s list: give it one in «Prices/Costs» first.',
  'ofertas.hoja.precioDe': 'Offer price of {presentacion}',
  'ofertas.hoja.sinOferta': 'No offer',
  'ofertas.hoja.ahorra': 'Customer saves {monto}',
  'ofertas.hoja.sugeridoEra': 'Suggested: {monto}',
  'ofertas.hoja.noBaja': 'The offer has to be less than the list price.',
  'ofertas.hoja.cuesta': 'Costs {monto}',
  'ofertas.hoja.bajoMinimo': 'Below the {minimo} minimum',
  'ofertas.hoja.sinCosto': 'No cost yet: the margin it leaves is unknown.',
  'ofertas.hoja.bajoCosto': '{presentacion}: under its cost, each one sold loses {pierde}.',
  'ofertas.hoja.mejorQueBotar': 'Even so, what expires on {fecha} unsold is lost whole: {monto}.',
  'ofertas.hoja.desde': 'From',
  'ofertas.hoja.hasta': 'To',
  'ofertas.hoja.dias': '{n} days on offer, the first and the last included.',
  'ofertas.hoja.unDia': 'A single day on offer.',
  'ofertas.hoja.desdeHoy': 'It starts today or later.',
  'ofertas.hoja.alReves': 'It ends before it starts.',
  'ofertas.hoja.muyLarga': 'An offer lasts {n} days at most.',
  'ofertas.hoja.nota': 'Note (optional)',
  'ofertas.hoja.noSePudo': 'It could not be put on offer.',
  'ofertas.resultados': 'Results',
  'ofertas.resultadosExplica':
    'How the offers of the last {dias} days did: their product’s pace on their list while they ran, against the four weeks before; what sold on offer, what it brought in and the discount given; and what is left.',
  'ofertas.sinResultados': 'No offer has run in the last {dias} days.',
  'ofertas.corre': 'Running',
  'ofertas.termino': 'Ended',
  'ofertas.r.vendidas': 'Sold on offer',
  'ofertas.r.ritmo': 'A week',
  'ofertas.r.ritmoValor': '{durante}, before {antes}',
  'ofertas.r.ritmoNuevo': '{durante}, before none',
  'ofertas.r.ingreso': 'Brought in',
  'ofertas.r.descuento': 'Discount given',
  'ofertas.r.ganancia': 'Profit',
  'ofertas.r.quedan': 'Left today',
  'ofertas.r.lote': 'Of what was expiring by {fecha}: {quedan} left',
  'ofertas.r.loteCorre': 'Of what expires by {fecha}: {quedan} left',
  'ofertas.r.botadas': 'thrown out expired: {n}',
  'ofertas.r.despues': 'Since: {n} a week',
  'ofertas.van': 'Sold: {n} in {dias} days · {semana} a week, before {antes}',
  'ofertas.vanNuevo': 'Sold: {n} in {dias} days · {semana} a week, before none',
  'ofertas.vanHoy': 'Sold today: {n}',
  'ofertas.whatsapp': 'Send on WhatsApp',
  'ofertas.whatsappExplica': 'The offers running and to come, to send to the customers',
  'ofertas.personal': 'PDF for staff',
  'ofertas.personalExplica': "Today's offers as the till charges them, on a page to print and hand to your staff: in Spanish, with no costs",
  'ofertas.personalArchivo': 'current-offers_{fecha}.pdf',
  'paquetes.hoja.masQue': 'A package holds more than {paquete}.',
  'paquetes.hoja.precio': '{lista} price of the package',
  'paquetes.hoja.desde': 'Starts at {n} × the {paquete} price',
  'paquetes.hoja.costo': 'Costs {monto}',
  'paquetes.hoja.sinPrecio':
    'Added without a price: whoever sets prices gives it one on the product’s page, and until then the till does not offer it.',
  'paquetes.hoja.noSePudo': 'The package could not be added.',

  // ---------- Getting started ----------
  'arranque.paso': 'Step {n} of {total}',
  'arranque.saltar': 'Skip for now',
  'arranque.pendiente': 'The shop still needs setting up',
  'arranque.continuar': 'Carry on',

  'arranque.nombre.titulo': 'What is the shop called?',
  'arranque.nombre.porque': 'It shows at the top of the screen and on every receipt you hand out.',
  'arranque.nombre.ejemplo': 'Pulpería La Esperanza',
  'arranque.nombre.accion': 'Save the name',

  'arranque.productos.titulo': 'There is nothing to sell yet',
  'arranque.productos.porque':
    'You can start from a list of what almost any pulpería sells and drop what you do not need. It beats typing them in one at a time.',
  'arranque.productos.accion': 'Start from the basic list',
  'arranque.productos.alterna': "I'd rather type them in myself",

  'arranque.precios.titulo': 'They still need prices',
  'arranque.precios.porque':
    'Nothing can be sold without a price. {n} to go — it is all on one screen, a price next to each name.',
  'arranque.precios.accion': 'Set the prices',

  'arranque.esperando.titulo': 'The shop is not ready yet',
  'arranque.esperando.porque':
    "Setup is not finished, and that is the owner's job. Let them know and sign in again.",

  // ---------- Home screen ----------
  'inicio.liquidarRuta': 'Settle the route',
  'inicio.mercaderiaComprada': 'Goods bought',
  'inicio.gastos': 'Expenses/Debts',
  'inicio.pedidosDeHoy': "Today's orders",
  'inicio.atajos': 'Shortcuts',
  'inicio.ayudaCompra': 'Record what you bought from a supplier: what came in, what it cost and how it was paid. It adds to the stock.',
  'inicio.ayudaGasto': 'What was spent and what is owed: the expenses, what is still owed to suppliers and what they were paid. A new one is recorded right there.',
  'inicio.ayudaProductos': 'Everything the shop sells, with its prices, presentations and codes. A new one is added right there.',
  'inicio.ayudaClientes': "Who owes you and how much: each client's credit, their payments and their statement.",
  'inicio.ayudaPedidos': "Take a client's order to deliver another day, and see the ones still to deliver.",
  'inicio.ayudaPedidosDeHoy': 'The orders still to deliver, by delivery date.',
  'inicio.ayudaInventario': 'How much of each product there is, and where. To count it, move it, repack it or take out what is damaged.',
  'inicio.ayudaFiado': 'Everything the clients owe: how much, who, and since when.',
  'inicio.ayudaContabilidad': 'How much did you make? Sales, costs, expenses and profit for each period and the balance sheet; and in its tab, the payroll.',
  'inicio.historialVentas': 'Sales History',
  'inicio.ayudaHistorialVentas': 'Every sale: a ticket, a client or a product, by date, payment or who charged it.',
  'inicio.ayudaReponer': 'What is running out and how much of each thing to buy, by category or by supplier, with what it cost last time.',
  'inicio.ayudaControl': 'What each person did with the money: voided sales, refunds, prices brought down and tills short, person by person.',
  'inicio.ayudaBitacora': 'Everything recorded, in order: who did what, when and on which device. Search it by what was done, who, the day or a ticket.',
  'inicio.contabilidadPlanilla': 'Accounting/Payroll',
  'inicio.ayudaCerrarCaja': 'At the end of your shift, step by step: count the drawer, leave the change and hand over the rest. If the shop is closing, the day is gone over too.',
  'inicio.mas': 'Everything else',
  'inicio.cajaCerrada': 'The till is closed — start the day before selling',
  'inicio.enRuta': 'A route from before is still open ({camion}, since {fecha}): settle it to close this till.',
  'inicio.faltaProveedor': 'No suppliers yet. One is needed before a purchase can be recorded.',

  // ---------- Menu ----------
  'menu.titulo': 'Everything else',
  'seccion.pestanas': 'Screens in this section',
  'seccion.precios': 'Prices/Costs',
  'seccion.gastos': 'Expenses',

  // ---------- Test data (development only) ----------
  'prueba.cargar': 'Fill with test data',
  'prueba.explicacion':
    'Builds a whole shop in one go — priced products, stock that was bought, clients, sales, an order, expenses and staff — so you can try the system without setting anything up. Only appears in development.',
  'prueba.creando': 'Creating… {paso}',
  'prueba.fallo': 'It failed: {error}',
  'prueba.borrar': 'Start from scratch',
  'prueba.confirmarBorrar': 'Everything goes: the server and this device.',
  'prueba.borrarAviso': 'And then, what do you want to start with?',
  'prueba.conDatos': 'With test data',
  'prueba.vacio': 'Empty',
  'prueba.yaHay': 'There is data already. Nothing new was created.',

  // ---------- Settings ----------
  'ajustes.cambiarNombre': 'Change',
  'inicio.sinPrecio': '{n} product(s) have no price — they cannot be sold',
  'ajustes.titulo': 'Settings',
  'ajustes.idioma': 'Language',
  'ajustes.esteDispositivo': 'This device',
  'ajustes.codigo': 'Code',
  'ajustes.ultimoRecibido': 'Last entry received',
  'ajustes.proximoLocal': 'Next local number',
  'ajustes.configuracion': 'Configuration',
  'ajustes.negocio': 'Business',
  'ajustes.tasaTienda': 'Shop rate',
  'ajustes.tasaOficial': 'Official BCN rate',
  'ajustes.margenMinimo': 'Minimum margin',
  'ajustes.iva': 'VAT',
  'ajustes.ivaActivo': 'On',
  'ajustes.ivaInactivo': 'Off (not DGI-registered)',
  'ajustes.camiones': 'Trucks',
  'ajustes.usuarios': 'Users',
  'ajustes.bitacora': 'Activity log',
  'ajustes.bitacoraTodo': 'Everything that has happened',
  'ajustes.ultimaSync': 'Last sync',
  'ajustes.nunca': 'Never',
  'ajustes.desfaseReloj': 'Clock drift',
  'ajustes.datos': 'Data on this device',
  'ajustes.conservados': 'Kept by the browser',
  'ajustes.ayudaConservar':
    'Short of space, the browser could clear it, and whatever has not been sent with it. Add the app to the home screen and ask again.',
  'ajustes.pedirConservar': 'Ask to keep it',
  'ajustes.explicacionCodigo':
    "The device code is the prefix of every ticket number. That is why two devices can never issue the same receipt, even with no signal.",
  'ajustes.rechazados': '{n} rejected',
  'ajustes.salir': 'Sign out',

  // ---------- Fiado ----------
  'pago.fiado': 'On credit',
  'fiado.sinCliente': 'To sell on credit, first choose who to.',
  'fiado.escogerCliente': 'Choose the client',
  'fiado.debe': 'Owes {monto}',
  'fiado.aFavor': '{monto} in their favour',
  'fiado.quedaria': 'After this sale they would owe',
  'fiado.limite': 'Credit limit',
  'fiado.sinFiado': 'No credit',
  'fiado.noTieneFiado': '{nombre} has no credit set up.',
  'fiado.pasaLimite': 'That is {monto} over the limit.',
  'fiado.ustedAutoriza': 'You are allowing it as the owner.',
  'fiado.pideDueno': 'It needs the owner\'s PIN.',
  'fiado.pinYConfirmar': 'Owner\'s PIN, then confirm',
  'fiado.motivoPin': 'Selling {monto} on credit to {nombre}, over their limit.',
  'comun.imprimir': 'Print',
  'comun.listo': 'Done',
  'clientes.activosYFiado': '{n} active · owed {monto}',
  'clientes.todos': 'Everyone',
  'clientes.deben': 'Owing ({n})',
  'clientes.ayudaLimite': 'The most they may owe. Empty: credit only if the owner approves it.',
  'clientes.limiteInvalido': 'Type an amount, or leave it empty.',
  'clientes.saldoAnterior': 'Owed from before',
  'clientes.ayudaSaldoAnterior':
    'What they already owed before the app, as the notebook has it. Correcting it later changes only the difference.',
  'clientes.saldoAnteriorInvalido': 'Type an amount, or leave it empty.',

  // ---------- La cuenta de un cliente ----------
  'cuenta.noExiste': 'That client does not exist.',
  'cuenta.editar': 'Edit',
  'cuenta.debe': 'Owes',
  'cuenta.noDebe': 'Owes nothing',
  'cuenta.aFavor': 'In their favour',
  'cuenta.limite': 'Credit limit: {monto}',
  'cuenta.sinFiado': 'No credit set up',
  'cuenta.debeDesde': 'Owing since {fecha}, {n} day(s) ago',
  'cuenta.debeDesdeHoy': 'Owing since today',
  'cuenta.recordar': 'Remind by WhatsApp',
  'cuenta.estado': 'Statement',
  'cuenta.movimientos': 'History',
  'cuenta.sinMovimientos': 'Nothing sold on credit yet.',
  'cuenta.verAnteriores': 'Show earlier ({n} more)',
  'cuenta.fiado': 'On credit · {ticket}',
  'cuenta.devolucion': 'Return · {ticket}',
  'cuenta.saldoAnterior': 'Owed from before',
  'cuenta.porConfirmar': 'not confirmed yet',
  'cuenta.abono': 'Payment · {forma}',
  'cuenta.perdido': 'Written off',
  'cuenta.deshacer': 'Undo',
  'cuenta.darPorPerdido': "Won't pay: write it off",
  'perdido.titulo': 'Write off',
  'perdido.explicacion':
    '{nombre} owes {monto}. What is written off comes off what they owe and goes down as an expense, «Fiado written off»: what fiado cost the shop. The sale stays a sale. If they come back and pay, undo it.',
  'perdido.monto': 'How much?',
  'perdido.quedaria': 'They would still owe {monto}',
  'perdido.masDeLoQueDebe': 'They owe {monto}: no more than that',
  'perdido.motivo': 'Why?',
  'perdido.motivoEjemplo': "Moved away, passed away, won't pay",
  'perdido.limite': "Their fiado limit stays at {monto}: change it in their file if you won't give them fiado any more.",
  'perdido.confirmar': 'Write off',
  'perdido.deshacerTitulo': 'Undo the write-off',
  'perdido.deshacerExplicacion':
    'They owe the {monto} written off on {fecha} again. If they came to pay, take the payment after.',
  'perdido.deshacerParte':
    'They owe {monto} of the {total} written off on {fecha} again: the rest already came back, with a payment, a void or a return.',
  'perdido.deshacerEjemplo': 'They came back and paid, or it was a mistake',
  'cuenta.queda': 'Leaves {monto}',

  // ---------- Abonos ----------
  'abono.titulo': 'Payment from {nombre}',
  'abono.monto': 'How much they pay',
  'abono.forma': 'How they pay',
  'abono.quedaria': 'They would still owe {monto}',
  'abono.quedaAFavor': '{monto} would be in their favour',
  'abono.aLaGaveta': 'Goes into the open till\'s drawer: the till count will expect it.',
  'abono.sinCaja': 'To take cash, open the till on this device: the payment goes into the drawer and the till count expects it.',
  'abono.anularDeCaja':
    'That payment went into a shift already closed: the {monto} comes out of the till open on this device, to hand back to the client.',
  'abono.anularSinCaja':
    'That payment went into a shift already closed. To hand back the {monto}, first open the till on this device.',
  'abono.porConfirmar': 'Waits in «Unconfirmed transfers» until somebody sees the money; if it never comes, they owe it again.',
  'abono.alBanco': 'Goes to the bank account.',
  'abono.aLaBilletera': 'Goes to the mobile wallet.',
  'abono.registrar': 'Record payment',
  'abono.listo': 'Payment recorded',
  'abono.enviar': 'Send receipt by WhatsApp',
  'abono.anular': 'Void payment',
  'abono.anularExplicacion': 'The {monto} payment from {fecha} is taken away, and what they owe goes back up.',
  'abono.pinParaAnular': 'Voiding the {monto} payment from {cliente}. It goes on record under {nombre}.',
  'caja.abonos': 'Client payments in cash',
  'caja.devoluciones': 'Refunds paid in cash',
  'caja.pagadoDeGaveta': 'Purchases, expenses and suppliers paid from the drawer',
  'caja.fiado': 'Sold on credit this shift (not in the drawer)',

  // Devoluciones de clientes
  'ventas.fiado': 'on credit',
  'ventas.conDevolucion': 'with a return',
  'ventas.devueltas': '{n} returned',
  'ventas.devuelto': 'Returned',
  'devolucion.devolver': 'Return items',
  'devolucion.devolverMonto': 'Refund {monto}',
  'devolucion.titulo': 'Return {ticket}',
  'devolucion.explicacion': 'Enter how much of each item is coming back. The refund is what was charged, less its share of any discount.',
  'devolucion.vendido': 'Sold: {n}',
  'devolucion.yaDevuelto': 'already returned: {n}',
  'devolucion.cuanto': 'How much comes back',
  'devolucion.todo': 'All',
  'devolucion.masDeLoQueQueda': 'Only {n} left to return.',
  'devolucion.como': 'How the money goes back',
  'devolucion.aSuCuenta': 'Off their tab',
  'devolucion.deLaGaveta': 'It comes out of the open till\'s drawer: the cash count will expect that much less.',
  'devolucion.deSuCuenta': 'It comes off what {nombre} owes. If they owe nothing, it stays as credit in their favour.',
  'devolucion.delBanco': 'It comes out of the bank account.',
  'devolucion.deLaBilletera': 'It comes out of the mobile wallet.',
  'devolucion.sinCaja': 'To refund in cash, open the till on this device.',
  'devolucion.pin': 'Refund {monto} from sale {ticket}. It goes on record under {nombre}.',
  'devolucion.hastaEnDinero': 'At most {monto} goes back as money, what this sale took in as money. Give that back, and the rest to their account.',
  'devolucion.sinDinero': 'This sale took in no money to give back: its transfer has not been confirmed yet.',
  'devolucion.esperaLaTransferencia':
    'What was paid by transfer has not been confirmed yet: it goes on their tab once it shows at the bank, or once it is marked as never arrived.',
  'devolucion.contraLaPerdida': '{monto} was paid with a transfer that never arrived: it does not go on their tab, it comes off that loss.',
  'devolucion.conElRedondeo': '{monto} goes back, what was charged: the {redondeo} of rounding was never charged.',
  'devolucion.noSePudo': 'The return could not be recorded.',
  'abono.noSePudo': 'The payment could not be recorded.',

  // Devoluciones a proveedores
  'devolucionCompra.devolver': 'Return to supplier',
  'devolucionCompra.devuelto': 'Returned to supplier: {monto}',
  'devolucionCompra.titulo': 'Return to {proveedor}',
  'devolucionCompra.explicacion': 'Purchase of {fecha}. Each item is worth what it cost on this purchase, with its share of the delivery cost.',
  'devolucionCompra.recibido': 'Received: {n} {unidad}',
  'devolucionCompra.masQueExistencia': 'Only {n} in stock here. Check the count.',
  'devolucionCompra.como': 'How the supplier settles it',
  'devolucionCompra.descontar': 'Off what we owe',
  'devolucionCompra.deLaCuenta': 'It comes off what is still owed on this purchase ({monto}).',
  'devolucionCompra.pasaDeLoQueSeDebe': 'Only {monto} is still owed on this purchase. Take off less, or ask for the money back.',
  'devolucionCompra.alEfectivo': 'The supplier hands back cash: it goes to general cash, not the till\'s drawer.',
  'devolucionCompra.alBanco': 'The supplier pays it back into the bank account.',
  'devolucionCompra.aLaBilletera': 'The supplier pays it back into the mobile wallet.',

  // Mínimo en existencia
  'detalle.minimo': 'Minimum stock ({unidad})',
  'detalle.minimoEs': 'Minimum stock: {n} {unidad}',
  'detalle.minimoAutomatico': 'Based on sales',
  'detalle.minimoInvalido': 'Enter a quantity, or leave it empty.',
  'detalle.ayudaMinimo': 'When stock falls to this or below, it shows up in «Reorder». Empty: worked out from how fast it sells.',

  // Editar producto
  'editarProducto.boton': 'Edit product',
  'editarProducto.titulo': 'Edit product',
  'editarProducto.guardar': 'Save changes',
  'editarProducto.faltaNombre': 'Enter the product’s name.',
  'editarProducto.yaExiste': 'There is already a product called “{nombre}”.',
  'editarProducto.ayudaUnidad': 'What the stock is counted in.',
  'editarProducto.soloElNombre':
    'Only the name changes, nothing is converted: the {cantidad} {antes} in stock become {cantidad} {despues}.',

  // Por comprar
  'reponer.titulo': 'Reorder',
  'reponer.subtitulo': '{n} product(s)',
  'reponer.subtituloUrgentes': '{n} product(s) · {urgentes} can\'t wait',
  'reponer.unaSemana': '1 week',
  'reponer.semanas': '{n} weeks',
  'reponer.porProveedor': 'By supplier',
  'reponer.porCategoria': 'By category',
  'reponer.explicacion': 'What won\'t last {n} week(s) at the pace of the last four, and anything at its minimum, with how much to buy. Tap one to see its last purchases.',
  'reponer.nada': 'Nothing is running low.',
  'reponer.agotados': 'Out of stock',
  'reponer.pronto': 'Out within {n} days',
  'reponer.alUltimoCosto': 'At the last cost',
  'reponer.sinCosto': 'Leaving out {n} never bought through the app: they have no cost yet.',
  'reponer.seReempacan': 'Bagged here',
  'reponer.sinProveedor': 'No supplier yet',
  'reponer.sinProveedorAyuda': 'Never bought through the app. Once the first purchase is received, they go with that supplier.',
  'reponer.nProductos': '{n} product(s)',
  'reponer.nUrgentes': '{n} urgent',
  'reponer.unos': 'about {monto}',
  'reponer.ultimaCompra': 'last bought {fecha}',
  'reponer.estimado': 'About {monto} at the last cost',
  'reponer.de': 'From: {producto}',
  'reponer.reempacar': 'Repack',
  'reponer.agotado': 'Out of stock',
  'reponer.hay': '{n} left',
  'reponer.alDia': '{n} a day',
  'reponer.aLaSemana': '{n} a week',
  'reponer.alcanza': 'lasts {n} day(s)',
  'reponer.seAcabaHoy': 'runs out today',
  'reponer.minimo': 'minimum {n}',
  'reponer.comprarPara': 'To buy for {n} week(s)',
  'reponer.hayAhora': 'In stock',
  'reponer.seVende': 'Sells',
  'reponer.sinVentas': 'No sales in four weeks',
  'reponer.desdeQueLlego': 'since {fecha}',
  'reponer.alcanzaPara': 'Lasts',
  'reponer.menosDeUnDia': 'Less than a day',
  'reponer.nDias': '{n} day(s)',
  'reponer.minimoEtiqueta': 'Minimum',
  'reponer.ultimaVenta': 'Last sold',
  'reponer.ningunaVenta': 'Not in four weeks',
  'reponer.comoLaUltima': 'No sales to go by: as much as the last purchase.',
  'reponer.ultimasCompras': 'The last purchases',
  'reponer.nuncaComprado': 'Never bought through the app.',
  'reponer.cadaUno': '{monto} each',
  'reponer.verProveedor': 'See {proveedor}',
  'reponer.colProducto': 'Product',
  'reponer.colSituacion': 'Left · lasts · sells',
  'reponer.colSituacionProveedor': 'Left · lasts · sells · supplier',
  'reponer.colUltimaCompra': 'Last bought',
  'reponer.colCantidadCosto': 'Quantity · cost',
  'reponer.colComprar': 'To buy',
  'reponer.colCosto': 'Est. cost',
  'inicio.seEstanAcabando': '{n} product(s) running low',

  // Descargar datos
  'comun.si': 'Yes',
  'comun.no': 'No',
  'exportar.titulo': 'Download data',
  'exportar.corto': 'Download',
  'exportar.explicacion': 'Files that open in Excel or any spreadsheet. Dated records come from the period chosen; the rest as they stand today.',
  'exportar.compartirTitulo': '{negocio} data',
  'exportar.compartirTodo': 'Send everything',
  'exportar.descargarTodo': 'Download everything',
  'exportar.descargar': 'Download',
  'exportar.compartir': 'Send',
  'exportar.preparando': 'Preparing…',
  'exportar.error': 'The file could not be prepared. Try again.',
  'exportar.comoEstaHoy': 'As it stands today.',
  'exportar.ventas': 'Sales',
  'exportar.ventasDesc': 'One row per sale: total, how it was paid, cost and profit.',
  'exportar.detalle': 'Sales by product',
  'exportar.detalleDesc': 'One row per item sold, to add up by product.',
  'exportar.inventario': 'Stock valuation',
  'exportar.inventarioDesc': 'How much of each product is in each place, and what it is worth at cost.',
  'exportar.precios': 'Products and prices',
  'exportar.preciosDesc': 'Every pack size with its price on each list.',
  'exportar.clientes': 'Clients and credit',
  'exportar.clientesDesc': 'The clients, their limit and what they owe.',
  'exportar.abonos': 'Payments on credit',
  'exportar.abonosDesc': 'What clients paid off what they owe.',
  'exportar.compras': 'Purchases',
  'exportar.comprasDesc': 'What was bought, what was paid, returned and still owed.',
  'exportar.gastos': 'Expenses',
  'exportar.gastosDesc': 'The business\'s expenses, by category.',
  'exportar.diario': 'Journal',
  'exportar.diarioDesc': 'Every entry with its accounts, debits and credits: what an accountant asks for.',
  'exportar.col.fecha': 'Date',
  'exportar.col.ticket': 'Ticket',
  'exportar.col.cliente': 'Client',
  'exportar.col.total': 'Total',
  'exportar.col.iva': 'IVA',
  'exportar.col.descuento': 'Discount',
  'exportar.col.envio': 'Delivery cost',
  'exportar.col.devuelto': 'Returned',
  'exportar.col.costo': 'Cost',
  'exportar.col.utilidad': 'Profit',
  'exportar.col.anulada': 'Voided',
  'exportar.col.anulado': 'Voided',
  'exportar.col.producto': 'Product',
  'exportar.col.cantidad': 'Quantity',
  'exportar.col.precio': 'Price',
  'exportar.col.subtotal': 'Subtotal',
  'exportar.col.categoria': 'Category',
  'exportar.col.lugar': 'Location',
  'exportar.col.unidad': 'Unit',
  'exportar.col.costoUnitario': 'Cost per unit',
  'exportar.col.valor': 'Value',
  'exportar.col.minimo': 'Minimum',
  'exportar.col.activo': 'Active',
  'exportar.col.presentacion': 'Pack size',
  'exportar.col.contiene': 'Contains',
  'exportar.col.codigos': 'Barcodes',
  'exportar.col.tipo': 'Type',
  'exportar.col.telefono': 'Phone',
  'exportar.col.direccion': 'Address',
  'exportar.col.lista': 'Price list',
  'exportar.col.limite': 'Credit limit',
  'exportar.col.debe': 'Debit',
  'exportar.col.haber': 'Credit',
  'exportar.col.monto': 'Amount C$',
  'exportar.col.montoOriginal': 'Amount in its currency',
  'exportar.col.moneda': 'Currency',
  'exportar.col.forma': 'Paid by',
  'exportar.col.referencia': 'Reference',
  'exportar.col.nota': 'Note',
  'exportar.col.descripcion': 'Description',
  'exportar.col.proveedor': 'Supplier',
  'exportar.col.documento': 'Invoice',
  'exportar.col.vence': 'Due',
  'exportar.col.pagado': 'Paid',
  'exportar.col.pendiente': 'Still owed',
  'exportar.col.concepto': 'Description',
  'exportar.col.codigo': 'Code',
  'exportar.col.cuenta': 'Account',
  'exportar.col.saldo': 'Owes',

  // Impresora
  'ajustes.impresora': 'Receipt printer',
  'ajustes.papel': '{mm} mm roll',
  'ajustes.ayudaImpresora': 'Prints through whatever printer this phone or computer has connected. Most small ones take 58 mm. What you choose here applies to this device only.',
  'ajustes.imprimirPrueba': 'Print a test page',
  'seccion.reponer': 'Reorder',

  // Cliente nuevo y cédula
  'pos.clienteNuevo': 'New client',
  'pos.abonar': 'Pay some of what they owe',
  'pos.agregarComo': 'Add «{nombre}» as a client',
  'pos.pantallaCliente': 'Customer screen',

  // The customer screen: the second window, on a monitor facing the counter.
  'pantalla.titulo': 'Customer screen',
  'pantalla.ayuda': 'Move this window to the screen facing the customer and press “Full screen”.',
  'pantalla.sinCaja': 'Open “Sell” in the other window and the sale shows up here.',
  'pantalla.completa': 'Full screen',
  'pantalla.bienvenido': 'Welcome!',
  'pantalla.aPagar': 'To pay',
  'pantalla.gracias': 'Thank you for your purchase!',
  'pantalla.vuelto': 'Your change',
  'pantalla.oferta': 'Offer',
  'pantalla.ahorra': 'You save',
  'pantalla.ahorro': 'You saved {monto}',
  'pantalla.llevaGratis': '{nombre} · {n} free',
  'clientes.cedula': 'ID number (cédula)',
  'clientes.ayudaCedula': 'The number on their ID card, in case you ever need to find them.',
  'clientes.ejemploCedula': '001-010190-0001A',
  'clientes.fotoCedula': 'Photo of their ID',
  'clientes.tomarFotoCedula': 'Take a photo of their ID',
  'clientes.cambiarFotoCedula': 'Take it again',
  'clientes.ayudaFotoCedula': 'Up close: let the card fill the photo, with the name and number readable. Afterwards only the owner sees it.',
  'cuenta.datos': 'Details',
  'cuenta.fotoNoLlega': 'The photo hasn\'t reached this phone yet. It will show once there\'s signal.',
  'cuenta.verFotoCedula': 'See the ID photo full size',
  'exportar.col.cedula': 'ID number',
  'cuenta.acercarFoto': 'Zoom in',
  'cuenta.alejarFoto': 'See the whole photo',
  'cuenta.tocarParaAcercar': 'Tap the photo to zoom in.',
  'cuenta.tocarParaAlejar': 'Drag the photo to read it. Tap it again to see all of it.',

  // Teléfono estricto
  'clientes.ayudaTelefono': 'Eight digits, without the 505.',
  'clientes.ayudaTelefonoObligatorio': 'Required: eight digits, without the 505.',
  'clientes.faltanDigitos': '{n} digit(s) to go.',
  'clientes.telefonoConfirmar': 'Read it back to the client to confirm it.',
  'clientes.telefonoIncompleto': '{n} digit(s) missing: Nicaraguan numbers have 8.',
  'clientes.telefonoPrefijo': 'Nicaraguan numbers start with 2, 5, 7 or 8.',
  'clientes.telefonoLargo': 'A phone number has 8 digits. If there are two numbers, keep just one.',
  'clientes.telefonoExtranjeroCorto': 'Too short for a foreign number: the country code goes after the +.',
  'clientes.telefonoFalta': 'Enter their phone: it\'s how you\'ll collect.',

  // Business settings
  'ajustes.cambiarConfiguracion': 'Change the settings',
  'baja.titulo': 'This device was revoked',
  'baja.explicacion': 'The owner removed it from the shop, or the server no longer recognises it. Nothing done here would reach anywhere any more, so the app stops. If the owner reinstates it, it opens again by itself, with everything it had.',
  'baja.pendientes': '{n} record(s) from this device were never sent.',
  'baja.intentar': 'Try again',
  'baja.borrar': 'Wipe this device and register it again',
  'baja.borrarTitulo': 'Wipe what is on this device?',
  'baja.borrarMensaje': "It is left as if newly installed, ready to register with the owner's PIN.",
  'baja.borrarConPendientes': "It is left as if newly installed, ready to register with the owner's PIN. The {n} record(s) that were never sent are lost.",
  'personal.titulo': 'Staff and devices',
  'personal.subtitulo': 'Who signs in to the shop, and on which device',
  'personal.administrar': 'Manage staff and devices',
  'personal.personas': 'People',
  'personal.personasAyuda': 'Everyone with their own name and PIN: what they do goes on record under them.',
  'personal.usted': '(you)',
  'personal.desactivada': 'Deactivated',
  'personal.agregar': 'Add a person',
  'personal.rol': 'What they do',
  'personal.rolAyuda.cajero':
    'Sells, charges, opens and closes the till; records the goods bought and keeps the inventory and the products with their prices. Never sees profit or what the stock is worth.',
  'personal.rolAyuda.repartidor': 'Takes the orders out and charges them at a till of their own, on their phone: starts and ends a shift like a cashier. Never sees costs.',
  'personal.rolAyuda.dueno': 'Everything: prices, purchases, staff, settings, and the PIN that authorises.',
  'personal.agregada': '{nombre} can sign in with their PIN on this device now, and on the others after the next sync.',
  'personal.agregadaConDueno': "{nombre} can sign in with their PIN now. From now on, voids and refunds ask for the owner's PIN; this can be changed in «Configuración».",
  'personal.noSeGuardo': 'It could not be saved. Try again.',
  'personal.pinEnUso': 'That PIN already belongs to someone else. Choose another.',
  'personal.pinIgual': 'The new PIN is the same as the current one.',
  'personal.desactivarTitulo': 'Deactivate {nombre}?',
  'personal.desactivarMensaje': "{nombre} will no longer be able to sign in on any device, from each one's next sync. Everything they did stays in their name, and they can be reactivated.",
  'personal.desactivar': 'Deactivate',
  'personal.reactivar': 'Reactivate',
  'personal.pinNuevo': 'Give them a new PIN',
  'personal.pinNuevoDe': 'New PIN for {nombre}',
  'personal.pinNuevoAyuda': 'For when {nombre} forgot theirs or someone else knows it. The current one stops working on each device as soon as it syncs.',
  'personal.rolPropio': 'Only another owner can change what you do.',
  'personal.ultimoDueno': 'This is the only active owner, and the shop always needs one.',
  'personal.ustedAyuda': 'Your own PIN is changed in Ajustes, with «Change my PIN».',
  'personal.miPin': 'Change my PIN',
  'personal.miPinListo': 'Done. Your new PIN works on this device now, and on the others after the next sync.',
  'personal.cambiarPin': 'Change the PIN',
  'personal.pinActual': 'Your current PIN',
  'personal.pinNuevoCampo': 'New PIN (6 to 10 digits)',
  'personal.equipos': 'Devices',
  'personal.equiposAyuda': 'The phones and computers registered to the shop.',
  'personal.equiposPractica': 'The practice shop only has this device.',
  'personal.equiposSinServidor': 'No connection to the server: when each device last connected is unknown.',
  'personal.esteEquipo': '(this device)',
  'personal.tipo.mostrador': 'Counter',
  'personal.tipo.telefono': 'Phone',
  'personal.tipo.camion': 'Truck',
  'personal.tipo.admin': 'Office',
  'personal.dadoDeBaja': 'Revoked',
  'personal.vistoEn': 'last connected {cuando}',
  'personal.nuncaVisto': 'has never connected',
  'personal.relojAdelantado': 'Its clock is {min} min fast: what it records gets the wrong time.',
  'personal.relojAtrasado': 'Its clock is {min} min slow: what it records gets the wrong time.',
  'personal.darDeBaja': 'Revoke',
  'personal.darDeBajaTitulo': 'Revoke «{nombre}»?',
  'personal.darDeBajaMensaje': 'It stops syncing the next time it tries to connect, and whatever it had not sent stays on it. If it turns up, or it was a mistake, it can be reinstated right here, with everything it had. If it was lost or taken, change the PINs too, yours first: the device keeps what checks them, and in a few hours they can be worked out.',
  'personal.agregarEquipo': "To add a device, open the app on it: it will ask for the owner's PIN.",
  'personal.darDeAlta': 'Reinstate',
  'personal.darDeAltaTitulo': 'Reinstate «{nombre}»?',
  'personal.darDeAltaMensaje': "It syncs again the next time it connects, with everything on it: whatever it had not sent goes into the books then. Only do this if the device is back in the shop's hands.",
  'negocio.titulo': 'Shop settings',
  'negocio.subtitulo': 'These apply to every phone in the shop.',
  'negocio.cambiar': 'Change',
  'negocio.sinPoner': 'Not set',
  'negocio.noSeGuardo': 'The change could not be saved. Try again.',
  'negocio.errorFalta': 'This cannot be left empty.',
  'negocio.grupoNegocio': 'The business',
  'negocio.grupoNegocioAyuda': 'How it appears on every receipt, printed or sent by WhatsApp.',
  'negocio.nombre': 'Name',
  'negocio.nombreAyuda': 'At the top of every receipt and on the home screen.',
  'negocio.direccion': 'Address',
  'negocio.direccionAyuda': 'The way you would give it to a customer, from a place everybody knows. Left empty, it is not printed.',
  'negocio.direccionEjemplo': 'From the central park, 2 blocks toward the lake',
  'negocio.telefono': 'Phone',
  'negocio.telefonoAyuda': 'Eight digits, without the 505. Left empty, it is not printed.',
  'negocio.ruc': 'RUC',
  'negocio.rucAyuda': 'If the shop is registered with the DGI. Left empty, it is not printed.',
  'negocio.errorRuc': 'Only numbers, letters and dashes.',
  'negocio.pie': 'Receipt footer',
  'negocio.pieAyuda': 'The last line: to say thank you or to tell customers something, like your opening hours. Left empty, nothing is printed.',
  'negocio.muestra': 'How the receipt looks',
  'negocio.grupoDolares': 'Dollars',
  'negocio.porDolar': 'C$ {tasa} per dollar',
  'negocio.cordobasPorDolar': 'Córdobas per dollar',
  'negocio.tasaTienda': 'Shop rate',
  'negocio.tasaTiendaAyuda': 'What a dollar is worth when a customer pays in dollars, and in expenses and purchases paid in dollars.',
  'negocio.tasaOficial': 'Official BCN rate',
  'negocio.tasaOficialAyuda': "The Central Bank's rate. The books are kept at it: the gap between the two rates is an exchange gain or loss, not margin.",
  'negocio.avisoTasa': "That is more than the Central Bank's rate (C$ {oficial}): the shop loses a little on every dollar it takes.",
  'negocio.errorTasa': 'Enter how many córdobas a dollar is worth, like 36.50.',
  'negocio.errorTasaRango': 'A dollar is worth between C$ {min} and C$ {max}. Check the decimal point is there.',
  'negocio.grupoCaja': 'Till',
  'negocio.redondeo': 'Cash rounding',
  'negocio.exacto': 'Exact',
  'negocio.redondeoEjemplo': 'When it is all paid in cash, {antes} is charged as {despues}. Transfers and cards are always exact.',
  'negocio.redondeoExacto': 'Cash is charged exactly, to the centavo.',
  'negocio.conteoCiego': 'Blind count',
  'negocio.conteoCiegoAyuda': 'Whoever opens or closes the till counts the money before seeing how much there should be, so the count is not bent to fit the number.',
  'negocio.fondo': 'Till float',
  'negocio.fondoAyuda': 'What is left in the drawer every night for change. The till count suggests it and says how much to take out.',
  'negocio.fondoLibre': 'Nothing',
  'negocio.fondoCordobas': 'Córdobas that stay',
  'negocio.fondoHojaAyuda': 'Empty: the till count suggests leaving nothing.',
  'negocio.grupoPrecios': 'Prices',
  'negocio.margenMinimo': 'Minimum margin',
  'negocio.margenMinimoAyuda': "Selling below this margin asks for the owner's PIN. At 0%, it only asks when selling below cost.",
  'negocio.ventaRapida': 'Quick sales without the owner',
  'negocio.ventaRapidaHasta': 'Up to {monto} per sale',
  'negocio.ventaRapidaSiempre': 'Never: every quick sale asks for the PIN',
  'negocio.ventaRapidaAyuda': "A quick sale has no cost and moves no stock, so nothing checks its price. Past this, charging it asks for the owner's PIN.",
  'negocio.ventaRapidaMonto': 'Córdobas per sale',
  'negocio.ventaRapidaHojaAyuda': 'What the quick sales on one sale may add up to without the owner. At 0, they all ask for the PIN.',
  'negocio.porcentaje': 'Percentage',
  'negocio.errorMargen': 'Enter a number from 0 to {max}, like 10 or 12.5.',
  'negocio.faltanteAviso': 'Warn about a till shortage',
  'negocio.faltanteAvisoDesde': 'from {monto}',
  'negocio.faltanteAvisoSiempre': 'every one',
  'negocio.faltanteAvisoAyuda': "A till count, a handover or cash received that comes out this much short or more shows on the owner's home screen, with whose till it was.",
  'negocio.faltanteAvisoMonto': 'From how much (C$)',
  'negocio.faltanteAvisoHojaAyuda': 'Empty or zero: every one.',
  'negocio.reconteo': 'Ask for a recount',
  'negocio.reconteoDesde': 'when off by {monto} or more',
  'negocio.reconteoSiempre': 'when off at all',
  'negocio.reconteoAyuda':
    'A count of the drawer (at the start, at the end or at a handover) this far off or more is asked again before it is taken, without saying by how much or which way: it is nearly always a note miscounted. The second stands, and you see both. In dollars, any bill.',
  'negocio.reconteoMonto': 'From how much (C$)',
  'negocio.reconteoHojaAyuda': 'Empty or zero: any difference asks for another count.',
  'negocio.grupoPedidos': 'Orders',
  'negocio.apartarPedidos': 'Set aside the goods on orders',
  'negocio.apartarPedidosAyuda':
    "An order sets aside what it takes, and one asking for more than is left unreserved is not saved. Orders are saved only with a connection, so two phones never promise the same goods, and the till asks for the owner's PIN to sell what is set aside. Before turning it on, update the app on every phone.",
  'negocio.venderSoloLoQueHay': 'The till sells only what is there',
  'negocio.venderSoloLoQueHayAyuda':
    "Anything past what is free (the stock less what orders set aside) takes the owner's yes, and with a connection every sale waits for the server's yes, so two tills never sell the last one. With no connection the till keeps selling, with a warning. Every product needs its stock in the books. Before turning it on, update the app on every phone.",
  'negocio.grupoSeguridad': 'Security',
  'negocio.equipoCallado': 'Warn about a device not connecting after',
  'negocio.equipoCalladoAyuda': "A phone that does not connect holds sales and voids nobody has seen, and loses them if it breaks. The owner's home screen says so.",
  'negocio.horas': '{n} hours',
  'negocio.unDia': '1 day',
  'negocio.bloqueo': 'Lock the screen after',
  'negocio.bloqueoAyuda': 'That long without anyone touching it, and it asks for the PIN of whoever was using it. «{boton}» on the home screen locks it at once.',
  'negocio.bloqueoPractica': 'The practice shop never locks by itself; the real one does.',
  'negocio.bloqueoNombre': 'Screen lock time',
  'negocio.unMinuto': '1 minute',
  'negocio.minutos': '{n} minutes',
  'negocio.unaHora': '1 hour',
  'negocio.anularConDueno': "Voids and refunds need the owner's PIN",
  'negocio.anularConDuenoAyuda': "In the open shift too: when voiding a sale or a payment, and when giving money back. Off, in the open shift everyone confirms with their own PIN. Anything from a shift already closed always needs the owner's.",
  'negocio.grupoVencimientos': 'Expiry',
  'negocio.grupoPlanilla': 'Payroll',
  'negocio.vacaciones': 'Holidays accrue',
  'negocio.vacacionesAyuda':
    "Each month, 2.5 days and a twelfth of the pay of whoever has benefits (Art. 76 of the Labour Code). Off, no run sets holidays aside and no days are earned, and they don't show in «Payroll»; what was already set aside is still owed. Aguinaldo and severance stay as they are.",
  'negocio.vencimientoAlerta': 'Warn before it expires',
  'negocio.vencimientoAlertaAyuda': 'This far ahead, a lot starts to show under {pantalla} and on the home screen.',
  'negocio.dias': '{n} days',

  // Bank accounts: the shop's, in Settings, and which one each payment uses
  'bancos.grupo': 'Bank accounts',
  'bancos.grupoAyuda': 'Each one is reconciled with its own statement.',
  'bancos.cual': 'Bank account',
  'bancos.enCordobas': 'In córdobas',
  'bancos.enDolares': 'In dollars',
  'bancos.cerrada': 'closed',
  'bancos.agregar': 'Add an account',
  'bancos.nueva': 'New account',
  'bancos.nombre': 'Name',
  'bancos.nombreAyuda': 'What the shop calls it: the bank and the currency, like “Banpro córdobas” or “US account”.',
  'bancos.moneda': 'Currency',
  'bancos.monedaAyuda': 'The currency the bank keeps the account in. It cannot be changed later.',
  'bancos.cordobas': 'Córdobas (C$)',
  'bancos.dolares': 'Dollars (US$)',
  'bancos.faltaNombre': 'Give it a name.',
  'bancos.nombreRepetido': 'There is already an account with that name.',
  'bancos.yaHayCinco': 'There are already five accounts in that currency, the most the app keeps.',
  'bancos.tiene': 'By the books it holds {monto}.',
  'bancos.abierta': 'Open',
  'bancos.abiertaAyuda': 'Closed, it is no longer offered for paying or getting paid, and what it moved stays on the books.',
  'bancos.noSeCierraPredeterminada': 'Customers’ transfers or card payments go into this account: change that first.',
  'bancos.noSeCierraConSaldo': 'By the books it still holds money: move it to another account, or reconcile it with the statement, before closing it.',
  'bancos.transferencias': 'Customers’ transfers go into',
  'bancos.transferenciasAyuda': 'The account the shop gives customers to transfer to. It can be changed when confirming each transfer.',
  'bancos.tarjeta': 'Card payments go into',
  'bancos.tarjetaAyuda': 'The account the bank’s card terminal pays into.',
  'bancos.unaSola': 'Customers’ transfers and card payments go into “{cuenta}”.',
  'bancos.deA': 'From {desde} to {hacia}',
  'bancos.pagaDe': 'Paid from',
  'bancos.entraA': 'Goes into',
  'bancos.saleDe': 'Comes out of',
  'ventas.duenoParaAnular': "Voiding {ticket} needs the owner's PIN. It goes on record under {nombre}.",
  'devolucion.pinDueno': "Refunding {monto} from sale {ticket} needs the owner's PIN. It goes on record under {nombre}.",
  'devolucion.pinDuenoOtroTurno': "Sale {ticket} is from before this shift: refunding {monto} needs the owner's PIN. It goes on record under {nombre}.",
  'ventas.duenoOtroTurno': "Sale {ticket} is from before this shift: voiding it needs the owner's PIN. It goes on record under {nombre}.",
  'abono.duenoOtroTurno': "The {monto} payment from {cliente} is from before this shift: voiding it needs the owner's PIN. It goes on record under {nombre}.",
  'abono.duenoParaAnular': "Voiding the {monto} payment from {cliente} needs the owner's PIN. It goes on record under {nombre}.",

  // Efectivo: lo que sale de las gavetas, recibido y contado
  'efectivo.titulo': 'Cash',
  'efectivo.subtitulo': 'What the tills hand over, the safe, the bank and the wallet',
  'efectivo.porRecibir': 'To receive',
  'efectivo.porRecibirAyuda': "What a till handed over with nobody there to take it. Count it as you receive it: anything missing is that till's.",
  'efectivo.nadaPorRecibir': 'Nothing to receive: everything that left the drawers has been counted.',
  'efectivo.cajaDe': "{nombre}'s till, {fecha}",
  'efectivo.turnoAbierto': 'Shift open',
  'efectivo.turnoCerrado': 'Shift closed',
  'efectivo.general': 'General cash',
  'efectivo.generalAyuda': 'The safe: what leaves the drawers, and what cash payments come out of.',
  'efectivo.segunLibros': 'Per the books',
  'efectivo.segunLibrosAl': 'Per the books, on that date',
  'efectivo.contar': 'Count general cash',
  'efectivo.bancoBilletera': 'Bank and wallet',
  'efectivo.bancoBilleteraAyuda': "Check them against the statement: any difference is recorded on the statement's date.",
  'efectivo.banco': 'Bank',
  'efectivo.billetera': 'Mobile wallet',
  'efectivo.bancoDolares': 'Dollar bank account',
  'efectivo.conciliar': 'Reconcile',
  'efectivo.cuadra': 'It balances',
  'efectivo.faltan': '{monto} short',
  'efectivo.sobran': '{monto} over',
  'efectivo.noSeGuardo': 'It could not be saved.',
  'efectivo.yaRecibida': 'That till has nothing left to receive: it may have been received on another device.',
  'efectivo.cambioLoEntregado': 'What that till handed over changed while you were counting. Count it again.',
  'efectivo.recibido': 'Received',
  'efectivo.entrego': 'Handed over',
  'efectivo.contado': 'Counted',
  'efectivo.faltanteDeCaja': "It is recorded as a shortage of {nombre}'s till.",
  'efectivo.recibirDe': 'Receive from {nombre}',
  'efectivo.recibir': 'Receive',
  'efectivo.recibirAyuda': 'Count what you were handed before seeing what it should be, as at the till count.',
  'efectivo.guardarArqueo': 'Save the count',
  'efectivo.arqueoAyuda': 'Count all the cash that is not in a drawer. Dollars are valued at the official rate. Any difference is recorded as over or short.',
  'efectivo.arqueoPrimera':
    "This is the first time the safe is counted: whatever it holds is the balance it starts with, the owner's. The difference with the books is neither a surplus nor a shortage.",
  'efectivo.arqueoPorRecibir': '{monto} from the tills is still to be received. Receive it before counting, or count without that money.',
  'efectivo.arqueoGuardado': 'Saved: the books now say what you counted.',
  'efectivo.conciliarBanco': 'Reconcile the bank',
  'efectivo.conciliarBilletera': 'Reconcile the wallet',
  'efectivo.conciliarDolares': 'Reconcile the dollar account',
  'efectivo.conciliarAyudaDolares':
    'Type the balance in dollars the statement shows, and its date. Whatever it has over or under is recorded at the official rate.',
  'efectivo.saldoEstadoDolares': 'Balance on the statement, in dollars',
  'efectivo.conciliarAyuda': 'Enter the balance the statement shows, and its date.',
  'efectivo.conciliarEnCola':
    '{n} payment(s) up to that date are waiting in the queue, for {monto}. If they show on the statement, confirm them first in “Unconfirmed transfers”: squared without them, the difference counts them, and confirming them later would put them in again.',
  'efectivo.verLaCola': 'See the transfer queue',
  'efectivo.fechaEstado': 'Statement date',
  'efectivo.saldoEstado': 'Statement balance',
  'efectivo.conciliado': 'Reconciled as of {fecha}: the books now show that balance.',
  'inicio.porRecibir': '{n} till(s) handed over cash not yet received',
  'inicio.rutaSinLiquidar': 'A route from before is still open ({camion}, since {fecha}) on “{equipo}”: it is settled on that phone, with your PIN.',
  'inicio.rechazosSinRevisar': '{n} record(s) the server refused, not reviewed',
  'inicio.equiposCallados': '{n} device(s) not connected for over {dias} day(s)',
  'inicio.pinesDelDueno': "{n} wrong tries in a row at the owner's PIN in the last 24 hours",

  // Pagar de la gaveta
  'gaveta.sale': 'Paid from the drawer',
  'gaveta.saleAyuda': "Out of the cash in this device's open till ({monto} expected): the till count takes it off.",
  'gaveta.saleAyudaSinMonto': "Out of the cash in this device's open till: the till count takes it off.",
  'gaveta.noSaleAyuda': 'Out of general cash: the drawer is not touched.',
  // A till open on another device, for an expense (2026-10-16).
  'gaveta.saleAyudaDe': "Out of the cash in {quien}'s till on “{equipo}” ({monto} expected): that till's count takes it off.",
  'gaveta.saleAyudaDeSinMonto': "Out of the cash in {quien}'s till on “{equipo}”: that till's count takes it off.",
  'gaveta.otroEquipo': 'another device',
  'gaveta.deCual': 'Which till?',
  'gaveta.deEsteEquipo': '{caja} (this device)',
  'gaveta.noAlcanza': 'The drawer should hold {monto}: not enough. If it was paid from elsewhere, turn off “Paid from the drawer”.',
  'gaveta.noAlcanzaSinMonto': 'The drawer does not hold that much. If it was paid from elsewhere, turn off “Paid from the drawer”.',

  // Entregar la caja sin cerrarla
  'entrega.titulo': "Take over {nombre}'s till",
  'entrega.recibirDe': "Take over {nombre}'s till",
  'entrega.recibir': 'Take over the till',
  'entrega.recibida': 'Till taken over',
  'entrega.explicacion': "Count what is in the drawer without seeing what it should be. Anything missing or over stays on {nombre}'s shift, and the till goes on in your name, from what you counted.",
  'entrega.aSuNombre': 'It is all there. The till is now in your name.',
  'entrega.diferenciaDe': "The difference stays on {nombre}'s shift. The till is now in your name, from what you counted.",
  'entrega.noSeGuardo': 'It could not be saved.',
  'entrega.aviso': "This till is in {nombre}'s name. To serve, take it over: count the drawer.",
  'entrega.cajaDe': "The till is {nombre}'s",
  'entrega.paraVender': "What you sell would go into {nombre}'s till. Take it over first: the drawer is counted, and from then on the till is yours.",

  // Control por persona
  'control.titulo': 'Control',
  'control.subtitulo': 'What each person did with the money, side by side',
  'control.periodo': 'Period',
  'control.ultimosDias': 'Last {n} days',
  'control.nada': 'Nobody has done anything in these days.',
  'control.sinAutorizar': "Without the owner's authorisation",
  'control.sinAutorizarAyuda': "Sales the counter would have asked the owner about that went through without their PIN: from an old device, or from somebody who got round it.",
  'control.motivo.lista': 'another price list',
  'control.motivo.rapidas': 'too many quick sales',
  'control.motivo.margen': 'price below the minimum margin',
  'control.motivo.fiado': 'credit with no limit, or past it',
  'control.motivo.noEsDueno': 'authorised by somebody who is not an owner',
  'control.motivo.otroNombre': 'in the name of somebody who was not using the device',
  'control.faltantes': 'Till shortages',
  'control.faltantesAyuda': 'Cortes, handovers and cash received that came out short, in the name of whoever had the till.',
  'control.ventas': 'Sales',
  'control.anuladas': 'Voided',
  'control.devueltas': 'Returns',
  'control.rebajas': 'Prices brought down',
  'control.rapidas': 'Quick sales',
  'control.mermas': 'Write-offs',
  'control.caja': 'Till',
  'control.cajaCuadra': 'balances',
  'control.pinesMal': 'Wrong PINs',
  'control.conSiDelDueno': "With the owner's yes",
  'control.entradasDueno': 'Where the owner signed in',
  'control.entradasDuenoAyuda':
    "Every sign-in with the owner's PIN, device by device. If one wasn't you, somebody knows your PIN: change it in Settings.",
  'control.vecesUltima': '{n} sign-in(s) · last on {cuando}',
  // Lo que hizo una persona: su tarjeta de «Control», abierta (2026-10-23)
  'controlPersona.noExiste': "That person isn't in this shop.",
  'controlPersona.ver': 'What to show',
  'controlPersona.ver.todo': 'Everything',
  'controlPersona.ver.caja': 'Till',
  'controlPersona.ver.ventas': 'Sales',
  'controlPersona.ayuda':
    'Every opening, handover and count of a till, and every sale, void and return, newest first. Tap one to open it; a count done wrong is corrected right here.',
  'controlPersona.nada': 'Nothing in these days.',
  'controlPersona.nadaCaja': 'No till opened, handed over or counted in these days.',
  'controlPersona.nadaVentas': 'No sales, voids or returns in these days.',
  'controlPersona.abrio': 'Opened the till',
  'controlPersona.recibioDe': "Took over {nombre}'s till",
  'controlPersona.entregoA': 'Handed the till to {nombre}',
  'controlPersona.corte': 'Till count',
  'controlPersona.corteDe': "Closed {nombre}'s till",
  'controlPersona.cortePor': 'Till count, by {nombre}',
  'controlPersona.recibioLoDe': 'Received what {nombre} handed over',
  'controlPersona.recibidoPor': 'What they handed over, received by {nombre}',
  'controlPersona.conto': 'counted {monto}',
  'controlPersona.corregido': 'corrected',
  'controlPersona.lineas': '{n} line(s)',
  'controlPersona.anulo': 'Voided {ticket}',
  'controlPersona.devolucion': 'Return from {ticket}',
  'inicio.faltantesCaja': '{n} till shortage(s) these 7 days, the largest {monto} ({nombre})',
  'inicio.sinAutorizar': "{n} sale(s) without the owner's authorisation these 7 days",

  // Precios bajo el margen
  'margen.titulo': 'Prices below the margin',
  'margen.explicacion': 'The cost went up and these prices fell below the minimum margin ({minimo}): each sale earns less than you asked for, or loses. Raise them to the minimum with one tap, or leave them if it is on purpose.',
  'margen.todosBien': 'Every price is above the minimum margin.',
  'margen.subir': 'Raise',
  'margen.subirTodos': 'Raise all {n}',
  'margen.dejarlos': 'Leave them',
  'inicio.preciosBajoMargen': '{n} price(s) below the minimum margin',
  'inicio.negativos': '{n} product(s) with negative stock: they need counting',
  // --- «Cuadrar la semana» ---
  'cuadrar.titulo': 'Square up the week',
  'cuadrar.subtitulo': "The week's money, step by step",
  'cuadrar.explicacion':
    'From {desde} to {hasta}: the envelopes the tills left, what came out of the safe, the safe counted, the bank, and what the week left. Each step says what to do.',
  'cuadrar.cajasPorRecibir': '{n} envelope(s) from the tills to count.',
  'cuadrar.nadaPorRecibir': 'No envelopes from the tills to count.',
  'cuadrar.transferenciasPorVer': '{n} transfer(s) or wallet payment(s) are waiting for you to see them in the bank.',
  'cuadrar.nadaPorVer': 'No transfers to check.',
  'cuadrar.yDespues': 'Then: what came out of the safe, counting it, the bank, suppliers, and what the week left.',
  'cuadrar.empezar': 'Start',
  'cuadrar.contarCordobas': 'Count the córdobas {nombre} handed over ({fecha})',
  'cuadrar.contarCordobasExplicacion':
    "It is the money that left {nombre}'s till and that nobody has counted yet. Count it note by note: the system won't say how much there should be until the end.",
  'cuadrar.contarDolares': 'Are there dollars in what {nombre} handed over?',
  'cuadrar.contarDolaresExplicacion': 'Count the dollar bills apart. If there are none, tap “No dollars”.',
  'cuadrar.reviseRecibir':
    "If something is wrong, tap “Back” and fix it. Once received, the money goes into general cash, and anything short stays on {nombre}'s till.",
  'cuadrar.entregoMas': "While you were counting, {nombre}'s till handed over more money. Count it all again, together.",
  'cuadrar.recibir': 'Receive the money',
  'cuadrar.recibidoTitulo': 'Received from {nombre}',
  'cuadrar.recibidoExplicacion': 'This is what counting what {nombre} handed over found.',
  'cuadrar.yaRecibidoTitulo': "{nombre}'s envelope was already received",
  'cuadrar.yaRecibidoExplicacion':
    'While you were counting, it was received on another phone: there is nothing left to receive here, and nothing to put down against the till.',
  'cuadrar.entregoCordobas': 'Handed over in córdobas',
  'cuadrar.entregoDolares': 'Handed over in dollars',
  'cuadrar.faltanteDe': "What is short is written down on {nombre}'s till.",
  'cuadrar.transferenciaTitulo': 'Did this transfer arrive?',
  'cuadrar.transferenciaExplicacion':
    "Open the bank's app and look for this transfer by its reference and amount. A screenshot is not a payment: confirm it only if you see it in the bank.",
  'cuadrar.billeteraTitulo': 'Did this wallet payment arrive?',
  'cuadrar.billeteraExplicacion': 'Open the mobile wallet and look for this payment by its reference and amount. Confirm it only if you see it there.',
  'cuadrar.monto': 'Amount',
  'cuadrar.deLaVenta': 'From the sale',
  'cuadrar.deLaRecarga': 'From the top-up',
  'cuadrar.cliente': 'Client',
  'cuadrar.sinReferencia': 'No number',
  'cuadrar.fecha': 'Date',
  'cuadrar.referenciaAyuda': 'It came without a number: type the one the bank shows to confirm it.',
  'cuadrar.faltaReferencia': 'First type the number the bank shows.',
  'cuadrar.siLlego': 'Yes, it is in the bank',
  'cuadrar.siLlegoBilletera': 'Yes, it is in the wallet',
  'cuadrar.siLlegoDetalle': 'It is confirmed, and the money counts as received.',
  'cuadrar.todaviaNo': 'Not there yet',
  'cuadrar.todaviaNoDetalle': 'It stays pending, to check another day.',
  'cuadrar.noLlegoDetalle': 'The money never came: what happened is written down.',
  'cuadrar.confirmada': 'Confirmed: the money arrived.',
  'cuadrar.alFiado': "Moved to {nombre}'s account, as credit.",
  'cuadrar.perdida': 'Written down as a loss.',
  'cuadrar.noLlegoCliente':
    "The sale stands. What never came can go on {nombre}'s account, as credit, or be written down as a loss.",
  'cuadrar.noLlegoSinCliente': 'The sale stands, and what never came is written down as a loss.',
  'cuadrar.aFiadoDetalle': 'They owe it like any credit, and it shows on their statement.',
  'cuadrar.perdidaDetalle': 'Money that will not be collected: it is written down.',
  'cuadrar.faltaMotivo': 'First write what happened.',
  'cuadrar.gastosTitulo': "The week's expenses",
  'cuadrar.gastosExplicacion':
    'Before the safe is counted, everything that came out of it has to be written down, or it will come up short. Expenses first: did you pay anything that is not on the list?',
  'cuadrar.sinGastos': 'No expenses written down.',
  'cuadrar.anotarGasto': 'Yes, write down an expense',
  'cuadrar.anotarOtro': 'Write down another expense',
  'cuadrar.anotarDetalle': "A bill, something bought for the shop, a payment that isn't on the list.",
  'cuadrar.noPague': "I didn't pay anything",
  'cuadrar.esoEsTodo': "That's all",
  'cuadrar.depositoTitulo': 'Did you take money to the bank this week?',
  'cuadrar.depositoExplicacion':
    'What you took to the bank came out of the safe. Write down each deposit with its slip, if it is not on the list.',
  'cuadrar.siDeposite': 'Yes, I deposited',
  'cuadrar.otroDeposito': 'Write down another deposit',
  'cuadrar.siDepositeDetalle': 'Córdobas to the córdoba account, dollars to the dollar account.',
  'cuadrar.noDeposite': "I didn't deposit",
  'cuadrar.resumenTitulo': 'What the week left',
  'cuadrar.resumenExplicacion':
    'From {desde} to {hasta}: what was sold, what it cost, the expenses and what was left; how the customers paid, and what went to the bank.',
  'cuadrar.pendientesTitulo': 'What is still waiting',
  'cuadrar.pendientesExplicacion': "No need to do it now, but don't forget: each one takes you to where it is seen to.",
  'cuadrar.nadaPendiente': 'Nothing is left waiting now.',
  'cuadrar.sigueAbierta': "{nombre}'s till is still open: its cash is received when the shift ends.",
  'cuadrar.terminar': 'Finish',
  'cuadrar.listoTitulo': 'Done! The week is squared up',
  'cuadrar.listoExplicacion': 'The home screen asks for it again in a week. Anything left waiting stays where it is.',
  'cuadrar.verEfectivo': 'See the cash',
  'cuadrar.desdeEfectivo': 'The envelopes, the safe, the bank and suppliers, step by step',
  'cuadrar.inicioPrimera': "Go over the week's money: the envelopes, the safe and the bank",
  'cuadrar.inicioHaceDias': 'It has been {n} days since you went over the money',
  'cuadrar.resumenTituloDia': 'What the day left',
  'cuadrar.sinDepositos': 'No deposits written down.',
  'cuadrar.boleta': 'Slip {numero}',
  'cuadrar.noHuboMas': 'There was nothing else',
  'cuadrar.duenoTitulo': 'Did you take money for yourself?',
  'cuadrar.duenoExplicacion':
    'What you took from the safe for yourself is not a shop expense, but it came out of the safe. Write it down, if it is not on the list.',
  'cuadrar.paraUsted': 'For you',
  'cuadrar.sinRetiros': 'Nothing written down.',
  'cuadrar.siSaque': 'Yes, I took money',
  'cuadrar.otroRetiro': 'Write down another',
  'cuadrar.siSaqueDetalle': 'How much, in córdobas or dollars, and on what day.',
  'cuadrar.noSaque': "I didn't take any",
  'cuadrar.pagosTitulo': 'Did you pay any supplier?',
  'cuadrar.pagosExplicacion':
    'A cash payment to a supplier came out of the safe. Write it down, if it is not on the list: it also brings down what is owed to them.',
  'cuadrar.sinPagos': 'No payments written down.',
  'cuadrar.anotarPago': 'Write down a payment',
  'cuadrar.anotarPagoDetalle': 'To whom, how much, and how you paid.',
  'cuadrar.noPagueProveedor': "I didn't pay anyone",
  'cuadrar.proveedoresTitulo': 'What has to be paid to suppliers',
  'cuadrar.proveedoresExplicacion':
    'What is overdue and what falls due in the next seven days. You can pay it now, or when it is due.',
  'cuadrar.vencio': 'was due {fecha}',
  'cuadrar.pagarUno': 'Pay one now',
  'cuadrar.pagarDespues': 'Go on',
  'cuadrar.nadaPronto': 'Nothing else falls due this week.',
  'sobres.titulo': 'Do you have the envelopes at hand?',
  'sobres.explicacion':
    'Each till that ended its shift left an envelope with the money for you. Tap any you do not have now: it waits for next time. The rest are counted all together, without knowing how much there should be.',
  'sobres.de': '{nombre}, {fecha}',
  'sobres.loTengo': 'I have it',
  'sobres.noLoTengo': "I don't have it: it waits",
  'sobres.yaRecibido': 'Received on another phone',
  'sobres.contar': 'Count {n} envelope(s)',
  'sobres.ninguno': 'Go on without counting them',
  'sobres.contarCordobas': 'Count the córdobas in {n} envelope(s), all together',
  'sobres.contarCordobasExplicacion':
    'Open the envelopes and put the money together. Note by note, how many of each; the coins as one total. Without knowing how much there should be: that way you count what is there.',
  'sobres.entregaronMas': 'While you were counting, a till handed over more money. Count it all again, together.',
  'sobres.recibidoEnOtro': 'While you were counting, an envelope was received on another phone. Count the ones left again, all together.',
  'sobres.contarDolares': 'Are there dollars in the envelopes?',
  'sobres.revise':
    'What you counted from {n} envelope(s). If it matches what the tills handed over, it is received; if not, each envelope is counted on its own.',
  'sobres.cuadranTitulo': '{n} envelope(s) received: it all balances',
  'sobres.cuadranExplicacion':
    'The total is what the tills handed over. The money is general cash now: put it in the safe.',
  'sobres.cadaTitulo': "The total doesn't balance: count each envelope on its own",
  'sobres.cadaExplicacion':
    'That way you know which till the difference belongs to. One at a time, as before: without knowing how much there should be.',
  'fuerte.contarCordobas': 'Count the córdobas in the safe',
  'fuerte.contarExplicacion':
    'Everything there is, the envelopes included. Note by note, how many of each; the coins as one total. Without knowing how much there should be.',
  'fuerte.primeraVez':
    'This is the first time it is counted here: whatever it holds is the balance the safe starts with. Count all of it, the envelopes included: note by note, and the coins as one total.',
  'fuerte.contarDolares': 'Are there dollars in the safe?',
  'fuerte.revise':
    'What you counted in the safe. It is held against the books: if something is off, it is counted once more.',
  'fuerte.reviseInicial': 'What you counted in the safe. From today, this is what it holds: the books start here.',
  'fuerte.guardar': 'Save the count',
  'fuerte.empiezaTitulo': 'The safe starts with this',
  'fuerte.empiezaExplicacion':
    "What you counted is general cash's opening balance, the owner's. From the next count on, anything short or over is written down as a shortage or a surplus.",
  'fuerte.resultadoTitulo': 'The safe, counted',
  'fuerte.resultadoExplicacion': 'What you counted, against everything the week wrote down as coming in and going out.',
  'fuerte.enTotal': 'In all, at the official rate',
  'fuerte.segunLibros': 'According to the books',
  'fuerte.cuadra': 'The safe balances exactly.',
  'fuerte.sobranteAnotado': 'What is over is written down as a surplus of general cash.',
  'fuerte.faltanteAnotado': "What is short is written down as a shortage of general cash: it is no till's.",
  'estado.banco': 'What does the bank say?',
  'estado.dolares': 'What does the dollar account say?',
  'estado.billetera': 'What does the mobile wallet say?',
  'estado.cuentaNombrada': 'What does “{cuenta}” say?',
  'estado.bancoExplicacion':
    "Look up today's balance in the bank's app or statement, and type it as it reads. Then it is held against the books.",
  'estado.billeteraExplicacion':
    "Look up today's balance in the wallet, and type it as it reads. Then it is held against the books.",
  'estado.saldoCordobas': 'Balance, in córdobas',
  'estado.saldoDolares': 'Balance, in dollars',
  'estado.sinEstado': "I don't have it at hand now",
  'estado.comparar': 'Compare',
  'estado.resultadoTitulo': 'Held against the books',
  'estado.segunEstado': 'According to the statement',
  'estado.segunLibros': 'According to the books',
  'estado.cuadra': 'It matches the books.',
  'estado.deMas': 'The statement has more',
  'estado.deMenos': 'The statement has less',
  'estado.diferenciaAnotada':
    'The difference is written down as a reconciliation difference: a bank charge, interest, or something not written down.',
  'duenoPaso.monto': 'How much did you take for yourself?',
  'duenoPaso.montoExplicacion':
    'It is not a shop expense: it lowers what you have in the business, not the profit. In córdobas, dollars, or both.',
  'duenoPaso.fecha': 'On what day?',
  'duenoPaso.motivo': 'What for? (if you like)',
  'duenoPaso.motivoAyuda': 'To remember it later. It changes nothing on the books.',
  'duenoPaso.reviseExplicacion': "It comes out of the safe and is written down as the owner's withdrawal.",
  'duenoPaso.guardar': 'Save',
  'pagoPaso.deuda': 'Whom did you pay?',
  'pagoPaso.deudaExplicacion': 'What is owed to each supplier, purchase by purchase. Tap the one you paid.',
  'pagoPaso.nadaPorPagar': 'Nothing is owed to any supplier.',
  'pagoPaso.debe': 'owing {monto}',
  'pagoPaso.vence': 'due {fecha}',
  'pagoPaso.monto': 'How much did you pay {nombre}?',
  'pagoPaso.montoExplicacion': '{monto} is owed. If you paid part of it, type that part: the rest stays owed.',
  'pagoPaso.montoInvalido': 'An amount above zero and no more than is owed.',
  'pagoPaso.fecha': 'On what day did you pay?',
  'pagoPaso.forma': 'How did you pay?',
  'pagoPaso.formaExplicacion':
    'In cash it comes out of the safe; by transfer or wallet, out of the bank or the wallet.',
  'pagoPaso.efectivo': 'In cash, from the safe',
  'pagoPaso.efectivoDetalle': 'In córdobas, from the safe.',
  'pagoPaso.reviseExplicacion': 'It brings down what is owed to the supplier, and comes out of where you paid it from.',
  'pagoPaso.de': 'For',
  'pagoPaso.cuanto': 'How much',
  'pagoPaso.guardar': 'Save the payment',
  'dejo.titulo': 'What it left',
  'dejo.ingresos': 'Sold and other income',
  'dejo.costo': 'What what was sold cost',
  'dejo.ganancia': 'Profit on what was sold ({margen})',
  'dejo.gananciaSinMargen': 'Profit on what was sold',
  'dejo.gastos': 'Expenses',
  'dejo.quedo': 'Left over',
  'dejo.perdio': 'Lost',
  'dejo.cobrado': 'How they paid ({n} sale(s))',
  'dejo.sinVentas': 'There were no sales.',
  'dejo.alBanco': 'To the bank',

  // --- Cash taken to the bank ---
  'deposito.monto': 'How much did you deposit?',
  'deposito.montoExplicacion':
    "What the bank's deposit slip says. If you deposited only córdobas or only dollars, leave the other one empty.",
  'deposito.cordobas': 'Córdobas',
  'deposito.cordobasAyuda': 'They go to the córdoba account.',
  'deposito.dolares': 'Dollars',
  'deposito.dolaresAyuda': 'They go to the dollar account.',
  'deposito.boleta': "The slip's number",
  'deposito.boletaExplicacion':
    "It is printed on the slip, and finds the deposit on the bank statement. If you don't have it, leave it empty.",
  'deposito.numeroBoleta': 'Slip number',
  'deposito.fecha': 'Date of the deposit',
  'mover.titulo': 'Move money',
  'mover.sentido': 'From where to where?',
  'mover.alBanco': 'Cash to the bank',
  'mover.delBanco': 'Bank to cash',
  'mover.enEfectivo': 'General cash, by the books',
  'mover.enBanco': 'Bank, by the books',
  'mover.enBancoDolares': 'Dollar account, by the books',
  'mover.cordobasDelBanco': 'They come out of the córdoba account.',
  'mover.dolaresDelBanco': 'They come out of the dollar account.',
  'mover.referenciaDelBanco': 'Withdrawal or cheque number',
  'mover.fecha': 'Date',
  'mover.nota': 'Note (optional)',
  'mover.guardarAlBanco': 'Save the deposit',
  'mover.guardarDelBanco': 'Save the withdrawal',
  'mover.explicacionAlBanco': 'It leaves general cash (the safe) and goes into the bank, on the day of the slip. It stays in “Cash” with the other deposits, where it is voided if it was never made.',
  'mover.explicacionDelBanco': 'It leaves the bank and goes into general cash (the safe), on the day it was taken out. It stays in “Cash”, where it is voided if it was never made.',
  'mover.entreCuentas': 'Between accounts',
  'mover.enCuenta': '“{cuenta}”, by the books',
  'mover.cordobasA': 'The córdobas go to',
  'mover.cordobasDe': 'The córdobas come out of',
  'mover.dolaresA': 'The dollars go to',
  'mover.dolaresDe': 'The dollars come out of',
  'mover.desde': 'Out of',
  'mover.hacia': 'Into',
  'mover.saleCordobas': 'What went out, in córdobas',
  'mover.saleDolares': 'What went out, in dollars',
  'mover.llegaCordobas': 'What arrived, in córdobas',
  'mover.llegaDolares': 'What arrived, in dollars',
  'mover.recibidoAyuda': 'It changes currency: what the bank put in the other account, as the receipt says.',
  'mover.tasaDelBanco': 'The bank changed it at C$ {tasa} to the dollar; the official rate is C$ {oficial}.',
  'mover.referenciaEntre': 'Transfer number',
  'mover.guardarEntre': 'Save the transfer',
  'mover.unaSolaCuenta': 'The shop has only one open bank account. To move money between accounts, add another in “Shop settings”.',
  'mover.explicacionEntre':
    'It leaves one account and goes into the other, on the day of the transfer. If it changes currency, the gap between the bank’s rate and the official one is an exchange gain or loss. It stays in “Cash”, where it is voided if it never happened.',
  'aporte.titulo': "Owner's contribution",
  'aporte.motivo': 'What for (optional)',
  'aporte.guardar': 'Save the contribution',
  'aporte.explicacion': 'Money the owner puts into the business raises its capital. It is not a sale or a loan, and it does not change the profit. It stays under “Expenses and payments”, “Owner” tab, where it is voided if it was never made.',
  'deposito.reviseExplicacion':
    'If something is wrong, tap “Back” and fix it. Once saved, the money leaves general cash and goes into the bank.',
  'deposito.alBancoCordobas': 'To the córdoba account',
  'deposito.alBancoDolares': 'To the dollar account',
  'deposito.guardar': 'Save the deposit',

  // --- An expense, a step at a time ---
  'gastoPaso.categoria': 'What did the money go on?',
  'gastoPaso.categoriaExplicacion': "Pick the closest one. It tells you later where the shop's money goes.",
  'gastoPaso.monto': 'How much did you pay?',
  'gastoPaso.montoExplicacion': 'What the receipt says. If you paid in dollars, tap “Dollars” first.',
  'gastoPaso.descripcion': 'What was it, exactly?',
  'gastoPaso.descripcionExplicacion': 'A few words, to recognise it later on the list.',
  'gastoPaso.pago': 'How did you pay it?',
  'gastoPaso.pagoExplicacion': "Where the money came from. If you haven't paid it yet, it is “I still owe it”.",
  'gastoPaso.efectivo': 'In cash',
  'gastoPaso.efectivoDetalle': 'It comes out of general cash, the safe.',
  'gastoPaso.efectivoGaveta': 'In cash, from the drawer',
  'gastoPaso.efectivoGavetaDetalle': "It comes out of the till open on this device: the corte won't expect it.",
  'gastoPaso.transferencia': 'By transfer',
  'gastoPaso.transferenciaDetalle': 'It comes out of the bank account.',
  'gastoPaso.billetera': 'With the mobile wallet',
  'gastoPaso.billeteraDetalle': 'It comes out of the wallet.',
  'gastoPaso.credito': 'I still owe it',
  'gastoPaso.creditoDetalle': 'It goes on accounts payable, to pay later.',
  'gastoPaso.deLaGaveta': 'In cash, from the drawer',
  'gastoPaso.delGeneral': 'In cash, from general cash',
  'gastoPaso.proveedor': 'Who do you owe it to?',
  'gastoPaso.proveedorExplicacion': 'If it is a supplier on the list, pick them: it then shows with what you owe them.',
  'gastoPaso.referencia': "The transfer's number",
  'gastoPaso.referenciaExplicacion': "It is on the receipt. If you don't have it, leave it empty.",
  'gastoPaso.foto': 'Do you have the receipt?',
  'gastoPaso.fotoExplicacion':
    "Take a photo of it: it stays with the expense, to know later what the money went on. If you don't have it, go on.",
  'gastoPaso.reviseExplicacion': 'If something is wrong, tap “Back” and fix it.',
  'gastoPaso.cuanto': 'Amount',
  'gastoPaso.fecha': 'On what day did you pay it?',
  'gastoPaso.fechaFutura': 'It cannot be a day still to come.',
  'gastoPaso.conFoto': 'Yes',
  'gastoPaso.sinFoto': 'No',
  // What was forgotten, put right: a till from another day, a shortage explained, what was written down wrongly voided.
  'inicio.cajaDeOtroDia': 'This till has been open since {fecha}: do its corte with «End my shift» before selling on.',
  'inicio.cajaAbiertaDesde': "{nombre}'s till on {equipo} has been open since {fecha}: its corte is still to do.",
  'explicar.boton': 'Explain',
  'explicar.botonFaltante': 'Explain what was missing',
  'explicar.titulo': 'Explain what was missing',
  'explicar.conteo.corte': 'At the corte on {fecha}',
  'explicar.conteo.entrega': 'When the till was handed over on {fecha}',
  'explicar.conteo.apertura': 'When the till was opened on {fecha}',
  'explicar.conteo.recibido': 'When the cash the till handed over was received on {fecha}',
  'explicar.conteo.arqueo': 'When general cash was counted on {fecha}',
  'explicar.conteo.otro': 'At the count on {fecha}',
  'explicar.falto': '{conteo}, {monto} was missing.',
  'explicar.faltoA': "{conteo}, {monto} was missing, on {nombre}'s account.",
  'explicar.yaExplicado': 'Already explained: {monto}, {descripcion}',
  'explicar.queda': 'Still to explain: {monto}',
  'explicar.ayuda':
    "Sometimes what is missing was an expense nobody wrote down: petrol for the motorbike, ice, the electricity bill. Explained here, it becomes that expense, on the day of the count, and no longer counts as missing. No money moves: the count already took it out.",
  'explicar.monto': 'How much was it?',
  'explicar.hasta': 'Up to {monto}',
  'explicar.demasiado': 'That is more than is left to explain: {monto}',
  'explicar.descripcion': 'What did it go on?',
  'explicar.descripcionEjemplo': 'Petrol for the motorbike',
  'explicar.guardar': 'Explain',
  'explicar.pin': 'Explain {monto} of what was missing: “{descripcion}”.',
  'explicar.listo': 'Done: {monto} is now {categoria}, and no longer counts as missing.',
  'explicar.nada': 'What was missing at that count is already explained.',
  'explicar.noSeExplico': 'It could not be explained: {motivo}',
  'explicar.explicado': '{monto} already explained',
  'anular.porQue': 'Why is it being voided?',
  'anular.noSePuede': 'It can no longer be voided: {motivo}.',
  'anular.noSeAnulo': 'It could not be voided: {motivo}',
  'anular.pin': '{titulo}.',
  'anular.listo': 'Voided: the money is back where it was.',
  'anular.deposito': 'Void the deposit',
  'anular.depositoDel': 'Deposit of {fecha}',
  'anular.boleta': 'Slip {referencia}',
  'anular.efectoDeposito':
    'For a deposit that was never made, or was written down twice: the money goes back to general cash, on the day of the deposit.',
  'anular.sacado': 'Void the bank withdrawal',
  'anular.sacadoDel': 'Withdrawn from the bank on {fecha}',
  'anular.efectoSacado': 'For a withdrawal that was never made, or was written down twice: the money goes back to the bank, on the day it was taken out.',
  'anular.transferenciaBanco': 'Void the transfer between accounts',
  'anular.efectoTransferenciaBanco':
    'For a transfer that never happened, or was written down twice: the money goes back to the account it left, on the day of the transfer.',
  'anular.retiro': "Void the owner's drawing",
  'anular.aporte': "Void the owner's contribution",
  'anular.efectoRetiro':
    'For a drawing that never happened, or was written down twice: the money goes back where it came from, on the day of the drawing.',
  'anular.efectoAporte':
    'For a contribution that never happened, or was written down twice: the money leaves where it went in and goes back to the owner, on the day of the contribution.',
  'anular.pago': 'Void the payment',
  'anular.pagoA': 'Payment to {nombre} · {fecha}',
  'anular.efectoPago':
    'For a payment that was never made, or was written down twice: the money goes back where it came from, and the supplier is owed it again.',
  'anular.efectoPagoDeGaveta':
    'For a payment that was never made, or was written down twice: the money goes back into the drawer, the corte expects it, and the supplier is owed it again.',
  'anular.gasto': 'Void the expense',
  'anular.efectoExplicacion': 'What it explained is missing at that count again, and can be explained again.',
  'anular.efectoGasto':
    'For an expense that never happened, or was written down twice: it no longer counts as an expense, and the money goes back where it came from.',
  'efectivo.depositos': 'Deposits',
  'efectivo.depositosAyuda':
    'What was taken to the bank. One that was never made is voided here, before the safe is counted again or the bank is checked against its statement.',
  'efectivo.delBanco': 'Withdrawn from the bank',
  'efectivo.delBancoAyuda': 'What was brought from the bank to general cash. One that was never made is voided here, before the safe is counted again or the bank is checked against its statement.',
  'efectivo.entreCuentas': 'Between accounts',
  'efectivo.entreCuentasAyuda': 'What was moved from one bank account to another. One that never happened is voided here, before checking the accounts against their statements.',
  'efectivo.faltantes': 'What was missing at a count',
  'efectivo.faltantesAyuda':
    'Counts of general cash that came out short. If it was an expense nobody wrote down, explain it: it no longer counts as missing.',
  'gastos.pagosAProveedores': 'Payments to suppliers',
  'gastos.pagosAProveedoresAyuda': 'The latest. One that was never made, or was written down twice, is voided here.',
  'gastos.deFaltante': ' · missing at a count',
  'gastos.explicaFaltante': 'It explains what was missing at the count on {fecha}. It moved no money: the count had already taken it out.',
  'gastos.formaFaltante': 'Missing at a count',
  'gastos.explicacionNoSeCorrige': 'An explanation is not corrected: void it, and explain what was missing again.',
  'gastos.anularExplicacion': 'Void this explanation',

  // --- Searching the history: sales and orders (lib/historial.ts) ---
  'historial.buscarVentas': 'Ticket, client or product',
  'historial.buscarPedidos': 'Client, product or note',
  'historial.buscarCompras': 'Supplier, invoice or product',
  'historial.quitar': 'Remove “{filtro}”',
  'historial.quitarTodos': 'Clear the filters',
  'historial.periodo': 'Dates',
  'historial.periodo.todo': 'All time',
  'historial.periodo.hoy': 'Today',
  'historial.periodo.ayer': 'Yesterday',
  'historial.periodo.mes': 'This month',
  'historial.periodo.mesPasado': 'Last month',
  'historial.periodo.elegir': 'Pick dates',
  'historial.cliente': 'Client',
  'historial.todos': 'Anyone',
  'historial.forma': 'Payment',
  'historial.todas': 'Any',
  'historial.estado': 'Status',
  'historial.estado.todas': 'Any',
  'historial.estado.validas': 'Not voided',
  'historial.estado.anuladas': 'Voided',
  'historial.estado.conDevolucion': 'With a return',
  'historial.estado.porConfirmar': 'Transfer not confirmed',
  'historial.estado.porPagar': 'Still owed',
  'historial.tipo': 'Where from',
  'historial.tipo.todas': 'Anywhere',
  'historial.tipo.mostrador': 'At the counter',
  'historial.tipo.pedido': 'Orders delivered',
  'historial.tipo.conEnvio': 'With a delivery cost',
  'historial.tipo.rapida': 'With a quick sale',
  'historial.quien': 'Charged by',
  'historial.cobro': 'Charged by {nombre}',
  'historial.conEnvio': 'Only those that paid a delivery cost',
  'historial.porEntrega': 'By the day it is due.',
  'historial.porCobro': 'By the day it was charged.',
  'historial.encontradas': '{n} found · {total}',
  'historial.encontrados': '{n} found · {total}',
  'historial.ninguna': 'No sale matches what you are looking for.',
  'historial.ninguno': 'No order matches what you are looking for.',
  'historial.ningunaCompra': 'No purchase matches what you are looking for.',
  'historial.quitarBusqueda': 'Clear the search and the filters',
  'historial.calendario.anterior': 'Previous month',
  'historial.calendario.siguiente': 'Next month',
  'historial.calendario.primero': 'Tap the first day.',
  'historial.calendario.ultimo': 'From {dia}: tap the last day, or «Done» for that day alone.',
  'historial.buscarProveedor': 'Find a supplier',
  'historial.buscarCliente': 'Find a client',
  'historial.ningunoSeLlamaAsi': 'Nobody by that name.',
  'historial.verTodos': 'See all',
  'historial.verTodas': 'See all',
  'historial.vigencia.todos': 'All',
  'historial.vigencia.vigentes': 'Not voided',
  'historial.vigencia.anulados': 'Voided',
  'pagosProv.registrados': '{n} payment(s)',
  'pagosProv.buscar': 'Supplier, invoice or receipt',
  'pagosProv.ninguno': 'No payment matches your search.',
  'pagosProv.vacio': 'Nothing has been paid to a supplier yet.',
  'pagosProv.factura': 'Invoice {documento}',
  'pagosProv.compraDel': 'Purchase of {fecha}',
  'pagosProv.deudaAnterior': 'Earlier debt',
  'duenoHist.registrados': '{n} movement(s)',
  'duenoHist.buscar': 'What it was for',
  'duenoHist.tipoTodos': 'All',
  'duenoHist.cuenta': 'Account',
  'duenoHist.resumen': '{n} found · took out {retiros} · put in {aportes}',
  'duenoHist.ninguno': 'No movement matches your search.',
  'duenoHist.vacio': 'The owner has not taken out or put in any money yet.',
  'turnosHist.registrados': '{n} closed shift(s)',
  'turnosHist.porApertura': 'By the day it was opened.',
  'turnosHist.deQuien': 'Whose',
  'turnosHist.buscarQuien': 'Find a staff member',
  'turnosHist.resultado': 'How it balanced',
  'turnosHist.resultado.todos': 'All',
  'turnosHist.resultado.cuadro': 'Balanced',
  'turnosHist.resultado.falto': 'Short',
  'turnosHist.resultado.sobro': 'Over',
  'turnosHist.resumen': '{n} found · sold {vendido}',
  'turnosHist.resumenDiferencia': '{n} found · sold {vendido} · at close {diferencia}',
  'turnosHist.ninguno': 'No shift matches your search.',
  'recargasHist.titulo': 'Past top-ups',
  'recargasHist.registradas': '{n} top-up(s)',
  'recargasHist.buscar': 'Phone number or reference',
  'recargasHist.estado.todas': 'All',
  'recargasHist.estado.confirmado': 'Paid',
  'recargasHist.estado.pendiente': 'Awaiting confirmation',
  'recargasHist.estado.rechazado': 'Payment never arrived',
  'recargasHist.resumen': '{n} found · sold {vendido} · commission {comision}',
  'recargasHist.ninguna': 'No top-up matches your search.',
  'recargasHist.vacio': 'No top-up has been sold yet.',
  'depositosHist.registrados': '{n} deposit(s)',
  'depositosHist.buscar': 'Slip number or note',
  'depositosHist.moneda': 'Currency',
  'depositosHist.moneda.todas': 'All',
  'depositosHist.moneda.cordobas': 'Córdobas',
  'depositosHist.moneda.dolares': 'Dollars',
  'depositosHist.ninguno': 'No deposit matches your search.',
  'depositosHist.vacio': 'Nothing has been taken to the bank yet.',
  'bitacora.buscar': 'What was done, who, a ticket or a reason',
  'bitacora.quien': 'Who did it',
  'bitacora.ninguno': 'No record matches your search.',
  // «Bitácora», one record opened (`lib/detalleEvento.ts`, `ui/DetalleEvento.tsx`).
  'registro.loHizo': 'Done by',
  'registro.seIntentoComo': 'Tried to get in as',
  'registro.cuando': 'When',
  'registro.equipo': 'Device',
  'registro.numero': 'Number on the server',
  'registro.soloAqui': 'Still only on this device: it has not reached the server.',
  'registro.rechazado': 'The books refused it: {motivo}',
  'registro.llegoDespues': 'Reached the server {cuanto} later: it was made with no signal.',
  'registro.reloj': "The device's clock was {cuanto} off.",
  'registro.loQueSeRegistro': 'What was recorded',
  'registro.nadaMas': 'This record holds nothing more.',
  'registro.despues': 'What happened after',
  'registro.libros': 'How the books took it',
  'registro.debe': 'Debit',
  'registro.haber': 'Credit',
  'registro.antes': 'Before: {valor}',
  'registro.noEstaAqui': 'Not on this device',
  'registro.ninguno': 'None',
  'registro.vacio': '(empty)',
  'registro.porUnidad': '{costo} per {unidad}',
  'registro.numeroN': 'No. {n}',
  'registro.conFoto': 'With a photo',
  'registro.cosas': '{n} in the list',
  'registro.personasN': '{n} person(s)',
  'registro.planillaAnulada': 'Voided: nobody left on it',
  'registro.pagoAnulado': 'Voided: nobody left on it',
  'registro.deLaGaveta': 'From the drawer',
  'registro.ventaRapida': 'quick sale',
  'registro.diferencia': 'Difference',
  'registro.falto': 'Short',
  'registro.sobro': 'Over',
  'registro.cuadro': 'None: it matched',
  'registro.corregido': 'As corrected',
  'registro.corregidoFalta': '{monto} short',
  'registro.corregidoSobra': '{monto} over',
  'registro.minutos': '{n} min',
  'registro.horas': '{n} h',
  'registro.dias': '{n} days',
  'registro.equivaleAlMes': 'Comes to a month',
  'detalle.enExistencia': 'In stock',
  // ---------- Análisis ----------
  'analisis.titulo': 'Analytics',
  'analisis.periodo': 'Period',
  'analisis.vistas': 'What to see',
  'analisis.enDolares': 'In dollars at the official rate, C$ {tasa} per dollar.',
  'analisis.tresMeses': 'Last 3 months',
  'analisis.ventas': 'Sales',
  'analisis.productos': 'Products',
  'analisis.clientes': 'Clients',
  'analisis.compras': 'Purchases',
  'analisis.gastos': 'Costs',
  'analisis.dinero': 'Money',
  'analisis.cargando': 'Working it out…',
  'analisis.comparadoCon': 'The arrows compare with {rango}.',
  'analisis.comparadoConDiaEntero': 'The arrows compare with all of {rango}: today is not over yet.',
  'analisis.sinAntes': 'No sales from {rango} to compare with.',
  'analisis.verMas': 'Show {n} more',
  'analisis.vendido': 'Sold',
  'analisis.utilidad': 'Gross profit',
  'analisis.margen': 'margin {pct}',
  'analisis.numVentas': 'Sales made',
  'analisis.ticket': 'Average sale',
  'analisis.porDia': 'Day by day',
  'analisis.porSemana': 'Week by week',
  'analisis.porMes': 'Month by month',
  'analisis.leyendaVendido': 'Sold',
  'analisis.leyendaUtilidad': 'Profit',
  'analisis.toqueColumna': 'Tap a column to see its figures.',
  'analisis.columna': '{fecha}: sold {vendido}, profit {utilidad}, {n} sale(s)',
  'analisis.promedioDia': 'Daily average: {monto}',
  'analisis.mejorDia': 'Best day: {fecha} ({monto})',
  'analisis.mejorSemana': 'Best week: {fecha} ({monto})',
  'analisis.mejorMes': 'Best month: {fecha} ({monto})',
  'analisis.sinVentas': 'No sales in this period.',
  'analisis.comoPagan': 'How they pay',
  'analisis.cuandoSeVende': 'When it sells',
  'analisis.cuandoAyuda': 'Sales by day and hour: the brighter the square, the more sales.',
  'analisis.horaPico': 'Busiest: {dia}, {desde} to {hasta}.',
  'analisis.masDeja': 'What makes the most',
  'analisis.masDejaAyuda': 'The products that left the most profit in the period.',
  'analisis.vendidoMargen': 'Sold {monto} · margin {pct}',
  'analisis.concentracion': '{n} product(s) make 80% of the profit, out of {total} sold.',
  'analisis.bajoMargen': 'Below the minimum margin ({pct})',
  'analisis.bajoMargenAyuda': 'Sold at less than the minimum margin in Shop settings. Losses first.',
  'analisis.todosSobreMargen': 'Everything sold above the minimum margin.',
  'analisis.vendidoVeces': 'Sold {monto} in {n} sale(s)',
  'analisis.aPerdida': 'at a loss',
  'analisis.parada': 'Stock not moving',
  'analisis.paradaAyuda': 'In stock, and neither sold nor bought in 30 days.',
  'analisis.paradaTotal': '{monto} tied up in {n} product(s), at cost.',
  'analisis.nadaParado': 'Nothing has gone 30 days without moving.',
  'analisis.ultimaVenta': 'last sold {fecha}',
  'analisis.masDe90': 'not sold in over 90 days',
  'analisis.nuncaVendido': 'never sold',
  'analisis.fiado': 'Credit by age',
  'analisis.fiadoAyuda': 'What is owed today, by how long ago it was given. Payments clear the oldest first.',
  'analisis.fiadoTotal': '{n} client(s) owe {monto}.',
  'analisis.tramo30': 'Up to 30 days',
  'analisis.tramo60': '31 to 60 days',
  'analisis.tramo90': '61 to 90 days',
  'analisis.tramoMas': 'Over 90 days',
  'analisis.nClientes': '{n} client(s)',
  'analisis.debenHace': 'Owing the longest',
  'analisis.haceDias': 'Oldest from {n} day(s) ago',
  'analisis.sinFiado': 'Nobody owes anything.',
  'porCobrar.titulo': 'Credit',
  'porCobrar.ordenAntiguo': 'Oldest first',
  'porCobrar.ordenMonto': 'Largest first',
  'porCobrar.ultimoAbono': 'Last paid {fecha}',
  'porCobrar.nuncaAbono': 'Has not paid anything yet',
  'porCobrar.deHoy': 'All of it from today',
  'porCobrar.limite': 'Limit: {monto}',
  'porCobrar.pasoLimite': 'Over their {monto} limit',
  'porCobrar.inactivo': 'Deactivated',
  'porCobrar.nadieAsi': 'Nobody who owes matches that.',
  'analisis.mejoresClientes': 'Best clients',
  'analisis.mejoresAyuda': 'Who bought the most in the period. Sales with no client are left out.',
  'analisis.comprasUtilidad': '{n} purchase(s) · profit {monto}',
  'analisis.sinClientes': 'No sale in this period has a client.',
  'analisis.seFueron': 'Stopped coming',
  'analisis.seFueronAyuda': 'Used to buy often and have not been back in 30 days.',
  'analisis.seFueDetalle': 'Last time {fecha} · bought on {n} day(s) in the 3 months before',
  'analisis.nadieSeFue': 'The regulars are all still coming.',
  'analisis.gastosTotal': 'Expenses',
  'analisis.utilidadNeta': 'Net profit',
  'analisis.seLlevan': 'Expenses take {pct} of income.',
  'analisis.enQueSeVa': 'Where it goes',
  'analisis.parteDeVentas': '{pct} of income',
  'analisis.sinGastos': 'No expenses in this period.',
  'analisis.equilibrio': 'Break-even',
  'analisis.equilibrioAyuda': 'From the income, costs and expenses of the last {n} day(s), as Accounting keeps them.',
  'analisis.equilibrioNecesita': 'Covering the expenses takes {monto} of income a day.',
  'analisis.equilibrioVende': 'Income is running at {monto} a day.',
  'analisis.sobra': 'Covered, with {monto} a day to spare.',
  'analisis.falta': '{monto} a day short of covering them.',
  'analisis.sinEquilibrio': 'It needs sales at a margin to work out.',
  'analisis.alzas': 'Price rises',
  'analisis.alzasAyuda': 'What a supplier charged at least 5% more for than the time before, freight aside.',
  'analisis.porProveedor': '{proveedor}: {n} product(s), {pct} more on average',
  'analisis.precioAntesAhora': '{antes} → {ahora} per {unidad}',
  'analisis.sinAlzas': 'No supplier raised prices in this period.',
  'analisis.detalle.ventas': "The period's sales",
  'analisis.detalle.ventasResumen': '{n} sale(s): {monto}, leaving {utilidad}.',
  'analisis.detalle.ticket': 'Ticket {ticket}',
  'analisis.detalle.dejo': 'left {monto}',
  'analisis.detalle.gastos': "The period's expenses",
  'analisis.detalle.gastosResumen': '{n} record(s), {monto} in all.',
  'analisis.detalle.verContabilidad': 'See it in Accounting',
  'analisis.mes.titulo': 'How the month is going',
  'analisis.mes.ayudaSemana': 'The rest of the month at what each day of the week has sold over the last {n} weeks.',
  'analisis.mes.ayudaRitmo': 'The rest of the month at its pace so far: there are too few weeks of sales yet to measure each day of the week.',
  'analisis.mes.cierra': 'At this pace the month closes at {monto} in sales.',
  'analisis.mes.deja': 'And leaves {monto} of gross profit.',
  'analisis.mes.van': '{n} of {de} days gone: {monto} sold.',
  'analisis.mes.pasado': 'All of last month: {monto}',
  'analisis.mes.anioPasado': '{mes}: {monto}',
  'analisis.metas.boton': 'Goals',
  'analisis.metas.titulo': "The month's goals",
  'analisis.metas.ayuda': 'What each month should sell and leave, in córdobas. Empty: no goal.',
  'analisis.metas.ventas': 'Sales goal',
  'analisis.metas.utilidad': 'Gross profit goal',
  'analisis.metas.mal': 'Type an amount, or leave it empty.',
  'analisis.metas.deMeta': '{llevado} of {meta}',
  'analisis.metas.cumplida': 'Reached.',
  'analisis.metas.llega': 'At this pace, it gets there.',
  'analisis.metas.falta': 'To get there: {monto} each day left.',
  'analisis.metas.sinMetas': 'No goals set: “Goals” sets them.',
  'analisis.diaDelMes.titulo': 'By day of the month',
  'analisis.diaDelMes.ayuda': 'What each day of the month sells on average over the last six months: paydays stand out.',
  'analisis.diaDelMes.columna': 'Day {dia}: {monto} on average',
  'analisis.diaDelMes.fuertes': 'The days that sell the most: {dias}. The best sells {pct} more than an ordinary day.',
  'analisis.canal.titulo': 'Where it sells',
  'analisis.canal.ayuda': 'The counter, orders, and orders with delivery: what each sells and leaves.',
  'analisis.canal.mostrador': 'Counter',
  'analisis.canal.pedido': 'Orders',
  'analisis.canal.envio': 'Orders with delivery',
  'analisis.canal.detalle': '{n} sale(s) · leaves {utilidad} ({margen}) · {ticket} a sale',
  'analisis.canal.envioCobro': 'Delivery charged {monto} on {n} order(s).',
  'analisis.canal.transporte': '{categoria} cost {monto}.',
  'analisis.canal.envioSobra': 'Delivery pays for transport, with {monto} to spare.',
  'analisis.canal.envioFalta': 'Delivery falls {monto} short of paying for transport.',
  'analisis.canal.pedidoPromedio': "An order with delivery carries {monto} of goods on average. The driver's pay is in the payroll, not here.",
  'analisis.lista.titulo': 'By price list',
  'analisis.lista.ayuda': 'What sold at each price and what it left.',
  'analisis.lista.detalle': '{n} sale(s) · leaves {utilidad} ({margen})',
  'analisis.categoria.titulo': 'By category',
  'analisis.categoria.ayuda': 'What each category sold and left in the period, what it holds on the shelf today at what it cost, and what that leaves a month. Tap one to see its products.',
  'analisis.categoria.vendio': 'sold {monto} at a {pct} margin',
  'analisis.categoria.nadaVendido': 'nothing sold',
  'analisis.categoria.estante': '{monto} on the shelf',
  'analisis.categoria.rinde': 'leaves {pct} of that a month',
  'analisis.agotado.titulo': 'Out of stock',
  'analisis.agotado.ayuda': 'What ran out and was selling: since when there is none, and what goes unsold each day without it, at what it sold the four weeks before.',
  'analisis.agotado.nada': 'Nothing that sells has run out.',
  'analisis.agotado.total': 'Together, some {venta} of sales a day, which would leave {utilidad}.',
  'analisis.agotado.hoy': 'Ran out today.',
  'analisis.agotado.desde': 'Out since {fecha}: some {monto} unsold.',
  'analisis.agotado.alDia': '{monto} a day',
  'analisis.sobra.titulo': 'Too much on the shelf',
  'analisis.sobra.ayuda': 'What sells, but there is more than three months of it at what sold the last {n} days: money tied up ahead of its time.',
  'analisis.sobra.nada': 'Nothing holds more than three months of stock.',
  'analisis.sobra.total': '{monto} in {n} product(s).',
  'analisis.sobra.alcanza': '{cantidad} · lasts {tiempo}',
  'analisis.sobra.meses': 'some {n} months',
  'analisis.sobra.anios': 'some {n} years',
  'analisis.simular.titulo': 'Try a price',
  'analisis.simular.ayuda': 'A price tried before it is set, against what sold the last {n} days.',
  'analisis.simular.boton': 'Try one',
  'analisis.simular.explica': 'What each one leaves at the new price, and how many would have to sell to leave what it leaves now. Nothing changes until the price is set on the product.',
  'analisis.simular.producto': 'Product',
  'analisis.simular.buscar': 'Type the name',
  'analisis.simular.hoy': 'Today, {unidad}: {precio} · costs {costo} · leaves {deja} ({pct}).',
  'analisis.simular.vendidas': 'The last {n} days, {cuantas} sold, which leave {monto} at this price.',
  'analisis.simular.sinVentas': 'None sold in the last {n} days.',
  'analisis.simular.nuevo': 'New price',
  'analisis.simular.mal': 'Type a price.',
  'analisis.simular.deja': 'At {precio}, each one leaves {deja} ({pct}).',
  'analisis.simular.perdida': 'At that price each one loses money.',
  'analisis.simular.bajoMinimo': 'It is under the minimum margin of {pct}.',
  'analisis.simular.menos': 'Selling {mismas} instead of {vendidas} leaves as much as the last {n} days.',
  'analisis.simular.mas': 'To leave as much as the last {n} days, {mismas} have to sell instead of {vendidas}.',
  'analisis.simular.alProducto': 'Go to the product',
  'analisis.fiadoVa.titulo': 'How credit is going',
  'analisis.fiadoVa.ayuda': 'What was given on credit, collected and written off in the period, and what selling on credit left; who owes more than a month ago, against their limit, and in how many days they pay.',
  'analisis.fiadoVa.periodo': '{dado} was given on credit and {cobrado} collected; {perdido} was written off.',
  'analisis.fiadoVa.queda': 'What was sold on credit left {utilidad}; less what was written off, {queda}.',
  'analisis.fiadoVa.deben': 'Who owes',
  'analisis.fiadoVa.subio': 'up {monto} in 30 days',
  'analisis.fiadoVa.bajo': 'down {monto} in 30 days',
  'analisis.fiadoVa.igual': 'the same as 30 days ago',
  'analisis.fiadoVa.limite': '{pct} of their limit',
  'analisis.fiadoVa.paga': 'pays in {n} days',
  'analisis.fiadoVa.nada': 'No credit given and nobody owing in the period.',
  'analisis.toca.titulo': 'Due to buy',
  'analisis.toca.ayuda': 'Regulars whose day to come back has come, by how often each bought the last six months. The latest for their rhythm first.',
  'analisis.toca.detalle': 'Buys every {n} day(s) · came {m} day(s) ago',
  'analisis.toca.porVez': '{monto} a visit',
  'analisis.toca.nada': 'Nobody is late: the regulars have come on time.',
  'analisis.proveedores.titulo': 'Whom the shop buys from',
  'analisis.proveedores.ayuda': 'What was bought from each supplier in the period, less what went back, and what is owed to them today. Tap one to see its purchases.',
  'analisis.proveedores.nada': 'No purchases in this period.',
  'analisis.proveedores.total': '{monto} in {n} purchase(s) from {m} supplier(s).',
  'analisis.proveedores.compras': '{n} purchase(s)',
  'analisis.proveedores.debe': '{monto} owed',
  'analisis.proveedores.lineas': '{n} product(s)',
  'analisis.proveedores.alCredito': 'on credit',
  'analisis.barato.titulo': 'Who sells it cheapest',
  'analisis.barato.ayuda': 'What another supplier sold cheaper than the last one it was bought from, the last {n} months, freight aside. What would save most a month first.',
  'analisis.barato.nada': 'The last of everything was bought where it was cheapest.',
  'analisis.barato.detalle': 'Bought from {ultimo} at {precio}; {barato} sold it at {precioBarato} on {fecha}, {pct} less.',
  'analisis.barato.alMes': '{monto} a month',
  'analisis.indice.titulo': 'What buying costs',
  'analisis.indice.ayuda': 'Each product bought in a month against the last time it was bought before, weighed by what was spent on it: how much dearer, or cheaper, what the shop buys has become.',
  'analisis.indice.nada': 'There are no purchases to compare yet.',
  'analisis.indice.subio': 'What the shop buys costs {pct} more than {n} months ago.',
  'analisis.indice.bajo': 'What the shop buys costs {pct} less than {n} months ago.',
  'analisis.indice.igual': 'What the shop buys costs the same as {n} months ago.',
  'analisis.oferta.titulo': 'Is the offer worth it?',
  'analisis.oferta.ayuda': 'When a supplier gives a better price for taking more: what it saves, how long it takes to sell, and whether the saving pays for the money left on the shelf.',
  'analisis.oferta.boton': 'Work it out',
  'analisis.oferta.explica': 'Nothing is bought here: it is only the sum.',
  'analisis.oferta.cuantas': 'Quantity ({unidad})',
  'analisis.oferta.malCantidad': 'Type a quantity.',
  'analisis.oferta.precio': 'Offer price, per {unidad}',
  'analisis.oferta.siempre': 'Usual price, per {unidad}',
  'analisis.oferta.siempreAyuda': "The last purchase's, freight aside.",
  'analisis.oferta.ahorra': 'It costs {invertido} and saves {ahorro} ({pct}).',
  'analisis.oferta.noAhorra': 'It costs {invertido} and saves nothing: it is not cheaper.',
  'analisis.oferta.dias': 'At the pace it sells, it is gone in some {n} days, after what is already there.',
  'analisis.oferta.sinRitmo': 'It did not sell in the last {n} days: there is no pace to go by.',
  'analisis.oferta.rinde': 'The saving is like earning {pct} a month on the money put in.',
  'analisis.oferta.perecedero': 'It is perishable: check it keeps {n} days.',
  'analisis.oferta.largo': 'It would sit more than three months on the shelf.',
  'analisis.presupuesto.titulo': "The month's budget",
  'analisis.presupuesto.ayuda': 'What each expense account has spent in the month against what it may spend.',
  'analisis.presupuesto.boton': 'Budgets',
  'analisis.presupuesto.nada': 'No budgets set: “Budgets” sets them.',
  'analisis.presupuesto.total': '{gastado} spent of {presupuesto} budgeted.',
  'analisis.presupuesto.pasado': 'Over by {monto}.',
  'analisis.presupuesto.quedan': '{monto} left · {pct} used',
  'analisis.presupuesto.sinPresupuesto': 'Without a budget: {monto} in {n} account(s).',
  'analisis.presupuesto.explica': 'What each account may spend a month, in córdobas. Empty: no budget. The accounts are those with expenses the last 90 days.',
  'analisis.presupuesto.sinCuentas': 'There are no expenses to budget yet.',
  'analisis.perdidas.titulo': 'What is lost',
  'analisis.perdidas.ayuda': 'Spoilage, expired goods, what the house uses, shortages, transfers that never arrived and fiado written off, at what they cost; and the last six months.',
  'analisis.perdidas.nada': 'Nothing was lost in the period.',
  'analisis.perdidas.total': '{monto} lost: {pct} of sales.',
  'analisis.planilla.titulo': 'The payroll',
  'analisis.planilla.ayuda': "The period's wages, employer INSS, INATEC, aguinaldo, vacations and severance, against the sales and what they left.",
  'analisis.planilla.nada': 'No payroll in the period.',
  'analisis.planilla.costo': 'The payroll cost {monto}.',
  'analisis.planilla.deVentas': 'That is {pct} of sales.',
  'analisis.planilla.deUtilidad': 'And {pct} of the gross profit.',
  'analisis.horas.titulo': 'Till hours',
  'analisis.horas.ayuda': 'The hours the till was open against the sales rung in them: where another pair of hands is needed and where one is enough.',
  'analisis.horas.nada': 'The till was not open in the period.',
  'analisis.horas.total': 'The till was open {n} hour(s); in {m} nothing sold.',
  'analisis.horas.movidas': 'The busiest hours',
  'analisis.horas.flojas': 'The slowest with the till open',
  'analisis.horas.cuando': '{dia}, {desde} to {hasta}',
  'analisis.horas.porHora': '{n} sales an hour',
  'analisis.alcanza.titulo': 'Can I afford it?',
  'analisis.alcanza.ayuda': 'One more person or a new fixed expense against what the shop left the last {n} days: what would be left a month and how much more would have to sell.',
  'analisis.alcanza.persona': 'One more person',
  'analisis.alcanza.gasto': 'A new fixed expense',
  'analisis.alcanza.sueldo': 'Monthly wage',
  'analisis.alcanza.monto': 'How much a month',
  'analisis.alcanza.costo': 'It costs the shop {total} a month: {sueldo} in wages, {patronal} in employer INSS and INATEC and {prestaciones} in aguinaldo and vacations.',
  'analisis.alcanza.queda': 'At the last {n} days, after every expense, {queda} is left a month.',
  'analisis.alcanza.quedaria': 'With this, {monto} would be left a month.',
  'analisis.alcanza.noAlcanza': 'It does not fit: {monto} a month would be short.',
  'analisis.alcanza.vender': 'To pay for it, {mes} more would have to sell a month, some {dia} a day, at a {pct} margin.',
  'analisis.alcanza.explica': 'Nobody is hired and nothing is spent here: it is only the sum.',
  'analisis.hoyEs': 'Today, {fecha}',
  'analisis.dinero.hoyTitulo': "Today's money",
  'analisis.dinero.hoyAyuda': 'What is in the tills, general cash, what was handed over uncounted, each bank account and the wallet, as the books carry it.',
  'analisis.dinero.hay': '{monto} in all.',
  'analisis.dinero.nada': 'The books hold no money yet.',
  'analisis.dinero.pronosticoTitulo': 'The next {n} days',
  'analisis.dinero.pronosticoAyuda': "What cash sales and fiado collected would bring, at the last {n} weeks' pace, less putting back what sells at cost and what falls due each day.",
  'analisis.dinero.final': 'In {n} days there would be some {monto}.',
  'analisis.dinero.minimo': 'The lowest day: {fecha}, with {monto}.',
  'analisis.dinero.falta': 'On {fecha}, {monto} would be short.',
  'analisis.dinero.columna': '{fecha}: {monto}',
  'analisis.toqueDia': 'Tap a day to see its figure.',
  'analisis.dinero.bajoCero': 'The books come to {monto} below zero: more money went out than came in by them, and some money coming in is not recorded.',
  'analisis.dinero.finalFalta': 'In {n} days some {monto} would be short.',
  'analisis.dinero.ventas': 'Sales paid on the spot',
  'analisis.dinero.cobros': 'Fiado collected',
  'analisis.dinero.reponer': 'Putting back what sells',
  'analisis.dinero.planilla': 'Payroll',
  'analisis.dinero.fijos': 'Fixed expenses',
  'analisis.dinero.compras': 'Purchases on credit',
  'analisis.dinero.gastos': 'Expenses on credit',
  'analisis.dinero.deudas': 'INSS, INATEC, income tax and wages owed',
  'analisis.dinero.supuestos': 'What is owed to INSS, INATEC and in income tax, and wages not handed over, have no day in the app: they are counted as paid today; so is a purchase on credit with no day.',
  'analisis.dinero.porDia': '{n} person(s) paid by the day are not counted: what they earn depends on the days they work.',
  'analisis.dinero.sinMonto': 'With no fixed amount, not counted: {lista}.',
  'analisis.dinero.venceTitulo': 'What falls due in {n} days',
  'analisis.dinero.venceAyuda': 'The payroll on its paydays, the aguinaldo on 10 December, the fixed expenses, the purchases on credit and what is owed with no day.',
  'analisis.dinero.venceNada': 'Nothing falls due in these days.',
  'analisis.dinero.planillaDe': 'Payroll, {desde} to {hasta}',
  'analisis.dinero.aguinaldo': "December's aguinaldo",
  'analisis.dinero.hoyMismo': 'today',
  'analisis.capital.titulo': 'Working capital',
  'analisis.capital.ayuda': "What the shop has to work with, and how long money takes to come back, at the last {n} days' pace.",
  'analisis.capital.tiene': 'There is {capital} to work with: {dinero} in money, {inventario} in goods, {fiado} in fiado and {otros} more, less {proveedores} owed to suppliers and {deudas} of other debts.',
  'analisis.capital.ciclo': 'Money takes some {n} days to come back: {inventario} on the shelf and {cobro} in fiado, less the {pago} the suppliers wait.',
  'analisis.capital.cicloAntes': 'Money comes back some {n} days before the suppliers are paid: {inventario} on the shelf and {cobro} in fiado, against the {pago} they wait.',
  'analisis.hoy': 'Today',
  'analisis.decidir.titulo': 'What to do today',
  'analisis.decidir.ayuda': 'What puts the most money at stake first; each opens where it is dealt with.',
  'analisis.decidir.nada': 'Nothing to decide today: nothing that sells is running out, no price is under the margin and no batch is expiring, nobody went past their credit limit and the till was not short.',
  'analisis.decidir.yMas': '{lista} and {n} more',
  'analisis.decidir.dinero': 'Money would run short on {fecha}',
  'analisis.decidir.dineroDetalle': 'What falls due in {n} days is more than would come in.',
  'analisis.decidir.dineroMonto': 'short',
  'analisis.decidir.reponer': 'Ran out or running out',
  'analisis.decidir.reponerMonto': 'of sales a week',
  'analisis.decidir.vencen': 'Expired or expiring',
  'analisis.decidir.vencenMonto': 'of goods',
  'analisis.decidir.limite': 'Past their credit limit',
  'analisis.decidir.limiteMonto': 'over the limit',
  'analisis.decidir.fiadoViejo': 'Fiado over 90 days old',
  'analisis.decidir.fiadoViejoMonto': 'to collect',
  'analisis.decidir.faltantes': 'Till shortages these 7 days',
  'analisis.decidir.faltantesMonto': 'short',
  'analisis.decidir.transferencias': 'Transfers not confirmed',
  'analisis.decidir.transferenciasDetalle': '{n}; the oldest from {fecha}',
  'analisis.decidir.transferenciasUna': 'From {fecha}',
  'analisis.decidir.transferenciasMonto': 'to confirm',
  'analisis.decidir.margen': 'Prices under the margin',
  'analisis.decidir.margenMonto': 'more a month',
  'analisis.decidir.lesToca': 'Due to buy',
  'analisis.decidir.lesTocaMonto': 'each time',
  'analisis.decidir.barato': 'Cheaper elsewhere',
  'analisis.decidir.baratoMonto': 'saved a month',
  'analisis.decidir.sobra': 'More than 3 months\' stock',
  'analisis.decidir.sobraMonto': 'sitting',
  'analisis.confianza.titulo': 'How far to trust the figures',
  'analisis.confianza.ayuda': 'What can keep a figure from being the real one.',
  'analisis.confianza.bien': 'Everything sold these 30 days has a cost and no stock is below zero.',
  'analisis.confianza.rapidas': '{monto} in {n} quick sale(s) these 30 days: they have no cost, so all of it counts as profit.',
  'analisis.confianza.sinCosto': '{n} product(s) sold at no cost these 30 days ({lista}): {monto} counts as profit in full.',
  'analisis.confianza.negativos': '{n} product(s) with stock below zero: until they are counted, the stock and what it costs are not the real ones.',
  'analisis.confianza.verNegativos': 'See which',
  'analisis.confianza.pocosDias': 'Sales start on {fecha}: paces and averages come from {n} day(s).',
  'analisis.confianza.dinero': 'The books show money below zero: some money coming in is not recorded.',
  'analisis.confianza.categorias': 'When each category was counted',
  'analisis.confianza.categoriasAyuda': 'What is not counted can go missing unnoticed.',
  'analisis.confianza.hace': '{n} days ago',
  'analisis.confianza.nuncaContadas': '{n} category(ies) never counted: {lista}.',
  'analisis.confianza.hoyMismo': 'Today',
  'analisis.confianza.contados': '{n} of {total} products counted in 90 days',
  'analisis.confianza.contar': 'Count',
  'analisis.confianza.sinCategorias': 'Nothing on the shelf to count.',
  'analisis.resumen.titulo': 'Month summary',
  'analisis.resumen.ayuda': 'Sales, profit, expenses and what was left, to send by WhatsApp or print.',
  'analisis.resumen.ver': 'See the summary',
  'analisis.resumen.de': 'Summary of {mes}',
  'analisis.resumen.contra': 'Against {mes}',
  'analisis.resumen.margen': 'Margin',
  'analisis.resumen.cierra': 'Close at this pace',
  'analisis.resumen.fiado': 'Fiado to collect',
  'analisis.resumen.dinero': 'Money today',
  'analisis.resumen.pdf': 'For a PDF, tap «Print» and choose «Save as PDF».',
  'inicio.ayudaAnalisis': 'How the shop is doing: sales, what makes the most, who owes and where the money goes.',
};
